// Editor.jsx
// ============================================================================
// ARCHITECTURE — NON-DESTRUCTIVE LAYER MODEL (Schema v4)
// ----------------------------------------------------------------------------
// Three explicitly separated values drive every pixel on screen:
//
//   originalImage      — the pristine uploaded photo. Never mutated after
//                        load. This is the ONLY thing that is ever written
//                        back to Firestore as the "source" image.
//
//   canvasImage        — the working base. It is the pristine original with
//                        every visible non-frame layer (inpaint / style)
//                        replayed on top, in application order. It never
//                        contains frame renders.
//
//   displayImageUrl    — the RENDERED composite. Purely derived:
//                        canvasImage + every visible frame layer
//                        (z-ordered). Recomputed whenever canvasImage or
//                        frameImages change. NEVER persisted. NEVER read
//                        back as a source.
//
// Frame layers are the single source of truth for placements. The Picture
// Frames tool's `savedFrames` prop is a projection of frame layers; the
// Layers panel reads the same frame layers. There is exactly one copy of
// each frame's transform (corners) and one copy of its source artwork.
//
// Editing a frame writes ONLY that frame's corners. The composite is
// regenerated from canvasImage + the current frame list; no per-frame
// snapshot is ever stored, so editing frame A can never move, duplicate,
// or hide frame B, and editing frame A can never revert to its original
// placed position (the corners in state are the ones we render).
//
// Save pipeline:
//   photoURL          = canvasImage      (working base — NOT the composite)
//   originalPhotoURL  = originalImage    (pristine)
//   canvasStateURL    = { layers, selectedLayerId, currentLassoPath }
//   frames subcollection + frameImages[] legacy mirror (binaries in Storage)
//
// Load pipeline:
//   originalImage = originalPhotoURL || photoURL
//   canvasImage   = replay(originalImage, nonFrameLayers)   // recomputed
//   frameImages   = subcollection → frameImagesURL → legacy array
//   displayImageUrl = composite(canvasImage, frameImages)   // rendered
// ============================================================================
//
// DEVELOPER NOTE — THEME ACCOMMODATIONS FOR NEW COMPONENTS
// ----------------------------------------------------------------------------
// Every new UI component added to this editor MUST include both light and
// dark theme variants. The convention is:
//   1. The root element carries `theme-light` or `theme-dark`.
//   2. Every theme-sensitive rule is written as a pair:
//         .theme-light .my-thing { ... }
//         .theme-dark  .my-thing { ... }
//      OR uses a fallback default plus an override for the opposite theme.
//   3. Popups/modals rendered outside the editor tree must inherit
//      `theme-light` / `theme-dark` from a context or prop. Do NOT hardcode
//      #FAFAFA / #333 — use the shared tokens below.
//   4. For inline-styled popups, read `uiTheme` from state and swap tokens.
//
// Shared tokens:
//   LIGHT: bg #FFFFFF  surface #F5F5F5  text #333  muted #888  border #E0E0E0
//   DARK:  bg #2c2c2c  surface #3a3a3a  text #fff  muted #aaa  border #555
// ============================================================================

import React, { useState, useRef, useEffect, useCallback, useMemo, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useTexture } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import axios from 'axios';
import { getEnhancePrompt } from '../components/OL_PROMPT_INPAINT';
import { IoMoon, IoSunny } from "react-icons/io5";
import { TbAtom2, TbWallpaper, TbCubePlus } from "react-icons/tb";
import { FiSettings, FiDownload, FiHeart, FiPlus, FiCopy, FiEdit2, FiImage, FiMinus, FiX, FiCamera, FiTrash2, FiGrid, FiUpload, FiVideo, FiCheck, FiArrowLeft, FiEye, FiEyeOff, FiLock, FiUnlock, FiMic, FiSave } from 'react-icons/fi';
import { CiExport } from 'react-icons/ci';
import { FaRuler, FaMapPin } from 'react-icons/fa';
import { RiFilmAiLine, RiCameraLine, RiGuideLine } from 'react-icons/ri';
import SpeakAndCreate from '../components/SAC';
import { LuRewind, LuShrink, LuUndo, LuRedo, LuDraftingCompass, LuPenTool } from 'react-icons/lu';
import { RiPaintingLine } from "react-icons/ri";
import { MdKeyboardArrowRight, MdFlashOn, MdFlashOff, MdFlashAuto, MdDragHandle } from 'react-icons/md';
import { ChromePicker } from 'react-color';

// ============================================
// FIREBASE IMPORTS FOR PROJECT PERSISTENCE
// ============================================
import {
  getAuth,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  deleteDoc,
  writeBatch,
  onSnapshot,
} from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadString, uploadBytes, getDownloadURL, deleteObject, listAll, getBlob } from 'firebase/storage';
import { db } from '../firebase';
import { checkSufficientCredits, deductImageCredits, getRemainingCredits, safeGenerate, calculateVideoGenerationCost, deductVideoCredits, deductCreditsWithTimeout } from '../services/usageService';
import {
  loadImageElement,
  computeHomography,
  buildFrameComposite,
  computeCompositeCacheKey,
  getCachedComposite,
  setCachedComposite,
} from '../services/compositeService';
import { useNotification, FULLSCREEN_TRIGGERS, TOAST_IDS } from '../components/NotificationSystem';

// Import Renders Panel
import RendersPanel from '../components/RendersPanel';
import SmartRecolorTool from '../components/SmartRecolorTool';
import DynamicCanvas from '../components/DynamicCanvas/';
import PictureFrameTool from '../components/PictureFrameTool';
import LayersPanel from '../components/LayersComponent';
import EditorErrorBoundary from '../components/EditorErrorBoundary';

// ============================================
// PROJECT SCHEMA VERSION
// ============================================
const PROJECT_SCHEMA_VERSION = 4;

// Data URL threshold — anything above this MUST live in Storage, not Firestore.
const DATA_URL_INLINE_LIMIT = 100 * 1024; // 100 KB

// ============================================================================
// LOAD PIPELINE DEBUG LOGGER
// ----------------------------------------------------------------------------
// Toggle LOAD_DEBUG to true in the browser console to see a full timing
// breakdown of the project load pipeline:
//     window.__OUTLINE_LOAD_DEBUG__ = true
// Then reload. Every log is prefixed with [LOAD] so you can filter.
//
// Timings are accumulated on a single object so a final "summary" line can
// show where the seconds actually went. This is the instrumentation you'll
// use to find the frame-load bottleneck.
// ============================================================================
const LOAD_DEBUG_KEY = '__OUTLINE_LOAD_DEBUG__';
const loadDebugEnabled = () => {
  try {
    return typeof window !== 'undefined' && window[LOAD_DEBUG_KEY] === true;
  } catch {
    return false;
  }
};

const loadTrace = {
  t0: 0,
  marks: [],
};

const loadDebugStart = (label) => {
  if (!loadDebugEnabled()) return;
  loadTrace.t0 = performance.now();
  loadTrace.marks = [];
  console.log(`%c[LOAD] ▶ ${label}`, 'color:#4594f9;font-weight:bold;');
};

const loadDebugMark = (label, data) => {
  if (!loadDebugEnabled()) return;
  const now = performance.now();
  const sinceStart = now - loadTrace.t0;
  const sinceLast = loadTrace.marks.length === 0
    ? sinceStart
    : now - loadTrace.marks[loadTrace.marks.length - 1].t;
  loadTrace.marks.push({ label, t: now });
  const delta = sinceLast.toFixed(0);
  const total = sinceStart.toFixed(0);
  const style = sinceLast > 500
    ? 'color:#e02f1d;font-weight:bold;'
    : sinceLast > 150
      ? 'color:#e0a11d;'
      : 'color:#4caf50;';
  console.log(
    `%c[LOAD] +${delta}ms (total ${total}ms) — ${label}`,
    style,
    data !== undefined ? data : ''
  );
};

const loadDebugSummary = () => {
  if (!loadDebugEnabled()) return;
  const total = (performance.now() - loadTrace.t0).toFixed(0);
  console.log(`%c[LOAD] ■ COMPLETE in ${total}ms`, 'color:#4caf50;font-weight:bold;');
  if (loadTrace.marks.length > 0) {
    console.table(loadTrace.marks.map((m, i) => ({
      step: i + 1,
      label: m.label,
      'since start (ms)': (m.t - loadTrace.t0).toFixed(0),
    })));
  }
};

// Always-on lightweight console logging for the composite pipeline.
// This one is gated by import.meta.env.DEV so it never ships to prod.
const compositeLog = (...args) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('[composite]', ...args);
  }
};

// ============================================
// STORAGE PATHS — single source of truth
// ============================================
const storagePaths = {
  canvasComposite: (uid, pid) => `users/${uid}/projects/${pid}/canvas/composite.png`,
  canvasOriginal:  (uid, pid) => `users/${uid}/projects/${pid}/canvas/original.png`,
  canvasWorking:   (uid, pid) => `users/${uid}/projects/${pid}/canvas/working.png`,
  canvasThumbnail: (uid, pid) => `users/${uid}/projects/${pid}/canvas/thumbnail.png`,
  canvasState:     (uid, pid) => `users/${uid}/projects/${pid}/canvas/state.json`,

  frameImage:      (uid, pid, fid) => `users/${uid}/projects/${pid}/frames/${fid}/image.png`,
  frameOriginal:   (uid, pid, fid) => `users/${uid}/projects/${pid}/frames/${fid}/original.png`,
  frameUncropped:  (uid, pid, fid) => `users/${uid}/projects/${pid}/frames/${fid}/uncropped.png`,
  frameBackground: (uid, pid, fid) => `users/${uid}/projects/${pid}/frames/${fid}/background.png`,

  noteAttachment:  (uid, pid, nid) => `users/${uid}/projects/${pid}/notes/${nid}/attachment.png`,
  drawing:         (uid, pid, did) => `users/${uid}/projects/${pid}/drawings/${did}.json`,
  moodboardAsset:  (uid, pid, aid) => `users/${uid}/projects/${pid}/moodboard/${aid}.png`,

  preProjectFrame: (uid, fid, filename) => `users/${uid}/frames/${fid}_${filename}`,

  roomCanvas:      (uid, pid, rid) => `users/${uid}/projects/${pid}/rooms/${rid}/canvas.png`,
  roomThumbnail:   (uid, pid, rid) => `users/${uid}/projects/${pid}/rooms/${rid}/thumbnail.png`,
  roomsState:      (uid, pid)      => `users/${uid}/projects/${pid}/rooms/state.json`,
};

// ============================================
// THEME TOKENS
// ============================================
const THEME_TOKENS = {
  light: {
    bg: '#FFFFFF', surface: '#F5F5F5', text: '#333333', muted: '#888888',
    border: '#E0E0E0', accent: '#4594F9', danger: '#E02F1D',
  },
  dark: {
    bg: '#2c2c2c', surface: '#3a3a3a', text: '#FFFFFF', muted: '#AAAAAA',
    border: '#555555', accent: '#4594F9', danger: '#E02F1D',
  },
};

// ============================================
// PROJECT SERVICE (Firebase CRUD)
// ============================================
const projectService = {
  async createProject(userId, projectData) {
    const projectsRef = collection(db, 'projects');
    const docRef = await addDoc(projectsRef, {
      userId,
      name: projectData.name || 'Untitled Project',
      details: projectData.details || '',
      photoURL: projectData.photo || null,
      originalPhotoURL: projectData.originalPhoto || projectData.photo || null,
      mode: projectData.mode || 'new-project',
      status: 'active',
      schemaVersion: PROJECT_SCHEMA_VERSION,
      layerCount: 0,
      frameCount: 0,
      noteCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: docRef.id };
  },

  async updateProject(projectId, updates) {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  },

  async autoSaveProject(userId, projectData) {
    const autoSavesRef = collection(db, 'project_autosaves');
    const q = query(
      autoSavesRef,
      where('userId', '==', userId),
      where('projectId', '==', projectData.projectId || 'new'),
    );
    const snapshot = await getDocs(q);

    const saveData = {
      userId,
      projectId: projectData.projectId || 'new',
      name: projectData.name || '',
      details: projectData.details || '',
      photoURL: projectData.photo || null,
      canvasState: projectData.canvasState || null,
      frameImages: projectData.frameImages || [],
      savedAt: serverTimestamp(),
    };

    if (snapshot.empty) {
      await addDoc(autoSavesRef, saveData);
    } else {
      await updateDoc(snapshot.docs[0].ref, saveData);
    }
  },

  async getAutoSave(userId, projectId) {
    const autoSavesRef = collection(db, 'project_autosaves');
    const q = query(
      autoSavesRef,
      where('userId', '==', userId),
      where('projectId', '==', projectId),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  },

  async getProject(projectId) {
    const projectRef = doc(db, 'projects', projectId);
    const docSnap = await getDoc(projectRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() };
  },

  async deleteProject(projectId) {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      status: 'deleted',
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  async restoreProject(projectId) {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      status: 'active',
      deletedAt: null,
      updatedAt: serverTimestamp(),
    });
  },
};

// ============================================
// PROJECT ASSET SERVICE (subcollections)
// ============================================
const projectAssetService = {
  async readEntities(pid, collectionName) {
    if (!pid || !collectionName) return [];
    try {
      const ref = collection(db, 'projects', pid, collectionName);
      const snap = await getDocs(ref);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.error(`[projectAssetService] readEntities(${collectionName}) failed:`, err);
      return [];
    }
  },

  async writeEntity(pid, collectionName, entityId, data) {
    if (!pid || !collectionName || !entityId) return;
    const ref = doc(db, 'projects', pid, collectionName, entityId);
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
  },

  async deleteEntity(pid, collectionName, entityId) {
    if (!pid || !collectionName || !entityId) return;
    try {
      await deleteDoc(doc(db, 'projects', pid, collectionName, entityId));
    } catch (err) {
      console.error(`[projectAssetService] deleteEntity(${collectionName}/${entityId}) failed:`, err);
    }
  },

  async clearCollection(pid, collectionName) {
    if (!pid || !collectionName) return;
    try {
      const ref = collection(db, 'projects', pid, collectionName);
      const snap = await getDocs(ref);
      if (snap.empty) return;
      const docs = snap.docs;
      for (let i = 0; i < docs.length; i += 400) {
        const batch = writeBatch(db);
        docs.slice(i, i + 400).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    } catch (err) {
      console.error(`[projectAssetService] clearCollection(${collectionName}) failed:`, err);
    }
  },

  async readFrames(pid) {
    return this.readEntities(pid, 'frames');
  },

  async writeFrames(pid, frames) {
    if (!Array.isArray(frames)) return;
    for (const frame of frames) {
      if (!frame || !frame.id) continue;
      const clean = { ...frame };
      for (const key of ['imageData', 'originalImage', 'originalUncroppedData', 'backgroundImage']) {
        if (typeof clean[key] === 'string' && clean[key].startsWith('data:')) {
          delete clean[key];
        }
      }
      await this.writeEntity(pid, 'frames', frame.id, clean);
    }
  },

  async readNotes(pid) { return this.readEntities(pid, 'notes'); },
  async writeNote(pid, note) { if (note?.id) await this.writeEntity(pid, 'notes', note.id, note); },
  async deleteNote(pid, nid) { await this.deleteEntity(pid, 'notes', nid); },

  async readDrawings(pid) { return this.readEntities(pid, 'drawings'); },
  async writeDrawing(pid, drawing) { if (drawing?.id) await this.writeEntity(pid, 'drawings', drawing.id, drawing); },
  async deleteDrawing(pid, did) { await this.deleteEntity(pid, 'drawings', did); },

  async readMoodboard(pid) { return this.readEntities(pid, 'moodboard'); },
  async writeMoodboardItem(pid, item) { if (item?.id) await this.writeEntity(pid, 'moodboard', item.id, item); },
  async deleteMoodboardItem(pid, aid) { await this.deleteEntity(pid, 'moodboard', aid); },

  async sweepProjectStorage(uid, pid) {
    if (!uid || !pid) return;
    const storage = getStorage();
    const projectPrefix = `users/${uid}/projects/${pid}`;

    await this.clearCollection(pid, 'frames');
    await this.clearCollection(pid, 'layers');
    await this.clearCollection(pid, 'notes');
    await this.clearCollection(pid, 'drawings');
    await this.clearCollection(pid, 'moodboard');

    try {
      const rootRef = storageRef(storage, projectPrefix);
      const listRecursive = async (ref) => {
        const listing = await listAll(ref);
        for (const item of listing.items) {
          try { await deleteObject(item); } catch (e) { /* ignore */ }
        }
        for (const prefix of listing.prefixes) {
          await listRecursive(prefix);
        }
      };
      await listRecursive(rootRef);
    } catch (err) {
      console.error('[projectAssetService] sweepProjectStorage error:', err);
    }
  },
};

// ============================================
// DEBUG CONSOLE TOGGLE
// ============================================
const DEBUG_CONSOLE_ENABLED = false;

// Replicate Configuration
const REPLICATE_API_TOKEN = import.meta.env.VITE_REPLICATE_API_TOKEN;
const INPAINTING_MODEL = "bria/genfill";

// ============================================
// ADVANCED INTERIOR DESIGN INPAINTING
// ============================================

const convertContainerPointsToImage = (points, containerW, containerH, imgW, imgH) => {
  if (!containerW || !containerH || !imgW || !imgH) return points;
  const containerAspect = containerW / containerH;
  const imageAspect = imgW / imgH;
  let renderWidth, renderHeight, offsetX, offsetY, scale;

  if (containerAspect > imageAspect) {
    renderHeight = containerH;
    scale = renderHeight / imgH;
    renderWidth = imgW * scale;
    offsetX = (containerW - renderWidth) / 2;
    offsetY = 0;
  } else {
    renderWidth = containerW;
    scale = renderWidth / imgW;
    renderHeight = imgH * scale;
    offsetX = 0;
    offsetY = (containerH - renderHeight) / 2;
  }

  return points.map(p => ({
    x: (p.x - offsetX) / scale,
    y: (p.y - offsetY) / scale,
  }));
};

const convertContainerPointsToImageContain = (points, containerW, containerH, imgW, imgH) => {
  if (!containerW || !containerH || !imgW || !imgH) return points;
  const containerAspect = containerW / containerH;
  const imageAspect = imgW / imgH;
  let renderWidth, renderHeight, offsetX, offsetY, scale;

  if (containerAspect > imageAspect) {
    renderHeight = containerH;
    scale = renderHeight / imgH;
    renderWidth = imgW * scale;
    offsetX = (containerW - renderWidth) / 2;
    offsetY = 0;
  } else {
    renderWidth = containerW;
    scale = renderWidth / imgW;
    renderHeight = imgH * scale;
    offsetX = 0;
    offsetY = (containerH - renderHeight) / 2;
  }

  return points.map(p => ({
    x: (p.x - offsetX) / scale,
    y: (p.y - offsetY) / scale,
  }));
};

const createMaskFromLasso = (lassoPoints, imageWidth, imageHeight) => {
  const canvas = document.createElement('canvas');
  canvas.width = imageWidth;
  canvas.height = imageHeight;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, imageWidth, imageHeight);

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(lassoPoints[0].x, lassoPoints[0].y);
  for (let i = 1; i < lassoPoints.length; i++) {
    ctx.lineTo(lassoPoints[i].x, lassoPoints[i].y);
  }
  ctx.closePath();
  ctx.fill();

  return canvas.toDataURL('image/png');
};

const applyInpaintedRegion = async (baseImageUrl, inpaintedImageUrl, maskUrl) => {
  const baseImg = await loadImageElement(baseImageUrl);
  const inpaintedImg = await loadImageElement(inpaintedImageUrl);
  const maskImg = await loadImageElement(maskUrl);

  const canvas = document.createElement('canvas');
  canvas.width = baseImg.width;
  canvas.height = baseImg.height;
  const ctx = canvas.getContext('2d', { colorSpace: 'display-p3' });

  ctx.drawImage(baseImg, 0, 0);

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = baseImg.width;
  maskCanvas.height = baseImg.height;
  const maskCtx = maskCanvas.getContext('2d', { colorSpace: 'display-p3' });
  maskCtx.drawImage(maskImg, 0, 0);
  const maskData = maskCtx.getImageData(0, 0, baseImg.width, baseImg.height);

  const inpaintCanvas = document.createElement('canvas');
  inpaintCanvas.width = baseImg.width;
  inpaintCanvas.height = baseImg.height;
  const inpaintCtx = inpaintCanvas.getContext('2d', { colorSpace: 'display-p3' });
  inpaintCtx.drawImage(inpaintedImg, 0, 0, baseImg.width, baseImg.height);
  const inpaintData = inpaintCtx.getImageData(0, 0, baseImg.width, baseImg.height);

  const baseData = ctx.getImageData(0, 0, baseImg.width, baseImg.height);

  for (let i = 0; i < maskData.data.length; i += 4) {
    const maskValue = maskData.data[i] / 255;

    if (maskValue > 0.01) {
      const blendFactor = maskValue < 0.5
        ? 2 * maskValue * maskValue
        : 1 - Math.pow(-2 * maskValue + 2, 2) / 2;

      baseData.data[i] = baseData.data[i] * (1 - blendFactor) + inpaintData.data[i] * blendFactor;
      baseData.data[i + 1] = baseData.data[i + 1] * (1 - blendFactor) + inpaintData.data[i + 1] * blendFactor;
      baseData.data[i + 2] = baseData.data[i + 2] * (1 - blendFactor) + inpaintData.data[i + 2] * blendFactor;
    }
  }

  ctx.putImageData(baseData, 0, 0);

  return canvas.toDataURL('image/png', 1.0);
};

const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

// loadImageElement is now imported from ../services/compositeService (shared
// with the Dashboard prefetch pipeline). The local definition has been removed.

const imageCache = new Map();
const gradientCache = new Map();

// ============================================
// CANVAS STATE STORAGE HELPERS
// ============================================
const uploadCanvasState = async (userId, projectId, stateObject) => {
  const storage = getStorage();
  const path = storagePaths.canvasState(userId, projectId);
  const stateRef = storageRef(storage, path);
  const jsonString = JSON.stringify(stateObject);
  await uploadString(stateRef, jsonString, 'raw', {
    contentType: 'application/json',
  });
  const downloadURL = await getDownloadURL(stateRef);
  return downloadURL;
};

const fetchCanvasState = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch canvas state: ${response.status}`);
  const data = await response.json();
  return data;
};

// ============================================
// ROOMS STATE STORAGE HELPERS
// ----------------------------------------------------------------------------
// Rooms are stored as a JSON file in Storage (like canvasState) rather
// than as a nested Firestore array on the project doc. Two reasons:
//
//   1. Firestore rejects nested arrays and enforces strict Map-shape
//      rules on array elements. A room contains a layers array and a
//      frameImages array, each of which contains Maps that carry their
//      own array fields — one malformed entry anywhere in that tree
//      throws "Property array contains an invalid nested entity" and
//      fails the entire save.
//
//   2. Rooms grow. Each room's layers + frameImages payload can easily
//      push the project doc past the 1 MB Firestore document limit as
//      the user adds angles and frames. A Storage JSON file has no such
//      ceiling.
// ============================================
const uploadRoomsState = async (userId, projectId, roomsArray) => {
  const storage = getStorage();
  const path = storagePaths.roomsState(userId, projectId);
  const roomsRef = storageRef(storage, path);
  const jsonString = JSON.stringify(roomsArray);
  await uploadString(roomsRef, jsonString, 'raw', {
    contentType: 'application/json',
  });
  const downloadURL = await getDownloadURL(roomsRef);
  return downloadURL;
};

const fetchRoomsState = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch rooms state: ${response.status}`);
  const data = await response.json();
  return data;
};

// ============================================
// FIRESTORE NESTED-ARRAY ROUND-TRIP HELPERS
// ----------------------------------------------------------------------------
// Firestore rejects arrays that directly contain other arrays with the
// error "Property array contains an invalid nested entity." The save
// pipeline rewrites every nested array into a numeric-keyed map with a
// sentinel field (`__outlineArrayShape: true`). This function reverses
// that transformation when data comes back from Firestore. It is safe
// to call on data that was never transformed — the sentinel check
// simply never matches.
// ============================================
const restoreFirestoreArrays = (value) => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map(item => restoreFirestoreArrays(item));
  }
  if (typeof value === 'object') {
    if (value.__outlineArrayShape === true) {
      const keys = Object.keys(value)
        .filter(k => k !== '__outlineArrayShape')
        .sort((a, b) => Number(a) - Number(b));
      return keys.map(k => restoreFirestoreArrays(value[k]));
    }
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = restoreFirestoreArrays(v);
    }
    return out;
  }
  return value;
};

const dataURLToBlob = (dataURL) => {
  const parts = dataURL.split(',');
  const mime = parts[0].match(/:(.*?);/)[1];
  const bstr = atob(parts[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new Blob([u8arr], { type: mime });
};

const uploadCanvasImage = async (userId, projectId, imageDataUrl) => {
  const storage = getStorage();
  const path = storagePaths.canvasComposite(userId, projectId);
  const imageRef = storageRef(storage, path);
  const blob = dataURLToBlob(imageDataUrl);
  await uploadBytes(imageRef, blob, { contentType: 'image/png' });
  return await getDownloadURL(imageRef);
};

const uploadOriginalImage = async (userId, projectId, imageDataUrl) => {
  const storage = getStorage();
  const path = storagePaths.canvasOriginal(userId, projectId);
  const imageRef = storageRef(storage, path);
  const blob = dataURLToBlob(imageDataUrl);
  await uploadBytes(imageRef, blob, { contentType: 'image/png' });
  return await getDownloadURL(imageRef);
};

const uploadWorkingImage = async (userId, projectId, imageDataUrl) => {
  const storage = getStorage();
  const path = storagePaths.canvasWorking(userId, projectId);
  const imageRef = storageRef(storage, path);
  const blob = dataURLToBlob(imageDataUrl);
  await uploadBytes(imageRef, blob, { contentType: 'image/png' });
  return await getDownloadURL(imageRef);
};

const uploadThumbnailImage = async (userId, projectId, imageDataUrl) => {
  const storage = getStorage();
  const path = storagePaths.canvasThumbnail(userId, projectId);
  const imageRef = storageRef(storage, path);
  const blob = dataURLToBlob(imageDataUrl);
  await uploadBytes(imageRef, blob, { contentType: 'image/png' });
  return await getDownloadURL(imageRef);
};

const deleteCanvasState = async (userId, projectId) => {
  const storage = getStorage();
  const path = storagePaths.canvasState(userId, projectId);
  const stateRef = storageRef(storage, path);
  try {
    await deleteObject(stateRef);
  } catch (err) {
    // Ignore if file doesn't exist
  }
};

// ============================================
// FRAME IMAGE MIGRATION
// ============================================
const migrateFrameImagesToStorage = async (userId, projectId, frames) => {
  if (!userId || !projectId || !Array.isArray(frames)) return frames;

  const storage = getStorage();

  const migrateOne = async (value, pathBuilder, frameId, fieldName) => {
    if (typeof value !== 'string') return value;
    if (!value.startsWith('data:')) return value;
    if (value.length <= DATA_URL_INLINE_LIMIT) return value;

    try {
      const path = pathBuilder(userId, projectId, frameId);
      const fileRef = storageRef(storage, path);
      const blob = dataURLToBlob(value);
      await uploadBytes(fileRef, blob, { contentType: 'image/png' });
      return await getDownloadURL(fileRef);
    } catch (err) {
      console.error(`[migrateFrameImagesToStorage] ${fieldName} for frame ${frameId} failed:`, err);
      return value;
    }
  };

  const migrated = [];
  for (const frame of frames) {
    if (!frame) continue;
    const f = { ...frame };

    f.imageData = await migrateOne(frame.imageData, storagePaths.frameImage, frame.id, 'imageData');
    f.originalImage = await migrateOne(frame.originalImage, storagePaths.frameOriginal, frame.id, 'originalImage');
    f.originalUncroppedData = await migrateOne(frame.originalUncroppedData, storagePaths.frameUncropped, frame.id, 'originalUncroppedData');
    f.backgroundImage = await migrateOne(frame.backgroundImage, storagePaths.frameBackground, frame.id, 'backgroundImage');

    f.imageURL = f.imageData;
    f.originalURL = f.originalImage;
    f.uncroppedURL = f.originalUncroppedData;
    f.backgroundURL = f.backgroundImage;

    migrated.push(f);
  }

  return migrated;
};

// ============================================
// SETTINGS PANEL COMPONENT
// ============================================
const SettingsPanel = ({ isOpen, onClose, uiTheme, onToggleTheme, onOpenDepthDiorama, autoSaveEnabled, onToggleAutoSave, gyroEnabled, onToggleGyro, voiceEnabled, onToggleVoice, tutorialShown, onToggleTutorial, onOpenTutorial }) => {
  if (!isOpen) return null;

  return (
    <div className="settings-panel-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>
        <div className="settings-content">
          <div className="settings-section">
            <h3>Appearance</h3>
            <div className="settings-item">
              <span>Theme</span>
              <button
                className={`settings-toggle-btn ${uiTheme === 'light' ? 'light' : 'dark'}`}
                onClick={onToggleTheme}
              >
                {uiTheme === 'light' ? <IoMoon size={16} /> : <IoSunny size={16} />}
                {uiTheme === 'light' ? 'Dark Mode' : 'Light Mode'}
              </button>
            </div>
          </div>

          <div className="settings-section">
            <h3>Project</h3>
            <div className="settings-item">
              <span>Auto-Save</span>
              <label className="settings-switch">
                <input type="checkbox" checked={autoSaveEnabled} onChange={onToggleAutoSave} />
                <span className="settings-slider"></span>
              </label>
            </div>
          </div>
          <div className="settings-section">
            <h3>Voice</h3>
            <div className="settings-item">
              <span>Speak & Create Mode</span>
              <label className="settings-switch">
                <input type="checkbox" checked={voiceEnabled} onChange={onToggleVoice} />
                <span className="settings-slider"></span>
              </label>
            </div>
          </div>
          <div className="settings-section">
            <h3>Tutorial</h3>
            <div className="settings-item">
              <span>Show Getting Started on Load</span>
              <label className="settings-switch">
                <input
                  type="checkbox"
                  checked={tutorialShown !== true}
                  onChange={onToggleTutorial}
                />
                <span className="settings-slider"></span>
              </label>
            </div>
            <div className="settings-item" style={{ borderBottom: 'none' }}>
              <span>Open Tutorial Now</span>
              <button className="settings-action-btn" onClick={onOpenTutorial}>
                Show
              </button>
            </div>
          </div>

        </div>
      </div>
      <style>{`
        .settings-panel-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .settings-panel {
          background-color: #2c2c2c;
          border-radius: 20px;
          width: 90%;
          max-width: 400px;
          max-height: 80vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border: 1px solid #444;
        }
        .theme-light .settings-panel {
          background-color: #ffffff;
          border-color: #E0E0E0;
        }
        .settings-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #444;
        }
        .theme-light .settings-header {
          border-bottom-color: #e0e0e0;
        }
        .settings-header h2 {
          color: white;
          margin: 0;
          font-size: 18px;
          font-family: Avenir, sans-serif;
        }
        .theme-light .settings-header h2 {
          color: #333;
        }
        .settings-close {
          background: none;
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
        }
        .theme-light .settings-close {
          color: #666;
        }
        .settings-content {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
        }
        .settings-section {
          margin-bottom: 24px;
        }
        .settings-section h3 {
          color: #4594F9;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
          font-family: Avenir, sans-serif;
        }
        .settings-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #444;
        }
        .theme-light .settings-item {
          border-bottom-color: #f0f0f0;
        }
        .settings-item span {
          color: white;
          font-family: Avenir, sans-serif;
          font-size: 14px;
        }
        .theme-light .settings-item span {
          color: #333;
        }
        .settings-toggle-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 20px;
          border: none;
          cursor: pointer;
          font-family: Avenir, sans-serif;
          font-size: 12px;
          background: #444;
          color: white;
        }
        .theme-light .settings-toggle-btn.light {
          background: #f0f0f0;
          color: #333;
        }
        .theme-dark .settings-toggle-btn.dark {
          background: #444;
          color: white;
        }
        .settings-action-btn {
          padding: 8px 16px;
          border-radius: 20px;
          border: none;
          cursor: pointer;
          background: #4594f9;
          color: white;
          font-family: Avenir, sans-serif;
          font-size: 12px;
        }
        .settings-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 24px;
        }
        .settings-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .settings-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 24px;
        }
        .settings-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        input:checked + .settings-slider {
          background-color: #4594f9;
        }
        input:checked + .settings-slider:before {
          transform: translateX(26px);
        }
      `}</style>
    </div>
  );
};

// ============================================
// QUICK STYLE PANEL COMPONENT
// ============================================
const QuickStylePanel = ({ isOpen, onClose, canvasImage, onStyleGenerated, authUser }) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [status, setStatus] = useState('');

  if (!isOpen) return null;

  const generateStyle = async () => {
    if (!prompt.trim() || !canvasImage) return;

    if (!authUser) {
      setStatus('Please log in to generate styles');
      return;
    }

    setIsGenerating(true);
    setStatus('Checking credits...');

    try {
      const COST = 2;
      const userRef = doc(db, 'users', authUser.uid);
      let userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        setStatus('User not found.');
        setIsGenerating(false);
        return;
      }

      let userData = userSnap.data();
      let remaining = getRemainingCredits(userData);

      if (remaining.images < COST) {
        setStatus(`Insufficient credits. Need ${COST}, have ${remaining.images}.`);
        setIsGenerating(false);
        return;
      }

      setStatus(`Deducting ${COST} credits...`);
      const deduction = await deductCreditsWithTimeout(authUser.uid, COST, { generationType: 'quick_style' });
      if (!deduction.success) {
        setStatus(`Credit deduction failed: ${deduction.error}`);
        setIsGenerating(false);
        return;
      }

      setStatus('Generating style variation...');

      const isDev = import.meta.env.DEV;
      const replicateBaseUrl = isDev ? '/api/replicate' : 'https://api.replicate.com';
      const finalPrompt = `Transform this room to ${prompt} style. CRITICAL: Keep the EXACT SAME camera angle, EXACT SAME object positions, EXACT SAME room dimensions and spatial layout. Only change the colors, materials, and textures. Preserve all depth relationships.`;
      const requestBody = {
        version: "black-forest-labs/flux-depth-pro",
        input: {
          prompt: finalPrompt,
          image: canvasImage,
          control_image: canvasImage,
          guidance: 8,
          num_outputs: 4,
          aspect_ratio: "9:16",
          output_format: "png",
          output_quality: 100,
          num_inference_steps: 28,
          disable_safety_checker: true,
          watermark: false
        }
      };
      const response = await axios.post(
        `${replicateBaseUrl}/v1/predictions`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${import.meta.env.VITE_REPLICATE_API_TOKEN}`,
          },
          timeout: 120000
        }
      );
      const predictionId = response.data.id;
      const getUrl = `${replicateBaseUrl}/v1/predictions/${predictionId}`;
      let attempts = 0;
      let output = null;
      while (attempts < 90 && !output) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const pollResponse = await axios.get(getUrl, {
          headers: { 'Authorization': `Token ${import.meta.env.VITE_REPLICATE_API_TOKEN}` }
        });
        if (pollResponse.data.status === 'succeeded') {
          const result = pollResponse.data.output;
          let imageUrls = [];
          if (Array.isArray(result)) {
            imageUrls = result.filter(item => typeof item === 'string' && (item.startsWith('http') || item.startsWith('data:')));
          } else if (typeof result === 'string') {
            imageUrls = [result];
          } else if (result && result.url) {
            imageUrls = [result.url];
          }
          output = imageUrls;
          break;
        } else if (pollResponse.data.status === 'failed') {
          throw new Error('Generation failed');
        }
        attempts++;
      }

      if (output && output.length > 0) {
        userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const updatedData = userSnap.data();
          const updatedRemaining = getRemainingCredits(updatedData);
          if (window.updateRemainingCredits) {
            window.updateRemainingCredits(updatedRemaining.images);
          }
        }

        const cleanName = prompt.replace(/\b(before|after)\s*\d*\b/gi, '').trim() || prompt;

        onStyleGenerated({
          name: cleanName,
          generatedImage: output[0],
          variations: output.filter(url => typeof url === 'string' && url.startsWith('http'))
        });
        setStatus('');
        onClose();
      } else {
        throw new Error('No images generated');
      }
    } catch (error) {
      console.error('Style generation error:', error);
      setStatus('Generation failed: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="quick-style-overlay" onClick={onClose}>
      <div className="quick-style-panel" onClick={(e) => e.stopPropagation()}>
        <div className="quick-style-header">
          <h2>Quick Style</h2>
          <button className="quick-style-close" onClick={onClose}>✕</button>
        </div>
        <div className="quick-style-content">
          <div className="quick-style-preview">
            <img src={canvasImage} alt="Original" />
          </div>
          <div className="quick-style-input">
            <input
              type="text"
              placeholder="Describe new style (e.g., 'modern minimalist', 'Victorian luxury')..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="quick-style-input-field"
            />
          </div>
          <button
            className="quick-style-generate-btn"
            onClick={generateStyle}
            disabled={isGenerating || !prompt.trim()}
          >
            {isGenerating ? 'Generating...' : 'Generate Style'}
          </button>
          {status && <div className="quick-style-status">{status}</div>}
        </div>
        <style>{`
          .quick-style-overlay {
            position: fixed; top:0; left:0; right:0; bottom:0;
            background: rgba(0,0,0,0.5); z-index:2000;
            display: flex; align-items: center; justify-content: center;
          }
          .quick-style-panel {
            background: #2c2c2c; border-radius: 20px; width: 90%; max-width: 380px;
            overflow: hidden;
            border: 1px solid #444;
          }
          .theme-light .quick-style-panel { background: white; border-color: #E0E0E0; }
          .quick-style-header {
            display: flex; justify-content: space-between; align-items: center;
            padding: 16px 20px; border-bottom: 1px solid #444;
          }
          .theme-light .quick-style-header { border-bottom-color: #E0E0E0; }
          .quick-style-header h2 { color: white; margin:0; font-size: 18px; }
          .theme-light .quick-style-header h2 { color: #333; }
          .quick-style-close { background: none; border: none; color: white; font-size: 20px; cursor: pointer; }
          .theme-light .quick-style-close { color: #666; }
          .quick-style-content { padding: 16px; }
          .quick-style-preview {
            position: relative;
            border-radius: 12px;
            overflow: hidden;
            margin-bottom: 16px;
            border: 2px solid #4594f9;
            height: 640px;
            min-height: 180px;
            max-height: 400px;
            background-color: #1a1a1a;
          }
          .quick-style-preview::before {
            display: none !important;
          }
          .quick-style-preview * {
            position: relative;
            z-index: 1;
          }
          .quick-style-preview img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .quick-style-input-field {
            width:100%; padding: 12px; border-radius: 12px; border: 1px solid #444;
            background:#1a1a1a; color:white; font-family: monospace; margin-bottom: 16px;
          }
          .theme-light .quick-style-input-field { background:#f5f5f5; color:#333; border-color:#e0e0e0; }
          .quick-style-generate-btn {
            width:100%; padding: 12px; border-radius: 30px; border: none;
            background:#4594f9; color:white; font-weight:600; cursor:pointer;
          }
          .quick-style-generate-btn:disabled { opacity: 0.5; cursor: not-allowed; }
          .quick-style-status { text-align:center; margin-top:12px; font-size:12px; color:#4594F9; }
        `}</style>
      </div>
    </div>
  );
};

// ============================================
// INPAINTING VARIATIONS MENU COMPONENT
// ============================================
const InpaintingVariationsMenu = ({ isOpen, variations, onSelectVariation, onClose, layerId, layers }) => {
  if (!isOpen) return null;

  const activeLayer = layers?.find(l => l.id === layerId);
  const allVariations = activeLayer?.variationHistory || variations || [];
  const uniqueVariations = [...new Set(allVariations)];

  return (
    <div className="inpainting-variations-menu">
      <div className="variations-header">
        <h3>Select Variation ({uniqueVariations.length} available)</h3>
        <button className="variations-close" onClick={onClose}>✕</button>
      </div>
      <div className="variations-container">
        {uniqueVariations.map((variation, index) => (
          <div
            key={index}
            className={`variation-item ${activeLayer?.selectedVariationIndex === index ? 'selected' : ''}`}
            onClick={() => onSelectVariation(variation, index)}
          >
            <img src={variation} alt={`Variation ${index + 1}`} />
            <span className="variation-label">V{index + 1}</span>
          </div>
        ))}
      </div>
      <style>{`
        .inpainting-variations-menu {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background-color: #333333;
          border-top: 1px solid #000000;
          z-index: 50;
          max-height: 200px;
          overflow-y: auto;
        }
        .theme-light .inpainting-variations-menu {
          background-color: #FFFFFF;
          border-top-color: #E0E0E0;
        }
        @media (min-width: 768px) {
          .inpainting-variations-menu {
            display: none !important;
          }
        }
        .variations-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          background-color: #595959;
          border-bottom: 1px solid black;
        }
        .theme-light .variations-header {
          background-color: #F0F0F0;
          border-bottom-color: #E0E0E0;
        }
        .variations-header h3 { color: white; margin: 0; font-size: 14px; }
        .theme-light .variations-header h3 { color: #333; }
        .variations-close { background: none; border: none; color: white; font-size: 20px; cursor: pointer; padding: 4px 8px; }
        .theme-light .variations-close { color: #666; }
        .variations-container { display: flex; gap: 12px; padding: 16px; overflow-x: auto; overflow-y: hidden; white-space: nowrap; -webkit-overflow-scrolling: touch; }
        .variation-item {
          flex-shrink: 0;
          width: 120px;
          cursor: pointer;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid transparent;
          transition: border-color 0.2s ease;
        }
        .variation-item:hover { border-color: #4594f9; }
        .variation-item.selected { border-color: #4594f9; box-shadow: 0 0 0 2px rgba(69, 148, 249, 0.3); }
        .variation-item img { width: 100%; height: 120px; object-fit: cover; display: block; }
        .variation-label { display: block; text-align: center; color: white; font-size: 12px; padding: 6px; background-color: #2c2c2c; }
        .theme-light .variation-label { color: #333; background-color: #F5F5F5; }
      `}</style>
    </div>
  );
};

// ============================================
// DEBUG CONSOLE COMPONENT
// ============================================
const DebugConsole = ({ logs, isExpanded, onToggle, onClear, onCopy }) => {
  if (!DEBUG_CONSOLE_ENABLED) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: isExpanded ? '80px' : '10px',
      right: '10px',
      width: isExpanded ? '90%' : '50px',
      maxWidth: '400px',
      maxHeight: isExpanded ? '300px' : '40px',
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      color: '#0f0',
      borderRadius: '8px',
      zIndex: 99999,
      fontFamily: 'monospace',
      fontSize: '10px',
      overflow: 'hidden',
      transition: 'all 0.2s ease',
      border: '1px solid #333',
      pointerEvents: 'auto'
    }}>
      <div style={{
        padding: '8px',
        backgroundColor: '#1a1a1a',
        borderBottom: isExpanded ? '1px solid #333' : 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer'
      }} onClick={onToggle}>
        <span style={{ color: '#4594f9', fontWeight: 'bold' }}>
          🐛 Debug {logs.length > 0 && `(${logs.length})`}
        </span>
        <div style={{ display: 'flex', gap: '5px' }}>
          {isExpanded && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onCopy(); }}
                style={{ background: '#333', border: 'none', color: '#0f0', padding: '2px 6px', borderRadius: '4px', fontSize: '8px', cursor: 'pointer' }}
              >📋</button>
              <button
                onClick={(e) => { e.stopPropagation(); onClear(); }}
                style={{ background: '#333', border: 'none', color: '#f00', padding: '2px 6px', borderRadius: '4px', fontSize: '8px', cursor: 'pointer' }}
              >🗑</button>
            </>
          )}
          <span style={{ fontSize: '12px' }}>{isExpanded ? '▼' : '▲'}</span>
        </div>
      </div>

      {isExpanded && (
        <div style={{ maxHeight: '250px', overflowY: 'auto', padding: '8px' }}>
          {logs.length === 0 ? (
            <div style={{ color: '#666', textAlign: 'center', padding: '10px' }}>No logs yet...</div>
          ) : (
            logs.map((log, i) => (
              <div key={i} style={{ borderBottom: '1px solid #222', padding: '4px 0', color: log.type === 'error' ? '#f66' : log.type === 'warn' ? '#ff6' : log.type === 'success' ? '#6f6' : '#0f0' }}>
                <span style={{ color: '#666' }}>{log.timestamp}</span>
                <span style={{ marginLeft: '8px' }}>{log.message}</span>
                {log.data && (
                  <div style={{ marginLeft: '8px', color: '#888', wordBreak: 'break-all' }}>
                    {typeof log.data === 'object' ? JSON.stringify(log.data, null, 2) : log.data}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPASS TOOLS MODAL
// ============================================
const CompassToolsModal = ({ onClose, uiTheme = 'light' }) => {
  const { showComingSoonSection } = useNotification();
  const T = THEME_TOKENS[uiTheme] || THEME_TOKENS.light;

  const tools = [
    {
      icon: <FaRuler size={24} />,
      label: 'Measurement',
      description: 'Annotate dimensions directly on your design',
    },
    {
      icon: <FiEdit2 size={24} />,
      label: 'Floor Plan Sketch',
      description: 'Draw basic layouts alongside your photo edit',
    },
    {
      icon: <FaMapPin size={24} />,
      label: 'Structural Notes',
      description: 'Flag load‑bearing walls or key elements',
    },
    {
      icon: <CiExport size={24} />,
      label: 'Export with Annotations',
      description: 'Share planning context with clients or contractors',
    },
  ];

  const handleToolClick = () => {
    showComingSoonSection('ARCHITECTURAL_TOOLS');
  };

  return (
    <div className={`compass-tools-overlay theme-${uiTheme}`} onClick={onClose}>
      <div className="compass-tools-panel" onClick={e => e.stopPropagation()}>
        <button className="compass-tools-close" onClick={onClose}>
          <FiX size={24} />
        </button>

        <div className="compass-tools-header">
          <LuDraftingCompass size={28} color={T.accent} />
          <h2>Architectural Tools</h2>
        </div>

        <div className="compass-tools-list">
          {tools.map((tool) => (
            <button
              key={tool.label}
              onClick={handleToolClick}
              className="compass-tool-item"
            >
              <span className="compass-tool-icon">{tool.icon}</span>
              <div>
                <div className="compass-tool-label">{tool.label}</div>
                <div className="compass-tool-desc">{tool.description}</div>
              </div>
            </button>
          ))}
        </div>

        <button onClick={onClose} className="compass-tools-close-btn">
          Close
        </button>
      </div>
      <style>{`
        .compass-tools-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          backdrop-filter: blur(6px);
          background-color: rgba(0,0,0,0.2);
          z-index: 20000;
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          font-family: Avenir, sans-serif;
        }
        .theme-light.compass-tools-overlay { background-color: rgba(0,0,0,0.25); }
        .theme-dark.compass-tools-overlay  { background-color: rgba(0,0,0,0.55); }
        .compass-tools-panel {
          border-radius: 24px;
          max-width: 480px;
          width: 100%;
          padding: 28px 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.25);
          position: relative;
        }
        .theme-light .compass-tools-panel { background: #FAFAFA; color: #333; }
        .theme-dark  .compass-tools-panel { background: #2c2c2c; color: #fff; border: 1px solid #444; }
        .compass-tools-close {
          position: absolute; top: 12px; right: 12px;
          background: transparent; border: none; cursor: pointer;
          padding: 4px; border-radius: 50%;
          transition: background 0.2s;
        }
        .theme-light .compass-tools-close { color: #888; }
        .theme-dark  .compass-tools-close { color: #ccc; }
        .theme-light .compass-tools-close:hover { background: #f0f0f0; }
        .theme-dark  .compass-tools-close:hover { background: #3a3a3a; }
        .compass-tools-header {
          display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
        }
        .compass-tools-header h2 {
          font-size: 1.3rem; font-weight: 600; margin: 0;
          font-family: Avenir, sans-serif;
        }
        .theme-light .compass-tools-header h2 { color: #333; }
        .theme-dark  .compass-tools-header h2 { color: #fff; }
        .compass-tools-list {
          display: flex; flex-direction: column; gap: 12px;
        }
        .compass-tool-item {
          display: flex; align-items: center; gap: 14px;
          padding: 14px 18px;
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left; width: 100%;
          font-family: Avenir, sans-serif;
        }
        .theme-light .compass-tool-item {
          border: 1px solid #e8e8e8; background: white; color: #333;
        }
        .theme-dark .compass-tool-item {
          border: 1px solid #444; background: #3a3a3a; color: #fff;
        }
        .theme-light .compass-tool-item:hover { border-color: #4594F9; background: #f5f9ff; }
        .theme-dark  .compass-tool-item:hover { border-color: #4594F9; background: #3f4859; }
        .compass-tool-icon {
          color: #4594F9; display: flex; align-items: center;
        }
        .compass-tool-label { font-weight: 600; }
        .compass-tool-desc { font-size: 0.8rem; }
        .theme-light .compass-tool-desc { color: #888; }
        .theme-dark  .compass-tool-desc { color: #bbb; }
        .compass-tools-close-btn {
          margin-top: 20px; width: 100%; padding: 12px;
          border-radius: 30px; border: none; cursor: pointer;
          font-size: 0.9rem; font-weight: 500;
          transition: background 0.2s;
          font-family: Avenir, sans-serif;
        }
        .theme-light .compass-tools-close-btn { background: #f0f0f0; color: #666; }
        .theme-dark  .compass-tools-close-btn { background: #3a3a3a; color: #ddd; }
        .theme-light .compass-tools-close-btn:hover { background: #e0e0e0; }
        .theme-dark  .compass-tools-close-btn:hover { background: #484848; }
      `}</style>
    </div>
  );
};

// ============================================
// RENDER-TIME COMPOSITE BUILDER
// ----------------------------------------------------------------------------
// All compositing code (loadImageElement, computeHomography,
// buildFrameComposite, the IndexedDB cache) has been extracted to
// ../services/compositeService so Dashboard's prefetch pipeline and the
// Editor share exactly one implementation. Imports at the top of this file.
// ============================================

// ============================================
// MAIN EDITOR COMPONENT
// ============================================
const Editor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast, showFullScreenModal, showTutorialModal, showComingSoonSection } = useNotification();

  // Usage state
  const [imagesRemaining, setImagesRemaining] = useState(0);
  const [userData, setUserData] = useState(null);

  // ============================================
  // FIREBASE AUTH & USER STATE
  // ============================================
  const [authUser, setAuthUser] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  // ============================================
  // UNIFIED PROJECT LOAD STATE
  // ----------------------------------------------------------------------------
  // isProjectLoading gates the mobile/desktop canvas placeholder. It stays
  // true from the moment we know we're loading a saved project until the
  // FINAL composite (base + frames) is ready to render. This prevents the
  // "bare base flashes, then frames pop in" regression.
  //
  // baseCanonicalRef     — true once the authoritative working base (from
  //                        Firestore, not location.state) has been set.
  // framesAttemptedRef   — true once the frames load pipeline has run to
  //                        completion (whether or not frames exist).
  // bothReadyRef         — shortcut: both above are true.
  // ============================================
  const [isProjectLoading, setIsProjectLoading] = useState(true);
  const [loadStatusText, setLoadStatusText] = useState('Loading project…');
  // projectReady gates the composite effect. It flips to true exactly once
  // per project load, AFTER the canonical working base and the frames list
  // have both landed in state. Without this gate the composite effect fires
  // multiple times during load (once when the base arrives, once when frames
  // arrive, once when normalized frames arrive), tripling the composite work
  // and turning a 1-2s load into 5-10s.
  const [projectReady, setProjectReady] = useState(false);
  const baseCanonicalRef = useRef(false);
  const framesAttemptedRef = useRef(false);
  const hasProjectToLoadRef = useRef(false);

  // Auth State Listener
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user);
      if (user) {
        try {
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            const remaining = getRemainingCredits(data);
            setImagesRemaining(remaining.images);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUserData(null);
      }
      setIsProfileLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // ============================================
  // PROJECT STATE FROM DASHBOARD NAVIGATION
  // ============================================
  const [projectData, setProjectData] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // ============================================
  // ASSET IMPORT STATE
  // ============================================
  const [pendingAsset, setPendingAsset] = useState(null);
  const [shouldOpenAssetPanel, setShouldOpenAssetPanel] = useState(false);

  // ============================================
  // AUTO-SAVE STATE
  // ============================================
  const [autoSaveInterval, setAutoSaveInterval] = useState(null);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSaveFailed, setLastSaveFailed] = useState(false);

  // === PERSISTENCE FIX ===
  // Serializes saves so auto-save, beforeunload, and the exit flow cannot
  // clobber one another.
  const saveInFlightRef = useRef(false);
  const lastSavedHashRef = useRef(null);

  // UI State
  const [uiTheme, setUiTheme] = useState(() => {
    return localStorage.getItem('editorUITheme') || 'light';
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [showQuickStylePanel, setShowQuickStylePanel] = useState(false);
  const [showCompassTools, setShowCompassTools] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportFormat, setExportFormat] = useState('png');
  const [exportQuality, setExportQuality] = useState(70);
  const [showRendersPanel, setShowRendersPanel] = useState(false);
  const [renderPanelCanvasImage, setRenderPanelCanvasImage] = useState(null);
  const [showWallColorTool, setShowWallColorTool] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const [activeToolName, setActiveToolName] = useState(null);
  const [showPictureFrameTool, setShowPictureFrameTool] = useState(false);
  const [frameImages, setFrameImages] = useState([]);

  // ── Rooms ──────────────────────────────────────────────────────────────
  // A "room" is a sibling background canvas with its own base image,
  // layer stack, selection, and frame set. The project always has at
  // least one room ("Original"), seeded after the initial load lands.
  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [layersPanelTab, setLayersPanelTab] = useState('layers');
  const [showRoomsPanel, setShowRoomsPanel] = useState(false);
  const roomsRef = useRef(rooms);
  const activeRoomIdRef = useRef(activeRoomId);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingDots, setLoadingDots] = useState('');
  const [pointerPosition, setPointerPosition] = useState({ x: null, y: null });
  const [isDrawingLasso, setIsDrawingLasso] = useState(false);
  const [lassoPoints, setLassoPoints] = useState([]);
  const [currentLassoPath, setCurrentLassoPath] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // Layers state
  const [layers, setLayers] = useState([]);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [showLayersMenu, setShowLayersMenu] = useState(false);
  const [mobileLayersPanelVisible, setMobileLayersPanelVisible] = useState(true);
  const [originalImage, setOriginalImage] = useState(null);
  const [layerCounter, setLayerCounter] = useState(1);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const lastSaveDataRef = useRef(null);
  const canvasImageRef = useRef(null);
  const displayImageUrlRef = useRef(null);

  const recolorToolRef = useRef(null);
  const pictureFrameToolRef = useRef(null);
  const [rightSidebarTool, setRightSidebarTool] = useState(null);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);

  const hasInitializedRef = useRef(false);
  const frameImagesRef = useRef(frameImages);

  // Ensure RendersPanel starts closed on mount and when project loads
  useEffect(() => {
    setShowRendersPanel(false);
  }, []);

  // Close RendersPanel when project changes
  useEffect(() => {
    if (currentProjectId || projectData) {
      setShowRendersPanel(false);
    }
  }, [currentProjectId, projectData]);

  // Close RendersPanel when navigating away or loading new project
  useEffect(() => {
    return () => {
      setShowRendersPanel(false);
      setRenderPanelCanvasImage(null);
    };
  }, []);

  // Prevent auto-opening of RendersPanel on mount
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setShowRendersPanel(false);
    }
  }, []);

  // Set theme color for Editor page
  useEffect(() => {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head.appendChild(metaThemeColor);
    }

    metaThemeColor.content = '#2D2D2D';
    document.documentElement.style.backgroundColor = '#2D2D2D';
    document.body.style.backgroundColor = '#2D2D2D';

    return () => {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
      if (metaThemeColor) {
        metaThemeColor.content = '#2D2D2D';
      }
    };
  }, []);

  // ============================================
  // HANDLE NAVIGATION STATE FROM DASHBOARD
  // ----------------------------------------------------------------------------
  // NOTE: We deliberately do NOT set originalImage here. The pristine
  // original is authoritative from Firestore (project.originalPhotoURL).
  // Setting it from `data.photo` (which may be a legacy composite) was a
  // root cause of the duplication bug.
  //
  // Provisional canvas: we set canvasImage/displayImageUrl from
  // data.photo so the canvas area has something to show immediately, but
  // we do NOT flip isProjectLoading to false here. The Firestore load
  // effect below is the authority that resolves the loading state.
  // ============================================
  useEffect(() => {
    if (location.state?.projectData) {
      const data = location.state.projectData;
      const pid = data.projectId || data.id || null;

      // ────────────────────────────────────────────────────────────────
      // PREFETCH FAST PATH
      // ----------------------------------------------------------------------------
      // Dashboard has already run the entire pipeline (project doc →
      // canvas state → frames → bitmaps → working base → composite) and
      // attached the result to location.state.projectData.__preloaded.
      // We populate every piece of state here in ONE commit and flip the
      // load gate open immediately — no Firestore read, no composite
      // rebuild, no spinner in the editor.
      //
      // The composite effect downstream will see the same fingerprint it
      // was going to build and short-circuit via its cache check.
      // ────────────────────────────────────────────────────────────────
      const pre = data.__preloaded;
      if (pre && pre.workingBase) {
        // Strip the payload off the object we park in projectData state
        // so it never gets re-serialized or accidentally re-consumed.
        const { __preloaded, ...cleanData } = data;

        setProjectData({ ...cleanData, projectId: pid });

        if (pid) {
          setCurrentProjectId(pid);
          sessionStorage.setItem('currentProjectId', pid);
        } else {
          setCurrentProjectId(null);
          sessionStorage.removeItem('currentProjectId');
        }

        // Populate canonical + derived state in a single synchronous pass.
        // Rooms: if Dashboard passed preloaded rooms, restore them. If not,
        // the seed effect will create the "Original" room once the base
        // settles.
        if (Array.isArray(pre.rooms) && pre.rooms.length > 0) {
          const restoredRoomsPre = pre.rooms.map(r => ({
            id: r.id,
            name: r.name || 'Room',
            canvasImage: r.canvasImage || null,
            originalImage: r.originalImage || r.canvasImage || null,
            thumbnail: r.thumbnail || r.canvasImage || null,
            naturalWidth: r.naturalWidth || 0,
            naturalHeight: r.naturalHeight || 0,
            layers: Array.isArray(r.layers) ? r.layers : [],
            layerCount: typeof r.layerCount === 'number'
              ? r.layerCount
              : (Array.isArray(r.layers) ? r.layers.length : 0),
            selectedLayerId: r.selectedLayerId || null,
            frameImages: Array.isArray(r.frameImages) ? r.frameImages : [],
            createdAt: r.createdAt || Date.now(),
          }));
          const restoredActivePre = pre.activeRoomId
            || (restoredRoomsPre[0] && restoredRoomsPre[0].id)
            || null;
          setRooms(restoredRoomsPre);
          setActiveRoomId(restoredActivePre);
          activeRoomIdRef.current = restoredActivePre;
          roomsRef.current = restoredRoomsPre;
        }

        setOriginalImage(pre.pristineBase || pre.workingBase);
        setCanvasImage(pre.workingBase);
        canvasImageRef.current = pre.workingBase;

        // If Dashboard produced a composite, use it directly. Otherwise
        // the composite effect below will rebuild from the working base.
        if (pre.composite) {
          setDisplayImageUrl(pre.composite);
          displayImageUrlRef.current = pre.composite;
        } else {
          setDisplayImageUrl(pre.workingBase);
          displayImageUrlRef.current = pre.workingBase;
        }

        // ── Frames source of truth ────────────────────────────────────
        // The ACTIVE ROOM's own `frameImages` must drive the composite
        // on load, NOT the project-level `pre.frames` mirror. The
        // project-level list is a legacy snapshot of whichever room was
        // active when the project was last saved. If the user switched
        // rooms between save and load — or if a save partially failed
        // and left the project-level mirror out of sync with the room
        // record — using it here renders that OTHER room's frames on
        // the currently active canvas. The active room's own record is
        // the only correct source for the frames that belong on the
        // canvas being shown.
        //
        // Falls back to `pre.frames` only when the project has no
        // rooms (pre-Rooms projects), where the top-level list IS the
        // sole source of frame data. An empty `frameImages` on the
        // active room is also authoritative: the room genuinely has no
        // frames, and that must not fall through to the project-level
        // mirror.
        const activeRoomPre = (Array.isArray(pre.rooms) && pre.rooms.length > 0)
          ? (pre.rooms.find(r => r.id === (pre.activeRoomId || pre.rooms[0].id)) || null)
          : null;
        if (activeRoomPre) {
          const roomFrames = Array.isArray(activeRoomPre.frameImages)
            ? activeRoomPre.frameImages
            : [];
          setFrameImages(roomFrames);
          frameImagesRef.current = roomFrames;
        } else if (Array.isArray(pre.frames) && pre.frames.length > 0) {
          setFrameImages(pre.frames);
          frameImagesRef.current = pre.frames;
        }
        if (Array.isArray(pre.layers) && pre.layers.length > 0) {
          setLayers(pre.layers);
        }
        if (pre.selectedLayerId) {
          setSelectedLayerId(pre.selectedLayerId);
        }
        if (pre.currentLassoPath) {
          setCurrentLassoPath(pre.currentLassoPath);
        }

        // Measure dimensions once so downstream lasso maths is correct
        // on the very first paint.
        if (pre.workingBase) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => setImageDimensions({ width: img.width, height: img.height });
          img.src = pre.workingBase;
        }

        // Mark every load-phase ref as settled and open the gate —
        // EXCEPT when the prefetched payload had no rooms AND we have a
        // project ID to fetch them from. In that case we defer the gate
        // to the Firestore load effect (which reads the project's
        // roomsStateURL) so rooms the user previously uploaded actually
        // come back on reload. Without this deferral the fast path
        // short-circuits the Firestore load, and every room added in a
        // previous session is silently dropped on reopen.
        const hasPreloadedRooms = Array.isArray(pre.rooms) && pre.rooms.length > 0;
        if (hasPreloadedRooms || !pid) {
          baseCanonicalRef.current = true;
          framesAttemptedRef.current = true;
          hasProjectToLoadRef.current = true;
          setProjectReady(true);
          setIsProjectLoading(false);
          setLoadStatusText('');
          setIsImageLoading(false);
        } else {
          // Rooms are missing from the payload. Leave the canonical-base
          // ref false so the Firestore load effect runs and restores
          // rooms from roomsStateURL.
          baseCanonicalRef.current = false;
          framesAttemptedRef.current = false;
          hasProjectToLoadRef.current = true;
          setIsProjectLoading(true);
          setLoadStatusText('Loading rooms…');
        }

        // ── Non-prefetch side effects (asset import, autosave) ──────
        const pendingAssetStr = sessionStorage.getItem('pendingAsset');
        if (pendingAssetStr) {
          try {
            const asset = JSON.parse(pendingAssetStr);
            setPendingAsset(asset);
            sessionStorage.removeItem('pendingAsset');
          } catch (error) {
            console.error('Error parsing pending asset:', error);
          }
        }
        if (location.state?.openAssetLibrary) {
          setShouldOpenAssetPanel(true);
        }
        return;
      }

      // ────────────────────────────────────────────────────────────────
      // LEGACY PATH — no __preloaded payload. The load effect below will
      // hit Firestore. This preserves the old behavior for any caller
      // that still navigates without the prefetch payload.
      // ────────────────────────────────────────────────────────────────
      if (pid) {
        hasProjectToLoadRef.current = true;
        setIsProjectLoading(true);
        setLoadStatusText('Loading project…');
        baseCanonicalRef.current = false;
        framesAttemptedRef.current = false;
      }

      setProjectData({
        ...data,
        projectId: pid
      });

      if (pid) {
        setCurrentProjectId(pid);
        sessionStorage.setItem('currentProjectId', pid);
      } else {
        setCurrentProjectId(null);
        sessionStorage.removeItem('currentProjectId');
      }

      // Provisional canvas — the load-from-Firestore effect will overwrite
      // this with the pristine base as soon as the project doc is read.
      const provisional = data.photo || data.photoURL;
      if (provisional) {
        setCanvasImage(provisional);
        setDisplayImageUrl(provisional);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setImageDimensions({ width: img.width, height: img.height });
        img.src = provisional;
      }

      if (data.frameImagesURL) {
        fetch(data.frameImagesURL)
          .then(res => res.json())
          .then(frames => {
            setFrameImages(frames);
            frameImagesRef.current = frames;
          })
          .catch(err => console.error('Failed to load frame images from URL:', err));
      } else if (data.frameImages) {
        setFrameImages(data.frameImages);
        frameImagesRef.current = data.frameImages;
      }

      if (data.canvasState) {
        if (data.canvasState.layers) {
          setLayers(data.canvasState.layers);
        }
        if (data.canvasState.selectedLayerId) {
          setSelectedLayerId(data.canvasState.selectedLayerId);
        }
        if (data.canvasState.currentLassoPath) {
          setCurrentLassoPath(data.canvasState.currentLassoPath);
        }
      }
    } else {
      const savedProjectId = sessionStorage.getItem('currentProjectId');
      if (savedProjectId) {
        hasProjectToLoadRef.current = true;
        setIsProjectLoading(true);
        setLoadStatusText('Loading project…');
        baseCanonicalRef.current = false;
        framesAttemptedRef.current = false;
        setCurrentProjectId(savedProjectId);
      } else {
        // Brand new project — nothing to wait on.
        baseCanonicalRef.current = true;
        framesAttemptedRef.current = true;
        setProjectReady(true);
        setIsProjectLoading(false);
      }
    }

    const pendingAssetStr = sessionStorage.getItem('pendingAsset');
    if (pendingAssetStr) {
      try {
        const asset = JSON.parse(pendingAssetStr);
        setPendingAsset(asset);
        sessionStorage.removeItem('pendingAsset');
      } catch (error) {
        console.error('Error parsing pending asset:', error);
      }
    }

    if (location.state?.openAssetLibrary) {
      setShouldOpenAssetPanel(true);
    }

    if (location.state?.projectData?.projectId && authUser) {
      recoverAutoSave(location.state.projectData.projectId);
    }
  }, [location, authUser]);

  // ============================================
  // LOAD PROJECT — Schema v4 aware
  // ----------------------------------------------------------------------------
  // Rebuilds:
  //   originalImage = originalPhotoURL (pristine) || photoURL
  //   canvasImage   = replay(originalImage, nonFrameLayers)
  //   frameImages   = subcollection → frameImagesURL → legacy array
  // The composite is then produced by the composite effect downstream.
  //
  // Timing instrumentation: every step calls loadDebugMark so the browser
  // console shows exactly where the seconds go.
  // ============================================
  useEffect(() => {
    const loadProjectFromFirestore = async () => {
      if (!authUser || !currentProjectId) return;

      loadDebugStart(`loadProjectFromFirestore(pid=${currentProjectId})`);
      setLoadStatusText('Fetching project…');

      try {
        const t0 = performance.now();
        let project = await projectService.getProject(currentProjectId);
        loadDebugMark('project doc fetched', {
          elapsedMs: (performance.now() - t0).toFixed(0),
          hasOriginal: !!project?.originalPhotoURL,
          hasWorking: !!project?.photoURL,
          hasCanvasStateURL: !!project?.canvasStateURL,
          hasFrameImages: Array.isArray(project?.frameImages) ? project.frameImages.length : 0,
        });
        if (!project) {
          baseCanonicalRef.current = true;
          framesAttemptedRef.current = true;
          setIsProjectLoading(false);
          loadDebugSummary();
          return;
        }

        // ------- Migration v1 → v2 (legacy canvasState) -------
        let needsMigration = false;
        let migratedCanvasState = null;

        if ((!project.schemaVersion || project.schemaVersion < 2) && project.canvasState) {
          needsMigration = true;
          setLoadStatusText('Upgrading project…');
          try {
            let stateObj = typeof project.canvasState === 'string'
              ? JSON.parse(project.canvasState)
              : project.canvasState;

            const sanitize = (obj) => {
              if (obj === null || obj === undefined) return null;
              if (typeof obj === 'function') return undefined;
              if (obj instanceof Date) return obj.toISOString();
              if (Array.isArray(obj)) {
                return obj.map(item => sanitize(item)).filter(item => item !== undefined);
              }
              if (typeof obj === 'object') {
                try {
                  const seen = new WeakSet();
                  const cleaned = JSON.parse(JSON.stringify(obj, (key, value) => {
                    if (typeof value === 'function') return undefined;
                    if (value instanceof Date) return value.toISOString();
                    if (typeof value === 'object' && value !== null) {
                      if (seen.has(value)) return undefined;
                      seen.add(value);
                    }
                    return value;
                  }));
                  return cleaned;
                } catch {
                  return {};
                }
              }
              return obj;
            };

            const cleanedState = {
              layers: sanitize(stateObj.layers),
              selectedLayerId: sanitize(stateObj.selectedLayerId),
              currentLassoPath: sanitize(stateObj.currentLassoPath),
            };

            const canvasStateURL = await uploadCanvasState(authUser.uid, currentProjectId, cleanedState);
            await projectService.updateProject(currentProjectId, {
              canvasStateURL: canvasStateURL,
              canvasState: null,
              schemaVersion: PROJECT_SCHEMA_VERSION,
            });

            project = await projectService.getProject(currentProjectId);
            migratedCanvasState = cleanedState;

            showToast('Project upgraded to the new format. All data preserved.', 'success', 4000);
          } catch (migrationError) {
            console.error('Migration failed:', migrationError);
            showToast('Failed to upgrade project. Some data may be missing.', 'error', 5000);
          }
        }

        // ------- Rooms: restore persisted rooms (if any) -------
        // Preferred source: roomsStateURL (Storage JSON). Falls back to the
        // legacy `project.rooms` inline array for projects saved before
        // the Storage migration.
        let restoredRooms = [];
        let restoredActiveRoomId = null;
        let rawRooms = null;
        if (project.roomsStateURL) {
          try {
            rawRooms = await fetchRoomsState(project.roomsStateURL);
          } catch (e) {
            console.warn('Failed to fetch rooms state from URL, falling back to inline:', e);
          }
        }
        if (!rawRooms && Array.isArray(project.rooms) && project.rooms.length > 0) {
          rawRooms = project.rooms;
        }
        if (Array.isArray(rawRooms) && rawRooms.length > 0) {
          restoredRooms = rawRooms.map(r => ({
            id: r.id,
            name: r.name || 'Room',
            canvasImage: r.canvasImage || null,
            originalImage: r.originalImage || r.canvasImage || null,
            thumbnail: r.thumbnail || r.canvasImage || null,
            naturalWidth: r.naturalWidth || 0,
            naturalHeight: r.naturalHeight || 0,
            layers: Array.isArray(r.layers) ? r.layers : [],
            layerCount: typeof r.layerCount === 'number'
              ? r.layerCount
              : (Array.isArray(r.layers) ? r.layers.length : 0),
            selectedLayerId: r.selectedLayerId || null,
            frameImages: Array.isArray(r.frameImages) ? r.frameImages : [],
            createdAt: r.createdAt || Date.now(),
          }));
          restoredActiveRoomId = project.activeRoomId
            || (restoredRooms[0] && restoredRooms[0].id)
            || null;
          setRooms(restoredRooms);
          setActiveRoomId(restoredActiveRoomId);
          activeRoomIdRef.current = restoredActiveRoomId;
          roomsRef.current = restoredRooms;
        }

        // ------- Determine pristine base and working base -------
        // The active room (if any) supplies the base image. Falls back to
        // the legacy project-level original/photo if the project has no
        // rooms yet (pre-Rooms projects, or a brand-new project).
        const activeRoomAtLoad = restoredRooms.find(r => r.id === restoredActiveRoomId) || null;
        const pristineBase = activeRoomAtLoad?.originalImage
          || activeRoomAtLoad?.canvasImage
          || project.originalPhotoURL
          || project.photoURL
          || null;

        // Load layers first (needed to replay onto pristine base)
        setLoadStatusText('Loading canvas state…');
        let loadedLayers = [];
        let canvasStateLoaded = false;
        const tState = performance.now();
        if (project.canvasStateURL) {
          try {
            const parsed = await fetchCanvasState(project.canvasStateURL);
            if (Array.isArray(parsed.layers)) loadedLayers = parsed.layers;
            canvasStateLoaded = true;
          } catch (e) {
            console.warn('Failed to load canvas state from URL:', e);
          }
        }
        if (!canvasStateLoaded && migratedCanvasState?.layers) {
          loadedLayers = migratedCanvasState.layers || [];
        } else if (!canvasStateLoaded && project.canvasState) {
          try {
            const parsed = typeof project.canvasState === 'string'
              ? JSON.parse(project.canvasState)
              : project.canvasState;
            if (Array.isArray(parsed.layers)) loadedLayers = parsed.layers;
          } catch (e) {
            console.warn('Failed to parse legacy canvasState', e);
          }
        }
        if (Array.isArray(loadedLayers) && loadedLayers.length > 0) {
          setLayers(loadedLayers);
        }
        loadDebugMark('canvas state loaded', {
          elapsedMs: (performance.now() - tState).toFixed(0),
          layerCount: loadedLayers.length,
        });

        // If an active room has its own layer stack, that's authoritative
        // over the top-level canvasState.
        if (activeRoomAtLoad) {
          if (Array.isArray(activeRoomAtLoad.layers) && activeRoomAtLoad.layers.length > 0) {
            loadedLayers = activeRoomAtLoad.layers;
            setLayers(activeRoomAtLoad.layers);
          }
          if (activeRoomAtLoad.selectedLayerId) {
            setSelectedLayerId(activeRoomAtLoad.selectedLayerId);
          }
        }

        // ------- Load frames: subcollection → frameImagesURL → legacy -------
        setLoadStatusText('Loading frames…');
        let loadedFrames = null;
        const tFrames = performance.now();

        try {
          const subcollectionFrames = await projectAssetService.readFrames(currentProjectId);
          loadDebugMark('frames subcollection read', {
            elapsedMs: (performance.now() - tFrames).toFixed(0),
            count: subcollectionFrames.length,
          });
          if (Array.isArray(subcollectionFrames) && subcollectionFrames.length > 0) {
            const legacyById = new Map(
              (Array.isArray(project.frameImages) ? project.frameImages : [])
                .filter(f => f && f.id)
                .map(f => [f.id, f])
            );
            loadedFrames = subcollectionFrames.map(sf => {
              const legacy = legacyById.get(sf.id) || {};
              return {
                ...legacy,
                ...sf,
                imageData: sf.imageData || sf.imageURL || legacy.imageData,
                originalImage: sf.originalImage || sf.originalURL || legacy.originalImage,
                originalUncroppedData: sf.originalUncroppedData || sf.uncroppedURL || legacy.originalUncroppedData,
                backgroundImage: sf.backgroundImage || sf.backgroundURL || legacy.backgroundImage,
              };
            });
          }
        } catch (e) {
          console.warn('Failed to load frames subcollection:', e);
        }

        if (!loadedFrames && project.frameImagesURL) {
          try {
            const framesData = await fetch(project.frameImagesURL);
            if (framesData.ok) {
              loadedFrames = await framesData.json();
            }
          } catch (e) {
            console.warn('Failed to load frameImagesURL:', e);
          }
        }

        if (!loadedFrames && Array.isArray(project.frameImages)) {
          loadedFrames = project.frameImages;
        }

        // ── Active room's frames are authoritative ────────────────────
        // Same rationale as the prefetch fast path above. The project-
        // level frame list (subcollection / frameImagesURL /
        // project.frameImages) is a legacy mirror of whichever room was
        // active at the last save, and is NOT scoped to any room. When
        // the project has rooms, the ACTIVE ROOM's own `frameImages` is
        // the only correct source for driving the composite — using the
        // project-level mirror here would render the wrong room's
        // frames on the current canvas whenever the active room on load
        // differs from the active room at save.
        //
        // An empty `frameImages` on the active room is authoritative:
        // the room genuinely has no frames, and that must not fall
        // through to the project-level mirror.
        if (activeRoomAtLoad) {
          loadedFrames = Array.isArray(activeRoomAtLoad.frameImages)
            ? activeRoomAtLoad.frameImages
            : [];
        }

        if (loadedFrames && loadedFrames.length > 0) {
          // Normalize frames — ensure rotation field exists for composite.
          // Also run every frame through restoreFirestoreArrays so any
          // nested arrays that were rewritten as numeric-keyed maps on
          // save are converted back to real arrays before the composite
          // and Picture Frame tool consume them.
          const normalized = loadedFrames.map(f => {
            const restored = restoreFirestoreArrays(f);
            return {
              ...restored,
              rotation: restored.rotation || { x: 0, y: 0 },
            };
          });
          setFrameImages(normalized);
          frameImagesRef.current = normalized;

          loadDebugMark('frames set in state', {
            count: normalized.length,
          });

          // Preload every frame's artwork into the browser cache so the
          // subsequent composite build doesn't have to fetch each image
          // serially over the network (which caused the "frames appear
          // seconds late" regression on project open).
          setLoadStatusText('Preloading frame artwork…');
          const tPreload = performance.now();
          await Promise.all(
            normalized.map(f => new Promise(resolve => {
              const src = f.originalImage || f.imageData || f.dataUrl;
              if (!src) return resolve();
              const pre = new Image();
              pre.crossOrigin = 'anonymous';
              pre.onload = () => resolve();
              pre.onerror = () => resolve();
              pre.src = src;
            }))
          );
          loadDebugMark('frames preloaded in cache', {
            elapsedMs: (performance.now() - tPreload).toFixed(0),
            count: normalized.length,
          });
        } else {
          loadDebugMark('no frames to load');
        }

        // ------- Rebuild canvasImage from pristine + non-frame layers -------
        setLoadStatusText('Rebuilding base…');
        const tBase = performance.now();
        let workingBase = pristineBase;
        if (workingBase && loadedLayers.length > 0) {
          for (const layer of loadedLayers) {
            if (!layer) continue;
            if (layer.type === 'frame') continue;
            if (layer.visible === false) continue;
            if (layer.generatedImage) {
              workingBase = layer.generatedImage;
            }
          }
        }

        if (workingBase) {
          setOriginalImage(pristineBase || workingBase);
          setCanvasImage(workingBase);
          canvasImageRef.current = workingBase;
          // Await the img load so we know dimensions are ready BEFORE we
          // flip isProjectLoading. This is the "load simultaneously" fix:
          // the composite effect will fire when canvasImage lands, but the
          // loading gate stays shut until frames are also in state.
          await new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              setImageDimensions({ width: img.width, height: img.height });
              resolve();
            };
            img.onerror = () => resolve();
            img.src = workingBase;
          });
          loadDebugMark('working base image decoded', {
            elapsedMs: (performance.now() - tBase).toFixed(0),
          });
        }

        // Mark canonical base & frames attempt so the composite effect is
        // allowed to flip isProjectLoading to false on its first good pass.
        // Flipping projectReady here (a React state, not a ref) guarantees
        // the composite effect re-runs AFTER both canonical inputs have
        // landed — which is what collapses the 3-in-a-row rebuilds into 1.
        // Seed an "Original" room if the project had none persisted. This
        // covers brand-new projects AND pre-Rooms projects. The seed uses
        // the just-loaded working base as the room canvas.
        if (restoredRooms.length === 0 && workingBase) {
          const seedRoom = {
            id: `room_${Date.now()}_original`,
            name: 'Original',
            canvasImage: workingBase,
            originalImage: pristineBase || workingBase,
            thumbnail: workingBase,
            naturalWidth: imageDimensions.width || 0,
            naturalHeight: imageDimensions.height || 0,
            layers: loadedLayers,
            layerCount: Array.isArray(loadedLayers) ? loadedLayers.length : 0,
            selectedLayerId: null,
            frameImages: [],
            createdAt: Date.now(),
          };
          setRooms([seedRoom]);
          setActiveRoomId(seedRoom.id);
          activeRoomIdRef.current = seedRoom.id;
          roomsRef.current = [seedRoom];
        }

        baseCanonicalRef.current = true;
        framesAttemptedRef.current = true;
        setProjectReady(true);

        setProjectData(prev => {
          if (!prev || !prev.name) {
            return { ...prev, name: project.name, details: project.details };
          }
          return prev;
        });

        if (needsMigration || (project.schemaVersion || 0) < PROJECT_SCHEMA_VERSION) {
          setProjectData(prev => ({ ...prev, schemaVersion: PROJECT_SCHEMA_VERSION }));
        }

        loadDebugSummary();
      } catch (error) {
        console.error('Failed to load project from Firestore:', error);
        showToast('Error loading project. Please try again.', 'error', 5000);
        // On error, let the canvas show whatever we have.
        baseCanonicalRef.current = true;
        framesAttemptedRef.current = true;
        setIsProjectLoading(false);
        loadDebugSummary();
      }
    };

    // Skip the Firestore load if Dashboard handed us a prefetched payload —
    // state is already canonical and the composite effect will short-circuit
    // via its cache check.
    if (currentProjectId && authUser && !baseCanonicalRef.current) {
      loadProjectFromFirestore();
    }
  }, [currentProjectId, authUser, showToast]);

  // ============================================
  // REAL-TIME COLLABORATION — FRAME SYNC
  // ----------------------------------------------------------------------------
  // Mirrors the shared-document-state pattern used by Figma/Canva at the
  // "one document, N readers/writers" tier. Every open Editor session
  // subscribes to the project's frames subcollection; when any session
  // commits a change (via handleSaveChanges → onFrameApplied → the
  // auto-save pipeline), every OTHER session receives an onSnapshot delta
  // and applies it to its local frameImages. The composite effect then
  // rebuilds once for that session.
  //
  // Self-echo suppression: we tag the last time WE wrote frames and skip
  // any snapshot that arrives within a short quiet window afterwards. This
  // prevents the local session from reprocessing its own write.
  //
  // Edit-lock: while the frame tool is open in THIS session we do NOT apply
  // remote changes — the user is mid-edit and clobbering their local state
  // would yank the frame out from under their finger. Once they close the
  // tool the next snapshot will bring them current.
  // ============================================
  const lastLocalFrameWriteAtRef = useRef(0);
  const frameToolOpenRef = useRef(false);

  useEffect(() => {
    frameToolOpenRef.current = showPictureFrameTool;
  }, [showPictureFrameTool]);

  useEffect(() => {
    if (!authUser || !currentProjectId) return;
    // Do not subscribe until the initial load has completed — the load
    // effect already populated frameImages from the same subcollection and
    // we don't want to race it.
    if (!baseCanonicalRef.current) return;

    let unsub = null;
    try {
      const framesCol = collection(db, 'projects', currentProjectId, 'frames');
      unsub = onSnapshot(
        framesCol,
        (snap) => {
          // 1) Self-echo suppression — ignore snapshots that are the echo
          //    of a write we just issued.
          if (Date.now() - lastLocalFrameWriteAtRef.current < 2500) return;

          // 2) Edit-lock — do not clobber a frame being edited here.
          if (frameToolOpenRef.current) return;

          const remote = snap.docs.map((d) => {
            const data = restoreFirestoreArrays(d.data());
            return {
              id: d.id,
              ...data,
              imageData: data.imageData || data.imageURL,
              originalImage: data.originalImage || data.originalURL,
              originalUncroppedData: data.originalUncroppedData || data.uncroppedURL,
              backgroundImage: data.backgroundImage || data.backgroundURL,
              rotation: data.rotation || { x: 0, y: 0 },
            };
          });

          // 3) Idempotency — if the incoming fingerprint matches ours,
          //    there is nothing to do. The composite effect is already
          //    keyed on this same fingerprint so a needless setFrameImages
          //    here would still be a no-op, but we skip it to avoid a
          //    wasted render pass on every snapshot.
          const fp = (arr) => JSON.stringify(
            (arr || []).map((f) => ({
              id: f.id,
              corners: f.corners,
              rotation: f.rotation,
              borderWidth: f.borderWidth || 0,
              borderType: f.borderType || 'solid',
              borderColor: f.borderColor || '#000000',
              zIndex: f.zIndex || 0,
              isPlaced: f.isPlaced,
            }))
          );
          if (fp(frameImagesRef.current) === fp(remote)) return;

          setFrameImages(remote);
          frameImagesRef.current = remote;
        },
        (err) => {
          console.warn('[rt] frames snapshot error:', err);
        }
      );
    } catch (err) {
      console.warn('[rt] failed to subscribe to frames subcollection:', err);
    }

    return () => {
      if (unsub) unsub();
    };
  }, [authUser, currentProjectId]);

  const recoverAutoSave = async (projectId) => {
    if (!authUser) return;

    try {
      const autoSave = await projectService.getAutoSave(authUser.uid, projectId);
      if (autoSave?.canvasState) {
        if (autoSave.canvasState.layers) {
          setLayers(autoSave.canvasState.layers);
        }
        if (autoSave.canvasState.selectedLayerId) {
          setSelectedLayerId(autoSave.canvasState.selectedLayerId);
        }
        if (autoSave.canvasState.currentLassoPath) {
          setCurrentLassoPath(autoSave.canvasState.currentLassoPath);
        }
        if (autoSave.frameImages) {
          setFrameImages(autoSave.frameImages);
          frameImagesRef.current = autoSave.frameImages;
        }
      }
    } catch (error) {
      console.error('Auto-save recovery failed:', error);
    }
  };

  // ============================================
  // SAVE PROJECT TO FIRESTORE — Schema v4
  // ============================================
  const saveProjectToFirestore = useCallback(async () => {
    if (!authUser || !projectData) return false;
    if (!autoSaveEnabled) return false;

    if (saveInFlightRef.current) {
      while (saveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 50));
      }
    }
    saveInFlightRef.current = true;

    // Firestore rejects arrays that directly contain arrays with the
    // error "Property array contains an invalid nested entity." Every
    // nested array is rewritten as a numeric-keyed map plus a sentinel
    // field (`__outlineArrayShape: true`) so the read pipeline can
    // restore the original array shape losslessly via
    // restoreFirestoreArrays().
    const sanitize = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'function') return undefined;
      if (value instanceof Date) return value.toISOString();
      if (typeof value === 'number') {
        return isFinite(value) ? value : null;
      }
      if (typeof value === 'string' || typeof value === 'boolean') return value;
      if (Array.isArray(value)) {
        return value
          .map(item => {
            if (Array.isArray(item)) {
              const map = { __outlineArrayShape: true };
              item.forEach((entry, idx) => {
                const cleaned = sanitize(entry);
                if (cleaned !== undefined) {
                  map[String(idx)] = cleaned;
                }
              });
              return map;
            }
            return sanitize(item);
          })
          .filter(item => item !== undefined);
      }
      if (typeof value === 'object') {
        const cleaned = {};
        for (const [key, val] of Object.entries(value)) {
          const cleanedVal = sanitize(val);
          if (cleanedVal !== undefined) {
            cleaned[key] = cleanedVal;
          }
        }
        return cleaned;
      }
      return undefined;
    };

    try {
      const effectiveProjectId = projectData.projectId || currentProjectId;

      const workingBase = canvasImageRef.current || null;
      const pristineBase = originalImage || workingBase || projectData?.photo || null;

      // ── PROJECT-LEVEL PRISTINE + PRIMARY CANVAS ──────────────────────
      // The project document's photoURL / originalPhotoURL represent the
      // PROJECT as a whole — what the dashboard renders as the project
      // card and what the loader treats as the pristine source. They MUST
      // come from the PRIMARY room (rooms[0]) and never from whichever
      // room happens to be active in the editor at save time. Without
      // this, switching to room 2 and saving silently overwrites the
      // project's photoURL with room 2's canvas, "losing" the original
      // project image on the dashboard.
      const primaryRoomAtSave = (roomsRef.current && roomsRef.current[0]) || null;
      const projectPrimaryCanvas = primaryRoomAtSave?.canvasImage || workingBase || null;
      const projectPristineOriginal = primaryRoomAtSave?.originalImage || pristineBase || projectPrimaryCanvas || null;

      let migratedFrames = frameImagesRef.current || [];
      if (effectiveProjectId && authUser && migratedFrames.length > 0) {
        try {
          migratedFrames = await migrateFrameImagesToStorage(
            authUser.uid,
            effectiveProjectId,
            migratedFrames
          );
          setFrameImages(migratedFrames);
          frameImagesRef.current = migratedFrames;
        } catch (migErr) {
          console.error('Frame image migration failed; falling back to original frames:', migErr);
          migratedFrames = frameImagesRef.current || [];
        }
      }

      const sanitizedFrameImages = sanitize(migratedFrames);

      const canvasStateObject = {
        layers: sanitize(layers),
        selectedLayerId: sanitize(selectedLayerId),
        currentLassoPath: sanitize(currentLassoPath),
      };

      let canvasStateURL = null;
      if (effectiveProjectId && authUser) {
        try {
          canvasStateURL = await uploadCanvasState(authUser.uid, effectiveProjectId, canvasStateObject);
        } catch (uploadErr) {
          console.error('Failed to upload canvas state:', uploadErr);
        }
      }

      let storedWorkingURL = projectPrimaryCanvas;
      if (
        effectiveProjectId &&
        authUser &&
        projectPrimaryCanvas &&
        projectPrimaryCanvas.startsWith('data:')
      ) {
        try {
          storedWorkingURL = await uploadWorkingImage(authUser.uid, effectiveProjectId, projectPrimaryCanvas);
        } catch (uploadErr) {
          console.error('Failed to upload working base:', uploadErr);
          storedWorkingURL = null;
        }
      }

      let storedOriginalURL = projectPristineOriginal;
      if (
        effectiveProjectId &&
        authUser &&
        projectPristineOriginal &&
        projectPristineOriginal.startsWith('data:')
      ) {
        try {
          storedOriginalURL = await uploadOriginalImage(authUser.uid, effectiveProjectId, projectPristineOriginal);
        } catch (uploadErr) {
          console.error('Failed to upload pristine original:', uploadErr);
          storedOriginalURL = null;
        }
      }

      let storedThumbnailURL = null;
      const compositeForThumbnail = displayImageUrlRef.current;
      if (
        effectiveProjectId &&
        authUser &&
        compositeForThumbnail &&
        compositeForThumbnail.startsWith('data:')
      ) {
        try {
          storedThumbnailURL = await uploadThumbnailImage(authUser.uid, effectiveProjectId, compositeForThumbnail);
        } catch (uploadErr) {
          console.error('Failed to upload thumbnail:', uploadErr);
          storedThumbnailURL = null;
        }
      } else if (compositeForThumbnail && !compositeForThumbnail.startsWith('data:')) {
        storedThumbnailURL = compositeForThumbnail;
      }

      const counts = {
        layerCount: Array.isArray(layers) ? layers.length : 0,
        frameCount: Array.isArray(migratedFrames) ? migratedFrames.length : 0,
        noteCount: 0,
      };

      // ── Rooms: merge live state into the active room, then persist ─────
      // The active room's stored fields are snapshots; the live editor
      // state is the source of truth for whatever room is currently open.
      let roomsForSave = roomsRef.current;
      if (activeRoomIdRef.current) {
        roomsForSave = roomsForSave.map(r => {
          if (r.id !== activeRoomIdRef.current) return r;
          return {
            ...r,
            canvasImage: canvasImageRef.current || r.canvasImage,
            originalImage: originalImage || r.originalImage,
            layers: layers,
            layerCount: Array.isArray(layers) ? layers.length : 0,
            selectedLayerId: selectedLayerId,
            frameImages: frameImagesRef.current,
            naturalWidth: imageDimensions.width || r.naturalWidth,
            naturalHeight: imageDimensions.height || r.naturalHeight,
          };
        });
      }

      let sanitizedRooms = [];
      if (Array.isArray(roomsForSave) && roomsForSave.length > 0) {
        const storage = getStorage();
        const uploadRoomDataUrl = async (value, pathBuilder, label, roomId) => {
          if (typeof value !== 'string') return value;
          if (!value.startsWith('data:')) return value;
          if (value.length <= DATA_URL_INLINE_LIMIT) return value;
          if (!effectiveProjectId || !authUser) return null;
          try {
            const r = storageRef(storage, pathBuilder(authUser.uid, effectiveProjectId, roomId));
            const blob = dataURLToBlob(value);
            const mime = (value.match(/^data:([^;]+)/) || [])[1] || 'image/png';
            await uploadBytes(r, blob, { contentType: mime });
            return await getDownloadURL(r);
          } catch (err) {
            console.warn(`[rooms] ${label} upload failed for ${roomId}:`, err);
            return null;
          }
        };

        for (const room of roomsForSave) {
          if (!room || !room.id) continue;
          const out = {
            id: room.id,
            name: room.name || 'Room',
            naturalWidth: room.naturalWidth || 0,
            naturalHeight: room.naturalHeight || 0,
            createdAt: room.createdAt || Date.now(),
            canvasImage: room.canvasImage || null,
            originalImage: room.originalImage || room.canvasImage || null,
            thumbnail: room.thumbnail || room.canvasImage || null,
            layers: sanitize(Array.isArray(room.layers) ? room.layers : []),
            layerCount: typeof room.layerCount === 'number'
              ? room.layerCount
              : (Array.isArray(room.layers) ? room.layers.length : 0),
            selectedLayerId: room.selectedLayerId || null,
            frameImages: sanitize(Array.isArray(room.frameImages) ? room.frameImages : []),
          };
          out.canvasImage = await uploadRoomDataUrl(out.canvasImage, storagePaths.roomCanvas, 'canvas', room.id);
          out.thumbnail = await uploadRoomDataUrl(out.thumbnail, storagePaths.roomThumbnail, 'thumbnail', room.id);
          sanitizedRooms.push(out);
        }
      }

      // Upload the rooms payload to Storage as a JSON file. This avoids
      // Firestore's nested-array restrictions and the 1 MB doc limit.
      // The project doc only stores the resulting URL.
      let roomsStateURL = null;
      if (sanitizedRooms.length > 0 && effectiveProjectId && authUser) {
        try {
          roomsStateURL = await uploadRoomsState(authUser.uid, effectiveProjectId, sanitizedRooms);
        } catch (roomsUploadErr) {
          console.error('[rooms] Failed to upload rooms state to Storage:', roomsUploadErr);
          roomsStateURL = null;
        }
      }

      const serializedFrames = JSON.stringify(sanitizedFrameImages || []);
      const frameImagesSafeForDoc =
        serializedFrames.length < 600 * 1024 ? sanitizedFrameImages : null;

      if (effectiveProjectId) {
        const projectDocPayload = {
          name: projectData.name || 'Untitled Project',
          details: projectData.details || '',
          photoURL: storedWorkingURL || projectPrimaryCanvas,
          originalPhotoURL: storedOriginalURL || projectPristineOriginal,
          thumbnailURL: storedThumbnailURL || projectData.thumbnailURL || null,
          canvasStateURL: canvasStateURL,
          canvasState: null,
          schemaVersion: PROJECT_SCHEMA_VERSION,
          roomsStateURL: roomsStateURL,
          rooms: [],
          activeRoomId: activeRoomIdRef.current || null,
          ...counts,
        };
        if (frameImagesSafeForDoc) {
          projectDocPayload.frameImages = frameImagesSafeForDoc;
        } else {
          projectDocPayload.frameImages = [];
        }

        await projectService.updateProject(effectiveProjectId, projectDocPayload);

        try {
          await projectAssetService.writeFrames(effectiveProjectId, sanitizedFrameImages || []);
          // Tag the write so the real-time listener ignores its own echo.
          lastLocalFrameWriteAtRef.current = Date.now();
        } catch (frameWriteErr) {
          console.error('Failed to dual-write frames subcollection:', frameWriteErr);
          throw new Error('Frame subcollection write failed: ' + (frameWriteErr?.message || 'unknown'));
        }

        if (!currentProjectId || currentProjectId !== effectiveProjectId) {
          setCurrentProjectId(effectiveProjectId);
          sessionStorage.setItem('currentProjectId', effectiveProjectId);
        }
      } else {
        const createPayload = {
          name: projectData.name || 'Untitled Project',
          details: projectData.details || '',
          photoURL: storedWorkingURL || projectPrimaryCanvas,
          originalPhotoURL: storedOriginalURL || projectPristineOriginal,
          thumbnailURL: storedThumbnailURL || null,
          canvasStateURL: null,
          canvasState: null,
          roomsStateURL: roomsStateURL,
          rooms: [],
          activeRoomId: activeRoomIdRef.current || null,
        };
        if (frameImagesSafeForDoc) {
          createPayload.frameImages = frameImagesSafeForDoc;
        }
        const result = await projectService.createProject(authUser.uid, createPayload);
        const newProjectId = result.id;
        setCurrentProjectId(newProjectId);
        sessionStorage.setItem('currentProjectId', newProjectId);
        setProjectData(prev => prev ? { ...prev, projectId: newProjectId } : prev);

        if (authUser) {
          try {
            canvasStateURL = await uploadCanvasState(authUser.uid, newProjectId, canvasStateObject);
            await projectService.updateProject(newProjectId, {
              canvasStateURL: canvasStateURL,
              schemaVersion: PROJECT_SCHEMA_VERSION,
              ...counts,
            });
          } catch (uploadErr) {
            console.error('Failed to upload canvas state for new project:', uploadErr);
          }
          if (sanitizedFrameImages && sanitizedFrameImages.length > 0) {
            try {
              await projectAssetService.writeFrames(newProjectId, sanitizedFrameImages);
              // Tag the write so the real-time listener ignores its own echo.
              lastLocalFrameWriteAtRef.current = Date.now();
            } catch (frameWriteErr) {
              console.error('Failed to write frames for new project:', frameWriteErr);
              throw new Error('Frame subcollection write failed: ' + (frameWriteErr?.message || 'unknown'));
            }
          }

          // ── ROOMS: upload the pending rooms payload now that we have an
          //    ID to key it against. The rooms-upload branch above only
          //    fires when `effectiveProjectId` already exists, so a
          //    project's FIRST save must land the rooms JSON here —
          //    otherwise the project document is created with
          //    `roomsStateURL: null` and every room the user uploaded is
          //    lost on the next open.
          if (sanitizedRooms.length > 0 && !roomsStateURL) {
            try {
              roomsStateURL = await uploadRoomsState(authUser.uid, newProjectId, sanitizedRooms);
              await projectService.updateProject(newProjectId, {
                roomsStateURL: roomsStateURL,
                activeRoomId: activeRoomIdRef.current || null,
              });
            } catch (roomsUploadErr) {
              console.error('[rooms] Failed to upload rooms state for new project:', roomsUploadErr);
            }
          }
        }
      }
      setLastSaveTime(new Date());
      setLastSaveFailed(false);
      return true;
    } catch (error) {
      console.error('Project save failed:', error);
      setLastSaveFailed(true);
      return false;
    } finally {
      saveInFlightRef.current = false;
    }
  }, [authUser, projectData, currentProjectId, autoSaveEnabled, layers, selectedLayerId, currentLassoPath, originalImage, imageDimensions]);

  // ============================================
  // NAVIGATION BACK TO DASHBOARD
  // ============================================
  const handleDashboardClick = useCallback(async () => {
    setIsLoading(true);
    setLoadingProgress(0);
    let dotCount = 0;
    dotsIntervalRef.current = setInterval(() => {
      dotCount = (dotCount + 1) % 4;
      setLoadingDots('.'.repeat(dotCount));
    }, 500);

    if (showPictureFrameTool && pictureFrameToolRef.current) {
      try {
        await pictureFrameToolRef.current.saveActiveEdit();
      } catch (err) {
        console.error('Error saving active frame edit:', err);
      }
      setShowPictureFrameTool(false);
    }

    if (autoSaveInterval) {
      clearInterval(autoSaveInterval);
      setAutoSaveInterval(null);
    }

    let saveSucceeded = false;
    if (authUser && projectData) {
      try {
        saveSucceeded = await saveProjectToFirestore();
        if (saveSucceeded) setHasUnsavedChanges(false);
      } catch (err) {
        console.error('Final save failed:', err);
      }
    }

    const progressPromise = new Promise((resolve) => {
      let progress = 0;
      loadingIntervalRef.current = setInterval(() => {
        progress += Math.random() * 30;
        if (progress >= 100) {
          progress = 100;
          setLoadingProgress(progress);
          clearInterval(loadingIntervalRef.current);
          resolve();
        } else {
          setLoadingProgress(Math.min(progress, 100));
        }
      }, 200);
    });

    const timeoutPromise = new Promise(resolve => setTimeout(resolve, 5000));

    try {
      await Promise.race([progressPromise, timeoutPromise]);
    } catch (error) {
      console.error('Error during progress animation:', error);
    } finally {
      clearInterval(dotsIntervalRef.current);
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
    }

    setIsLoading(false);
    navigate('/dashboard');
  }, [authUser, projectData, saveProjectToFirestore, navigate, showPictureFrameTool, autoSaveInterval]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [isBottomMenuOpen, setIsBottomMenuOpen] = useState(false);
  const [selectedButton, setSelectedButton] = useState(null);
  const [selectedBottomButton, setSelectedBottomButton] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const [gyroEnabled, setGyroEnabled] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const stored = localStorage.getItem('editorVoiceEnabled');
    if (stored !== null) {
      return stored === 'true';
    }
    return window.innerWidth <= 767;
  });
  const [tutorialShown, setTutorialShown] = useState(() => {
    return localStorage.getItem('editorTutorialShown') === 'true';
  });

  const toggleTutorialSetting = useCallback(() => {
    const newValue = !tutorialShown;
    setTutorialShown(newValue);
    if (newValue) {
      localStorage.setItem('editorTutorialShown', 'true');
    } else {
      localStorage.removeItem('editorTutorialShown');
    }
  }, [tutorialShown]);

  const sacRef = useRef(null);

  const [isListening, setIsListening] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const [showDynamicCanvas, setShowDynamicCanvas] = useState(false);
  const [isDynamicCanvasActive, setIsDynamicCanvasActive] = useState(false);

  const [isGenerateMenuOpen, setIsGenerateMenuOpen] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationDots, setGenerationDots] = useState('');
  const [generatedVariations, setGeneratedVariations] = useState([]);
  const [showVariationsMenu, setShowVariationsMenu] = useState(false);
  const [activeLayerForVariations, setActiveLayerForVariations] = useState(null);
  const [promptHistory, setPromptHistory] = useState([]);
  const [isPromptPanelExpanded, setIsPromptPanelExpanded] = useState(false);
  const [promptPanelHeight, setPromptPanelHeight] = useState(24);
  const [isDraggingPromptPanel, setIsDraggingPromptPanel] = useState(false);
  const promptPanelDragStartY = useRef(0);
  const promptPanelStartHeight = useRef(50);
  const inputRef = useRef(null);
  const desktopInputRef = useRef(null);
  const desktopMeasureRef = useRef(null);
  const mobileMeasureRef = useRef(null);
  const desktopCursorRef = useRef(null);
  const mobileCursorRef = useRef(null);

  // Watch for changes that need saving
  useEffect(() => {
    const currentSaveData = JSON.stringify({
      layers: layers.map(l => ({
        id: l.id,
        name: l.name,
        hasGenerated: !!l.generatedImage,
        variationsCount: l.variations?.length || 0,
        visible: l.visible !== false,
        locked: l.locked || false
      })),
      selectedLayerId,
      hasLasso: !!currentLassoPath,
      frameImages: frameImages.map(f => ({
        id: f.id,
        imageData: f.imageData ? f.imageData.substring(0, 100) : '',
        originalImage: f.originalImage ? f.originalImage.substring(0, 100) : '',
        corners: f.corners,
        zIndex: f.zIndex,
      })),
    });

    if (lastSaveDataRef.current !== null && currentSaveData !== lastSaveDataRef.current) {
      setHasUnsavedChanges(true);
    }
    lastSaveDataRef.current = currentSaveData;
  }, [layers, selectedLayerId, currentLassoPath, frameImages]);

  // Set up auto-save interval.
  //
  // PERSISTENCE FIX: the previous version listed `hasUnsavedChanges` and
  // `saveProjectToFirestore` as effect dependencies, which meant EVERY
  // toggle of `hasUnsavedChanges` (which happens on every keystroke of a
  // rename, every layer add, every room add, every room switch snapshot…)
  // tore down the running setInterval and started a brand-new one. Because
  // the interval was 30 s and the user interacted more often than that,
  // the countdown reset every few seconds and the save NEVER FIRED. The
  // live value of `hasUnsavedChanges` is now read through a ref inside the
  // tick, so the interval survives unrelated re-renders and actually fires
  // after 30 s of quiet. The same ref trick applies to
  // `saveProjectToFirestore` so a fresh callback identity (which changes
  // whenever `layers` / `selectedLayerId` / etc. change) cannot reset the
  // timer either.
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const saveProjectToFirestoreRef = useRef(saveProjectToFirestore);
  useEffect(() => {
    saveProjectToFirestoreRef.current = saveProjectToFirestore;
  }, [saveProjectToFirestore]);

  useEffect(() => {
    if (!authUser || !projectData || !autoSaveEnabled) return;

    if (!currentProjectId) {
      const initialTimeout = setTimeout(async () => {
        const saveFn = saveProjectToFirestoreRef.current;
        const ok = saveFn ? await saveFn() : false;
        if (ok) setHasUnsavedChanges(false);
      }, 3000);
      return () => clearTimeout(initialTimeout);
    }

    const interval = setInterval(async () => {
      if (hasUnsavedChangesRef.current) {
        const saveFn = saveProjectToFirestoreRef.current;
        const ok = saveFn ? await saveFn() : false;
        if (ok) {
          setHasUnsavedChanges(false);
        } else {
          console.error('[auto-save] Save failed — retrying next tick');
          showToast('Auto-save failed. Retrying…', 'warning', 4000);
        }
      }
    }, 30000);
    setAutoSaveInterval(interval);

    const handleBeforeUnload = () => {
      if (hasUnsavedChangesRef.current && authUser && projectData) {
        const saveFn = saveProjectToFirestoreRef.current;
        if (saveFn) saveFn();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [authUser, projectData, currentProjectId, autoSaveEnabled]);

  // Save when user navigates away
  useEffect(() => {
    return () => {
      if (authUser && projectData && hasUnsavedChanges && autoSaveEnabled) {
        saveProjectToFirestore();
        setHasUnsavedChanges(false);
      }
    };
  }, []);

  // ── ROOM PERSISTENCE: immediate save after the room list changes ────
  // The 30 s auto-save interval is a safety net, but a user who uploads a
  // room and immediately exits should not have to wait on it — and if
  // the exit-path save fails silently, the room would be gone. Trigger a
  // debounced save as soon as the room COUNT changes (add / delete) so
  // the payload lands in Firestore + Storage within a couple of seconds.
  // Renames and switches ride the auto-save interval and the exit save.
  const prevRoomsLengthRef = useRef(rooms.length);
  const roomSaveDebounceRef = useRef(null);
  useEffect(() => {
    if (!authUser || !projectData || !autoSaveEnabled) return;
    if (!baseCanonicalRef.current) return;
    if (isProjectLoading) return;
    if (rooms.length === 0) {
      prevRoomsLengthRef.current = 0;
      return;
    }
    if (rooms.length === prevRoomsLengthRef.current) return;
    prevRoomsLengthRef.current = rooms.length;

    if (roomSaveDebounceRef.current) clearTimeout(roomSaveDebounceRef.current);
    roomSaveDebounceRef.current = setTimeout(async () => {
      const saveFn = saveProjectToFirestoreRef.current;
      if (!saveFn) return;
      const ok = await saveFn();
      if (ok) setHasUnsavedChanges(false);
    }, 1500);

    return () => {
      if (roomSaveDebounceRef.current) clearTimeout(roomSaveDebounceRef.current);
    };
  }, [rooms.length, authUser, projectData, autoSaveEnabled, isProjectLoading]);

  // Debug console state
  const [debugLogs, setDebugLogs] = useState([]);
  const [isDebugExpanded, setIsDebugExpanded] = useState(false);

  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const placeholders = [
    "Type to generate item, style or decor description",
    "Try: modern sofa, vintage lamp, minimalist desk,",
    "Try: wainscotting wall panels, limewash texture."
  ];

  const buttonContainerRef = useRef(null);
  const borderRectRef = useRef(null);
  const starCanvasRef = useRef(null);
  const trailCanvasRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const insideStarsAnimationRef = useRef(null);
  const bouncingStarsRef = useRef([]);
  const animationStartTimeRef = useRef(null);
  const previousMenuStateRef = useRef({
    sideMenuOpen: false,
    bottomMenuOpen: false,
    layersMenuOpen: false,
    wallColorOpen: false,
    pictureFrameOpen: false,
    compassOpen: false,
    quickStyleOpen: false,
  });
  const closeWithoutRestoreRef = useRef(false);
  const gapSize = 50;
  let perimeter = 0;
  let totalDuration = 1800;

  const [borderSpeedMultiplier] = useState(1.0);
  const [asteroidSpeedMultiplier] = useState(1.0);
  const [starOffset] = useState(-62);

  const [isShaking, setIsShaking] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const MARCHING_ANTS_SPEED = 1.5;
  const MARCHING_ANTS_DASH_PATTERN = [10, 8];
  const LASSO_LINE_WIDTH = 2.5;

  const sliderContainerRef = useRef(null);
  const sliderButtonRef = useRef(null);
  const canvasRef = useRef(null);
  const lassoCanvasRef = useRef(null);
  const lassoCanvasDesktopRef = useRef(null);
  const desktopCanvasInnerRef = useRef(null);
  const desktopCanvasAreaRef = useRef(null);
  const fixedHeaderRef = useRef(null);
  const dragStartYRef = useRef(0);
  const dragStartPositionRef = useRef(0);
  const loadingIntervalRef = useRef(null);
  const dotsIntervalRef = useRef(null);
  const [marchingAntsOffset, setMarchingAntsOffset] = useState(0);
  const marchingAntsAnimRef = useRef(null);
  const pointerAnimRef = useRef(null);
  const resetSliderTimeoutRef = useRef(null);

  const [canvasImage, setCanvasImage] = useState(null);
  const [displayImageUrl, setDisplayImageUrl] = useState(null);
  const [isCompositePending, setIsCompositePending] = useState(false);

  // Keep canvasImageRef in sync for saveProjectToFirestore
  useEffect(() => {
    canvasImageRef.current = canvasImage;
  }, [canvasImage]);

  // Keep displayImageUrlRef in sync so saveProjectToFirestore can persist
  // the current composite (base + frames) as the dashboard thumbnail.
  useEffect(() => {
    displayImageUrlRef.current = displayImageUrl;
  }, [displayImageUrl]);

  // Whenever the base changes (new project, style layer applied, lasso
  // undo, etc.), reset display to base. The composite effect below will
  // re-apply placed frames on top.
  useEffect(() => {
    if (canvasImage) {
      console.log('[rooms:reset] base changed → displayImageUrl := raw base', {
        activeRoomId: activeRoomIdRef.current,
        isDataUrl: String(canvasImage).startsWith('data:'),
        isStoragePath: String(canvasImage).startsWith('storage-path:'),
      });
      setDisplayImageUrl(canvasImage);
    }
  }, [canvasImage]);

  // Keep frameImagesRef in sync for saveProjectToFirestore
  useEffect(() => {
    frameImagesRef.current = frameImages;
  }, [frameImages]);

  // Keep room refs in sync for saveProjectToFirestore
  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);

  useEffect(() => {
    activeRoomIdRef.current = activeRoomId;
  }, [activeRoomId]);

  // ── Room canvas preload ─────────────────────────────────────────────
  // Decode every room's canvas into the browser's image cache in the
  // background as soon as the room list is known. Room switches then
  // become instant — the browser already holds the decoded bitmap and
  // does not have to parse + decode a fresh 2048px JPEG on the swap.
  //
  // Keyed on the joined canvas URLs so it runs once per unique room
  // set, NOT on every rooms-array identity change (which happens on
  // every outgoing-room snapshot).
  const roomCanvasKey = (rooms || [])
    .map(r => r?.canvasImage || '')
    .join('|');

  useEffect(() => {
    if (!roomCanvasKey) return;
    for (const r of roomsRef.current || []) {
      const src = r?.canvasImage;
      if (!src || typeof src !== 'string') continue;
      // No crossOrigin here on purpose. The display <img> that consumes
      // these URLs also has no crossOrigin, and the browser keys its image
      // cache by (URL, request mode). A CORS-mode preload would warm a
      // different cache entry than the display img reads from — and
      // Firebase Storage without CORS configured would fail the preload
      // outright. A plain load warms the exact entry the display img hits.
      const img = new Image();
      img.src = src;
      // decode() resolves when the image is fully decoded and paintable,
      // which is a stronger guarantee than onload for an instant swap.
      if (img.decode) {
        img.decode().catch(() => { /* fall back to onload path */ });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCanvasKey]);

  const [isImageLoading, setIsImageLoading] = useState(true);
  const [displayImage, setDisplayImage] = useState(null);
  const [isDesktopImageLoaded, setIsDesktopImageLoaded] = useState(false);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
  const [headerBottom, setHeaderBottom] = useState(0);

  useEffect(() => {
    if (canvasImage) {
      setIsImageLoading(true);
    }
  }, [canvasImage]);

  // Wake Lock and Keep-Alive refs
  const keepAliveIntervalRef = useRef(null);
  const wakeLockRef = useRef(null);

  const debugLog = (message, data = null, type = 'info') => {
    if (import.meta.env.PROD) return;
    const timestamp = new Date().toLocaleTimeString();
    const safeData = data ? sanitizeForLog(data) : '';
    console.log(`[${timestamp}] ${message}`, safeData);
    setDebugLogs(prev => [...prev, { timestamp, message, data: safeData, type }].slice(-50));
  };

  const sanitizeForLog = (input) => {
    if (typeof input === 'string') {
      return input
        .replace(/data:[^;]+;base64,[^"'\s]+/g, '[DATA_URL_REDACTED]')
        .replace(/([?&]token=)[^&\s]+/g, '$1REDACTED')
        .replace(/https:\/\/[^\s]+/g, '[URL_REDACTED]');
    }
    if (Array.isArray(input)) {
      return input.map(item => sanitizeForLog(item));
    }
    if (input && typeof input === 'object') {
      const safe = {};
      for (const [key, value] of Object.entries(input)) {
        if (/token|uid|id|email|url|image|data/i.test(key)) {
          safe[key] = '[REDACTED]';
        } else {
          safe[key] = sanitizeForLog(value);
        }
      }
      return safe;
    }
    return input;
  };

  const clearDebugLogs = () => setDebugLogs([]);

  const copyDebugLogs = () => {
    const logText = debugLogs.map(l =>
      `[${l.timestamp}] ${l.message} ${l.data ? JSON.stringify(l.data) : ''}`
    ).join('\n');
    navigator.clipboard?.writeText(logText);
    debugLog('Logs copied to clipboard', null, 'success');
  };

  const requestWakeLock = async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        debugLog('Wake Lock active', null, 'success');
      } catch (err) {
        debugLog('Wake Lock failed', err.message, 'error');
      }
    }
  };

  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().then(() => {
        wakeLockRef.current = null;
        debugLog('Wake Lock released', null, 'info');
      });
    }
  };

  const isPWA = useMemo(() => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true;
  }, []);

  useEffect(() => {
    debugLog('Editor initialized', {
      isPWA,
      userAgent: navigator.userAgent,
      hasAuth: !!authUser,
      hasProject: !!projectData,
      projectId: currentProjectId,
      pendingAsset: !!pendingAsset
    });
  }, []);

  useEffect(() => {
    const updateCanvasDimensions = () => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setCanvasDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateCanvasDimensions();
    window.addEventListener('resize', updateCanvasDimensions);

    return () => window.removeEventListener('resize', updateCanvasDimensions);
  }, []);

  useEffect(() => {
    const updateHeaderBottom = () => {
      if (fixedHeaderRef.current) {
        const rect = fixedHeaderRef.current.getBoundingClientRect();
        setHeaderBottom(rect.bottom);
      }
    };
    updateHeaderBottom();
    window.addEventListener('resize', updateHeaderBottom);
    return () => window.removeEventListener('resize', updateHeaderBottom);
  }, []);

  // Shake detection
  useEffect(() => {
    let lastX = 0, lastY = 0, lastZ = 0;

    const handleMotion = (e) => {
      const acc = e.accelerationIncludingGravity;
      if (!acc) return;

      const deltaX = Math.abs(acc.x - lastX);
      const deltaY = Math.abs(acc.y - lastY);
      const deltaZ = Math.abs(acc.z - lastZ);

      if ((deltaX > 15 || deltaY > 15 || deltaZ > 15) && isGenerateMenuOpen && !isGenerating) {
        handleGeneratePress();
      }

      lastX = acc.x;
      lastY = acc.y;
      lastZ = acc.z;
    };

    if (typeof DeviceMotionEvent !== 'undefined') {
      window.addEventListener('devicemotion', handleMotion);
    }

    return () => window.removeEventListener('devicemotion', handleMotion);
  }, [isGenerateMenuOpen, isGenerating]);

  const requestGyroPermission = async () => {
    if (typeof DeviceOrientationEvent === 'undefined') return false;
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission === 'granted') {
          setGyroEnabled(true);
          return true;
        }
      } catch (err) {}
      return false;
    } else {
      setGyroEnabled(true);
      return true;
    }
  };

  const toggleDynamicCanvas = async () => {
    if (isDynamicCanvasActive) {
      setIsDynamicCanvasActive(false);
      setGyroEnabled(false);
    } else {
      setIsDynamicCanvasActive(true);
      if (!gyroEnabled) {
        const granted = await requestGyroPermission();
        if (granted) {
          setGyroEnabled(true);
        }
      }
    }
  };

  const toggleUITheme = () => {
    const newTheme = uiTheme === 'light' ? 'dark' : 'light';
    setUiTheme(newTheme);
    localStorage.setItem('editorUITheme', newTheme);
  };

  const toggleAutoSave = () => {
    setAutoSaveEnabled(!autoSaveEnabled);
    if (!autoSaveEnabled && authUser && projectData) {
      saveProjectToFirestore();
    }
  };

  const toggleVoice = () => {
    setVoiceEnabled(prev => {
      const newVal = !prev;
      localStorage.setItem('editorVoiceEnabled', String(newVal));
      return newVal;
    });
  };

  useEffect(() => {
    const stored = localStorage.getItem('editorTutorialShown');
    if (stored === null || stored === 'false') {
      const timer = setTimeout(() => {
        showTutorialModal(voiceEnabled, toggleVoice);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const openTutorial = useCallback(() => {
    setShowSettings(false);
    showTutorialModal(voiceEnabled, toggleVoice);
  }, [showTutorialModal, voiceEnabled, toggleVoice]);

  const handleStyleGenerated = useCallback((styleData) => {
    const newLayer = {
      id: `style_${Date.now()}`,
      name: `${styleData.name}`,
      type: 'style',
      generatedImage: styleData.generatedImage,
      variations: styleData.variations,
      originalImage: canvasImage,
      visible: true,
      locked: false,
      lassoPath: null,
      prompt: styleData.name,
    };
    setLayers(prev => [...prev, newLayer]);
    setSelectedLayerId(newLayer.id);
    setCanvasImage(styleData.generatedImage);
    setDisplayImage(styleData.generatedImage);
    debugLog('Style added as layer', { name: styleData.name });
    showToast(`Style "${styleData.name}" applied`, 'success', 3000);
  }, [canvasImage]);

  // ============================================
  // PICTURE FRAME HANDLER — Non-destructive
  // ============================================
  const handleFrameApplied = useCallback((frameData) => {
    if (!frameData || !frameData.id || !frameData.originalImage) return;

    const frameRecord = {
      id: frameData.id,
      imageData: frameData.originalImage,
      originalImage: frameData.originalImage,
      name: frameData.name || 'Frame',
      naturalWidth: frameData.naturalWidth || 600,
      naturalHeight: frameData.naturalHeight || 400,
      corners: frameData.corners || null,
      isPlaced: true,
      scale: frameData.scale || 1,
      backgroundImage: frameData.canvasBackground || frameData.backgroundImage || canvasImage,
      zIndex: frameData.zIndex || 0,
      rotation: frameData.rotation || { x: 0, y: 0 },
      borderWidth: frameData.borderWidth || 0,
      borderType: frameData.borderType || 'solid',
      borderColor: frameData.borderColor || '#000000',
      gradientStartColor: frameData.gradientStartColor || '#000000',
      gradientEndColor: frameData.gradientEndColor || '#ffffff',
      originalUncroppedData: frameData.originalUncroppedData,
      originalSourceNaturalWidth: frameData.originalSourceNaturalWidth,
      originalSourceNaturalHeight: frameData.originalSourceNaturalHeight,
      originalCorners: frameData.originalCorners,
    };

    setFrameImages(prev => {
      const existingIndex = prev.findIndex(f => f.id === frameData.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...frameRecord };
        return updated;
      }
      return [...prev, frameRecord];
    });
    frameImagesRef.current = frameImagesRef.current.some(f => f.id === frameData.id)
      ? frameImagesRef.current.map(f => f.id === frameData.id ? { ...f, ...frameRecord } : f)
      : [...frameImagesRef.current, frameRecord];

    const existingLayer = layers.find(l => l.type === 'frame' && l.frameData?.id === frameData.id);

    if (existingLayer) {
      setLayers(prev => prev.map(l => {
        if (l.id !== existingLayer.id) return l;
        return {
          ...l,
          frameData: { ...l.frameData, ...frameRecord },
        };
      }));
    } else {
      const layerId = `frame_${Date.now()}`;
      const newLayer = {
        id: layerId,
        name: frameData.name || 'Picture Frame',
        type: 'frame',
        originalImage: frameData.originalImage,
        visible: true,
        locked: false,
        lassoPath: null,
        prompt: 'Picture Frame',
        frameData: { ...frameRecord },
      };
      setLayers(prev => [...prev, newLayer]);
      setSelectedLayerId(newLayer.id);
      debugLog('Picture frame added as layer', { name: newLayer.name });
    }
    setHasUnsavedChanges(true);
  }, [canvasImage, layers, debugLog]);

  const deleteLayer = (layerId) => {
    const layer = layers.find(l => l.id === layerId);
    if (layer && layer.locked) {
      debugLog('Cannot delete locked layer', { layerId });
      showToast('This layer is locked. Unlock it to delete.', 'warning', 3000);
      return;
    }
    if (layer && layer.type === 'frame' && layer.frameData?.id) {
      const frameId = layer.frameData.id;
      const remainingFrames = frameImagesRef.current.filter(f => f.id !== frameId);
      setFrameImages(remainingFrames);
      frameImagesRef.current = remainingFrames;
      const pid = currentProjectId || projectData?.projectId;
      if (pid) {
        projectAssetService.deleteEntity(pid, 'frames', frameId).catch(err => {
          console.warn('Failed to delete frame subcollection doc:', err);
        });
      }
    } else if (layer && layer.originalImage) {
      setCanvasImage(layer.originalImage);
      setDisplayImage(layer.originalImage);
      canvasImageRef.current = layer.originalImage;
    }

    setLayers(prev => prev.filter(l => l.id !== layerId));

    if (selectedLayerId === layerId) {
      setSelectedLayerId(null);
      setCurrentLassoPath(null);
    }
    debugLog('Layer deleted', { layerId });
    showToast(`Layer "${layer.name}" deleted`, 'success', 3000);
  };

  const handleFrameRemoved = useCallback((frameId) => {
    const remainingFrames = frameImagesRef.current.filter(f => f.id !== frameId);
    setFrameImages(remainingFrames);
    frameImagesRef.current = remainingFrames;

    const layerToRemove = layers.find(l => l.type === 'frame' && l.frameData?.id === frameId);
    if (layerToRemove) {
      setLayers(prev => prev.filter(l => l.id !== layerToRemove.id));
      if (selectedLayerId === layerToRemove.id) {
        setSelectedLayerId(null);
        setCurrentLassoPath(null);
      }
    }

    const pid = currentProjectId || projectData?.projectId;
    if (pid) {
      projectAssetService.deleteEntity(pid, 'frames', frameId).catch(err => {
        console.warn('Failed to delete frame subcollection doc:', err);
      });
    }
  }, [layers, selectedLayerId, currentProjectId, projectData]);

  // ============================================
  // ROOM HANDLERS
  // ----------------------------------------------------------------------------
  // A room owns its own background canvas + layer stack + frame set.
  // Switching rooms snapshots the live editor state into the OUTGOING
  // room, then loads the INCOMING room's persisted state back into the
  // live editor. This is the "replace the original background canvas"
  // behavior described in the Rooms design.
  // ============================================
  const handleSelectRoom = useCallback((roomId, room) => {
    if (!room || !roomId) return;
    if (roomId === activeRoomIdRef.current) {
      console.log('[rooms:select] no-op (already active)', { roomId });
      return;
    }

    console.log('[rooms:select] START', {
      from: activeRoomIdRef.current,
      to: roomId,
      toName: room.name,
      incomingIsData: room.canvasImage ? String(room.canvasImage).startsWith('data:') : null,
      incomingIsStoragePath: room.canvasImage ? String(room.canvasImage).startsWith('storage-path:') : null,
      incomingFrameCount: Array.isArray(room.frameImages) ? room.frameImages.length : 0,
      outgoingIsData: canvasImageRef.current ? String(canvasImageRef.current).startsWith('data:') : null,
      outgoingFrameCount: (frameImagesRef.current || []).length,
    });

    // ── 0. Capture the OUTGOING room's live state into locals FIRST ────
    // CRITICAL ORDERING: setRooms uses a functional updater that React
    // runs during the NEXT render pass — by which point canvasImageRef
    // has already been reassigned to the INCOMING room's canvas (see the
    // synchronous `canvasImageRef.current = incomingCanvas` below).
    // Reading the ref inside the updater would therefore write the
    // INCOMING room's canvas into the OUTGOING room's record, corrupting
    // both rooms and making subsequent switches a no-op. Freeze the
    // outgoing values into locals here so the updater always consumes
    // the correct (outgoing) state.
    const outgoingId = activeRoomIdRef.current;
    const outgoingCanvas = canvasImageRef.current;
    const outgoingOriginal = originalImage;
    const outgoingLayers = layers;
    const outgoingSelectedLayerId = selectedLayerId;
    const outgoingFrames = frameImagesRef.current;
    const outgoingWidth = imageDimensions.width;
    const outgoingHeight = imageDimensions.height;

    // ── 1. Snapshot live state into the OUTGOING room ──────────────────
    if (outgoingId) {
      setRooms(prev => prev.map(r => {
        if (r.id !== outgoingId) return r;
        return {
          ...r,
          canvasImage: outgoingCanvas || r.canvasImage,
          originalImage: outgoingOriginal || r.originalImage,
          layers: outgoingLayers,
          layerCount: Array.isArray(outgoingLayers) ? outgoingLayers.length : 0,
          selectedLayerId: outgoingSelectedLayerId,
          frameImages: outgoingFrames,
          naturalWidth: outgoingWidth || r.naturalWidth,
          naturalHeight: outgoingHeight || r.naturalHeight,
        };
      }));
    }

    // ── 2. Swap live state to the INCOMING room ────────────────────────
    const incomingCanvas = room.canvasImage;
    setActiveRoomId(roomId);
    activeRoomIdRef.current = roomId;

    if (incomingCanvas) {
      setCanvasImage(incomingCanvas);
      canvasImageRef.current = incomingCanvas;
      setDisplayImageUrl(incomingCanvas);
      displayImageUrlRef.current = incomingCanvas;
      console.log('[rooms:select] live canvas swapped', {
        roomId,
        newCanvasIsData: String(incomingCanvas).startsWith('data:'),
        newCanvasIsStoragePath: String(incomingCanvas).startsWith('storage-path:'),
        newFrameCount: Array.isArray(room.frameImages) ? room.frameImages.length : 0,
      });
      // Reuse the room's stored natural dimensions instead of spinning
      // up a throwaway Image() just to measure it. The room record
      // carries these from both the upload path and the load path, so
      // there is no reason to re-decode. The dimension-aware paths
      // (lasso math, pointers) read imageDimensions on the next frame
      // and will see the correct values because the image itself is
      // already decoded in the browser cache by the preload effect.
      if (room.naturalWidth && room.naturalHeight) {
        setImageDimensions({ width: room.naturalWidth, height: room.naturalHeight });
      } else {
        const img = new Image();
        img.onload = () => setImageDimensions({ width: img.width, height: img.height });
        img.src = incomingCanvas;
      }
    }
    setOriginalImage(room.originalImage || incomingCanvas || null);
    setLayers(Array.isArray(room.layers) ? room.layers : []);
    setSelectedLayerId(room.selectedLayerId || null);
    setCurrentLassoPath(null);
    const incomingFrames = Array.isArray(room.frameImages) ? room.frameImages : [];
    setFrameImages(incomingFrames);
    frameImagesRef.current = incomingFrames;
    setHasUnsavedChanges(true);
    // The room at index 0 is the Primary Canvas — that is the name the
    // Layers panel shows for it, regardless of its stored `name` field.
    // Match that label here so the toast is consistent with the UI.
    const isPrimaryRoom = roomsRef.current[0]?.id === roomId;
    const displayLabel = isPrimaryRoom ? 'Primary Canvas' : `"${room.name}"`;
    showToast(`Switched to ${displayLabel}`, 'success', 2000);
  }, [originalImage, layers, selectedLayerId, imageDimensions, showToast]);

  const handleAddRoom = useCallback((room) => {
    if (!room || !room.id) return;
    const normalized = {
      ...room,
      originalImage: room.originalImage || room.canvasImage,
      layers: Array.isArray(room.layers) ? room.layers : [],
      layerCount: Array.isArray(room.layers) ? room.layers.length : 0,
      selectedLayerId: room.selectedLayerId || null,
      frameImages: Array.isArray(room.frameImages) ? room.frameImages : [],
    };

    // ── 0. Capture the OUTGOING room's live state into locals FIRST ────
    // Same hazard as handleSelectRoom: setRooms uses a functional updater
    // that React runs during the NEXT render pass — after canvasImageRef
    // has been reassigned to the new room's canvas below. Reading the ref
    // inside the updater would stamp the OUTGOING room with the INCOMING
    // room's canvas, corrupting both. Freeze the outgoing values here.
    const outgoingId = activeRoomIdRef.current;
    const outgoingCanvas = canvasImageRef.current;
    const outgoingOriginal = originalImage;
    const outgoingLayers = layers;
    const outgoingSelectedLayerId = selectedLayerId;
    const outgoingFrames = frameImagesRef.current;
    const outgoingWidth = imageDimensions.width;
    const outgoingHeight = imageDimensions.height;

    // ── Snapshot the OUTGOING room, append the new room, and swap the
    //    live editor state to the new room in ONE atomic pass. Doing
    //    this inline (instead of delegating to handleSelectRoom) avoids
    //    the early-return guard in handleSelectRoom and guarantees the
    //    new angle becomes the live canvas the instant it is added. ──
    setRooms(prev => {
      const updated = outgoingId
        ? prev.map(r => r.id === outgoingId ? {
            ...r,
            canvasImage: outgoingCanvas || r.canvasImage,
            originalImage: outgoingOriginal || r.originalImage,
            layers: outgoingLayers,
            layerCount: Array.isArray(outgoingLayers) ? outgoingLayers.length : 0,
            selectedLayerId: outgoingSelectedLayerId,
            frameImages: outgoingFrames,
            naturalWidth: outgoingWidth || r.naturalWidth,
            naturalHeight: outgoingHeight || r.naturalHeight,
          } : r)
        : prev;
      return [...updated, normalized];
    });

    setActiveRoomId(normalized.id);
    activeRoomIdRef.current = normalized.id;

    const incomingCanvas = normalized.canvasImage;
    if (incomingCanvas) {
      setCanvasImage(incomingCanvas);
      canvasImageRef.current = incomingCanvas;
      setDisplayImageUrl(incomingCanvas);
      displayImageUrlRef.current = incomingCanvas;
      const img = new Image();
      img.onload = () => setImageDimensions({ width: img.width, height: img.height });
      img.src = incomingCanvas;
    }
    setOriginalImage(normalized.originalImage || incomingCanvas || null);
    setLayers([]);
    setSelectedLayerId(null);
    setCurrentLassoPath(null);
    setFrameImages([]);
    frameImagesRef.current = [];

    setHasUnsavedChanges(true);
    showToast(`Room "${room.name}" added`, 'success', 3000);
  }, [originalImage, layers, selectedLayerId, imageDimensions, showToast]);

  const handleRenameRoom = useCallback((roomId, newName) => {
    if (!roomId || !newName) return;
    setRooms(prev => prev.map(r => r.id === roomId ? { ...r, name: newName } : r));
    setHasUnsavedChanges(true);
    showToast(`Room renamed to "${newName}"`, 'success', 3000);
  }, [showToast]);

  const handleDeleteRoom = useCallback((roomId) => {
    setRooms(prev => {
      if (prev.length <= 1) return prev;
      const filtered = prev.filter(r => r.id !== roomId);
      const wasActive = roomId === activeRoomIdRef.current;
      if (wasActive && filtered.length > 0) {
        const fallback = filtered[0];
        setActiveRoomId(fallback.id);
        activeRoomIdRef.current = fallback.id;
        if (fallback.canvasImage) {
          setCanvasImage(fallback.canvasImage);
          canvasImageRef.current = fallback.canvasImage;
          setDisplayImageUrl(fallback.canvasImage);
          displayImageUrlRef.current = fallback.canvasImage;
        }
        setOriginalImage(fallback.originalImage || fallback.canvasImage || null);
        setLayers(Array.isArray(fallback.layers) ? fallback.layers : []);
        setSelectedLayerId(fallback.selectedLayerId || null);
        setCurrentLassoPath(null);
        const fallbackFrames = Array.isArray(fallback.frameImages) ? fallback.frameImages : [];
        setFrameImages(fallbackFrames);
        frameImagesRef.current = fallbackFrames;
      }
      return filtered;
    });
    setHasUnsavedChanges(true);
    showToast('Room deleted', 'success', 3000);
  }, [showToast]);

  // ============================================
  // PROMOTE ROOM TO PRIMARY
  // ----------------------------------------------------------------------------
  // The Layers panel calls this when the user taps the number chip on a
  // non-primary room. "Primary" is defined purely by list position: the
  // room at index 0 is the primary canvas. Promoting moves the chosen
  // room to index 0 and swaps its canvas into the editor as the active
  // editing surface — same behavior as the design spec describes.
  // ============================================
  const handleSetPrimaryRoom = useCallback((roomId, room) => {
    if (!roomId || !room) return;
    setRooms(prev => {
      const idx = prev.findIndex(r => r.id === roomId);
      if (idx <= 0) return prev; // already primary, or not found
      const promoted = prev[idx];
      const rest = prev.filter((_, i) => i !== idx);
      return [promoted, ...rest];
    });
    // Swap the editing canvas to the newly promoted room. handleSelectRoom
    // safely no-ops if the room is already the active one.
    handleSelectRoom(roomId, room);
    setHasUnsavedChanges(true);
    showToast('Primary canvas updated', 'success', 3000);
  }, [handleSelectRoom, showToast]);

  const openRendersPanel = useCallback(() => {
    if (!canvasImage) {
      debugLog('Cannot open RendersPanel - no canvas image', null, 'warn');
      return;
    }
    setRenderPanelCanvasImage(displayImageUrl || canvasImage);
    setShowRendersPanel(true);
  }, [canvasImage, displayImageUrl]);

  const createNewLayer = (lassoPath, generatedImage = null, prompt = '', variations = []) => {
    const newLayer = {
      id: `layer_${Date.now()}_${layerCounter}`,
      name: `Layer ${layerCounter}`,
      lassoPath: lassoPath,
      originalImage: canvasImage,
      generatedImage: generatedImage,
      prompt: prompt,
      variations: variations,
      variationHistory: variations,
      selectedVariationIndex: 0,
      visible: true,
      locked: false,
    };

    setLayers(prev => [...prev, newLayer]);
    setLayerCounter(prev => prev + 1);
    setSelectedLayerId(newLayer.id);

    debugLog('New layer created', { layerId: newLayer.id, name: newLayer.name });
    showToast(`Layer ${newLayer.name} created`, 'success', 3000);
    return newLayer;
  };

  const updateLayerImage = (layerId, newImage, newVariations = []) => {
    setLayers(prev => prev.map(layer => {
      if (layer.id !== layerId) return layer;
      if (layer.locked) return layer;

      const existingUrls = new Set(layer.variationHistory || []);
      const uniqueNewVariations = newVariations.filter(v => !existingUrls.has(v));
      const updatedHistory = [...(layer.variationHistory || []), ...uniqueNewVariations];

      return {
        ...layer,
        generatedImage: newImage,
        variations: newVariations,
        variationHistory: updatedHistory,
        selectedVariationIndex: 0,
      };
    }));
    debugLog('Layer updated', { layerId, variationsCount: newVariations.length });
  };

  const selectLayer = (layerId) => {
    const layer = layers.find(l => l.id === layerId);
    if (layer && layer.locked) {
      debugLog('Cannot select locked layer', { layerId });
      return;
    }
    if (layer) {
      setSelectedLayerId(layerId);
      setCurrentLassoPath(layer.lassoPath);

      if (layer.type === 'frame') {
        // Frame layers: keep the composite canvas as-is.
      } else if (layer.generatedImage) {
        setCanvasImage(layer.generatedImage);
        setDisplayImage(layer.generatedImage);
      } else if (layer.originalImage) {
        setCanvasImage(layer.originalImage);
        setDisplayImage(layer.originalImage);
      }

      setIsGenerateMenuOpen(true);
      setIsPromptPanelExpanded(false);
      setPromptPanelHeight(24);
      setGeneratePrompt(layer.prompt || '');
      setActiveLayerForVariations(layerId);

      setTimeout(() => {
        initButtonAnimation();
      }, 100);

      debugLog('Layer selected', { layerId, name: layer.name });
    }
  };

  const toggleLayerVisibility = (layerId) => {
    setLayers(prev => prev.map(layer => {
      if (layer.id === layerId) {
        const newVisibility = layer.visible === false;
        debugLog(`Layer ${layerId} visibility: ${newVisibility ? 'visible' : 'hidden'}`);
        return { ...layer, visible: newVisibility };
      }
      return layer;
    }));
  };

  const toggleLayerLock = (layerId) => {
    setLayers(prev => prev.map(layer => {
      if (layer.id === layerId) {
        const newLockState = !layer.locked;
        debugLog(`Layer ${layerId} lock: ${newLockState ? 'locked' : 'unlocked'}`);
        return { ...layer, locked: newLockState };
      }
      return layer;
    }));
  };

  const reorderLayers = (fromIndex, toIndex) => {
    setLayers(prev => {
      const newLayers = [...prev];
      const [movedLayer] = newLayers.splice(fromIndex, 1);
      newLayers.splice(toIndex, 0, movedLayer);
      debugLog(`Layer reordered: ${fromIndex} -> ${toIndex}`);
      return newLayers;
    });
  };

  const handleLayerClick = (e) => {
    if (!currentLassoPath || activeTool !== 'pointer') return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (isPointInPolygon(x, y, currentLassoPath.points)) {
      const layer = layers.find(l => l.id === selectedLayerId);
      if (layer && !layer.locked) {
        setGeneratePrompt(layer.prompt || '');
        setIsGenerateMenuOpen(true);
        setActiveLayerForVariations(layer.id);
        setTimeout(() => {
          initButtonAnimation();
        }, 100);
      }
    }
  };

  const isPointInPolygon = (x, y, points) => {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x, yi = points[i].y;
      const xj = points[j].x, yj = points[j].y;

      const intersect = ((yi > y) !== (yj > y))
        && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  useEffect(() => {
    if (currentLassoPath && currentLassoPath.points.length > 2) {
      setIsGenerateMenuOpen(true);
      setIsPromptPanelExpanded(false);
      setPromptPanelHeight(24);
      setIsSideMenuOpen(false);
      setIsBottomMenuOpen(false);
      setSelectedButton(null);
      setSelectedBottomButton(null);
      setTimeout(() => {
        initButtonAnimation();
      }, 100);
    }
  }, [currentLassoPath]);

  const handleCloseGenerateMenu = () => {
    if (sacRef.current?.isListening) {
      sacRef.current.stopListening();
    }
    setIsGenerateMenuOpen(false);
    if (!activeLayerForVariations) {
      setCurrentLassoPath(null);
    }
    setLassoPoints([]);
    setIsDrawingLasso(false);
    setGeneratePrompt('');
    setGeneratedVariations([]);
    setShowVariationsMenu(false);
    setActiveLayerForVariations(null);
  };

  const handleCloseVariationsMenu = () => {
    setShowVariationsMenu(false);
    initButtonAnimation();
    setTimeout(() => {
      initButtonAnimation();
    }, 50);
  };

  const generateInpainting = async (imageUrl, maskUrl, prompt, onProgress) => {
    try {
      const isDev = import.meta.env.DEV;
      const apiUrl = isDev
        ? '/api/replicate/v1/predictions'
        : '/.netlify/functions/replicate-proxy';

      const { enhancedPrompt, negativePrompt } = getEnhancePrompt(prompt);

      const requestBody = {
        version: INPAINTING_MODEL,
        input: {
          image: imageUrl,
          mask: maskUrl,
          prompt: enhancedPrompt,
          negative_prompt: negativePrompt,
          num_inference_steps: 50,
          guidance_scale: 7.8,
        }
      };

      debugLog('Starting inpainting request', { apiUrl, isPWA, prompt: prompt.substring(0, 30) });

      let response;

      if (isPWA) {
        response = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', apiUrl, true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.timeout = 30000;

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve({ data: JSON.parse(xhr.responseText) });
            } else {
              reject(new Error(`XHR failed: ${xhr.status}`));
            }
          };

          xhr.onerror = () => reject(new Error('XHR network error'));
          xhr.ontimeout = () => reject(new Error('XHR timeout'));

          xhr.send(JSON.stringify(requestBody));
        });
      } else {
        response = await axios.post(apiUrl, requestBody, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000
        });
      }

      const predictionId = response.data.id;
      debugLog('Prediction created', { predictionId });

      let isComplete = false;
      let result = null;
      let dotCount = 0;

      const dotsInterval = setInterval(() => {
        dotCount = (dotCount + 1) % 4;
        onProgress(-1, '.'.repeat(dotCount));
      }, 500);

      while (!isComplete) {
        const pollUrl = isDev
          ? `/api/replicate/v1/predictions/${predictionId}`
          : `/.netlify/functions/replicate-poll?id=${predictionId}`;

        let pollResponse;

        if (isPWA) {
          pollResponse = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', pollUrl, true);
            xhr.timeout = 15000;

            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve({ data: JSON.parse(xhr.responseText) });
              } else {
                reject(new Error(`XHR poll failed: ${xhr.status}`));
              }
            };

            xhr.onerror = () => reject(new Error('XHR poll network error'));
            xhr.ontimeout = () => reject(new Error('XHR poll timeout'));

            xhr.send();
          });
        } else {
          pollResponse = await axios.get(pollUrl, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 15000
          });
        }

        const status = pollResponse.data.status;

        if (status === 'succeeded') {
          isComplete = true;
          clearInterval(dotsInterval);
          result = pollResponse.data.output;
          onProgress(100, '');
          debugLog('Generation succeeded', { predictionId });
        } else if (status === 'failed') {
          clearInterval(dotsInterval);
          debugLog('Generation failed', pollResponse.data.error, 'error');
          throw new Error('Generation failed: ' + (pollResponse.data.error || 'Unknown error'));
        } else {
          if (status === 'processing') {
            onProgress(50, '.'.repeat(dotCount));
          } else if (status === 'starting') {
            onProgress(10, '.'.repeat(dotCount));
          }
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }

      const outputs = Array.isArray(result)
        ? result.map(item => typeof item === 'string' ? item : item.url ? item.url() : item)
        : [result];

      await Promise.all(outputs.map(async (url) => {
        if (!imageCache.has(url)) {
          try {
            const img = await preloadImage(url);
            imageCache.set(url, img);
          } catch (err) {
            console.warn('Failed to preload image:', url);
          }
        }
      }));

      debugLog('Inpainting complete', { variationsCount: outputs.length }, 'success');
      return outputs;
    } catch (error) {
      debugLog('Inpainting error', error.message, 'error');
      console.error('Inpainting error:', error.response?.data || error.message);
      throw error;
    }
  };

  const handleGeneratePress = async () => {
    if (!canvasImage || !currentLassoPath || !generatePrompt.trim() || isGenerating) return;

    const selectedLayer = layers.find(l => l.id === selectedLayerId);
    if (selectedLayer && selectedLayer.locked) {
      debugLog('Cannot generate on locked layer', { layerId: selectedLayerId });
      showToast('This layer is locked. Unlock it to make changes.', 'warning', 3000);
      return;
    }

    debugLog('Generate button pressed', { prompt: generatePrompt });

    const remaining = getRemainingCredits(userData);
    if (remaining.iterations < 2) {
      const planName = userData?.subscription?.planName || userData?.pendingPlan?.name || 'starter';
      showFullScreenModal({
        trigger: FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_ITERATIONS,
        plan: planName.toLowerCase(),
        remaining: { iterations: remaining.iterations, videos: remaining.videos },
        onUpgrade: () => navigate('/pricing'),
        onAddBalance: () => navigate('/pricing'),
      });
      return;
    }

    pushToUndoStack();
    await requestWakeLock();

    let dotCount = 0;
    keepAliveIntervalRef.current = setInterval(() => {
      const dot = document.createElement('div');
      dot.style.position = 'fixed';
      dot.style.bottom = '10px';
      dot.style.right = '10px';
      dot.style.width = '8px';
      dot.style.height = '8px';
      dot.style.borderRadius = '50%';
      dot.style.backgroundColor = dotCount % 2 === 0 ? '#4594f9' : 'transparent';
      dot.style.zIndex = '9999';
      dot.style.pointerEvents = 'none';
      dot.id = 'keep-alive-dot';

      const oldDot = document.getElementById('keep-alive-dot');
      if (oldDot) oldDot.remove();
      document.body.appendChild(dot);
      dotCount++;
    }, 1000);

    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);

    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationDots('');

    const result = await safeGenerate(
      authUser.uid,
      2,
      async (onProgress) => {
        const maskUrl = createMaskFromLasso(
          currentLassoPath.points,
          imageDimensions.width,
          imageDimensions.height
        );

        const variations = await generateInpainting(
          canvasImage,
          maskUrl,
          generatePrompt,
          (progress, dots) => {
            if (progress === -1) {
              onProgress(dots);
            } else {
              onProgress(progress);
            }
          }
        );

        return { variations };
      },
      {
        type: 'image',
        onStart: () => {},
        onProgress: (progress) => {
          if (typeof progress === 'string') {
            setGenerationDots(progress);
          } else {
            setGenerationProgress(progress);
          }
        },
        onComplete: (result) => {
          const { variations } = result;
          setGeneratedVariations(variations);

          if (activeLayerForVariations) {
            updateLayerImage(activeLayerForVariations, canvasImage, variations);
          } else {
            createNewLayer(currentLassoPath, canvasImage, generatePrompt, variations);
          }

          setShowVariationsMenu(true);

          setPromptHistory(prev => [...prev, {
            text: generatePrompt,
            timestamp: new Date().toLocaleTimeString(),
            id: Date.now()
          }]);

          setTimeout(() => {
            initButtonAnimation();
          }, 100);

          const refreshUserData = async () => {
            const userRef = doc(db, 'users', authUser.uid);
            const updatedUser = await getDoc(userRef);
            if (updatedUser.exists()) {
              const remaining = getRemainingCredits(updatedUser.data());
              setImagesRemaining(remaining.images);
            }
          };
          refreshUserData();
        },
        onError: (error) => {
          console.error('Generation failed:', error);
          debugLog('Generation failed', error.message, 'error');
          alert('Generation failed. Please try again.');
        }
      }
    );

    setIsGenerating(false);
    setGenerationProgress(0);
    setGenerationDots('');

    releaseWakeLock();

    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }

    const dot = document.getElementById('keep-alive-dot');
    if (dot) dot.remove();
  };

  const handleSelectVariation = async (variationUrl, index) => {
    const selectedLayer = layers.find(l => l.id === activeLayerForVariations);
    if (selectedLayer && selectedLayer.locked) {
      debugLog('Cannot select variation on locked layer');
      showToast('This layer is locked. Unlock it to make changes.', 'warning', 3000);
      return;
    }

    try {
      debugLog('Variation selected', { index });

      const maskUrl = createMaskFromLasso(
        currentLassoPath.points,
        imageDimensions.width,
        imageDimensions.height
      );

      pushToUndoStack();
      const blendedImage = await applyInpaintedRegion(canvasImage, variationUrl, maskUrl);

      setCanvasImage(blendedImage);
      setDisplayImage(blendedImage);
      canvasImageRef.current = blendedImage;

      if (activeLayerForVariations) {
        setLayers(prev => prev.map(layer =>
          layer.id === activeLayerForVariations
            ? { ...layer, generatedImage: blendedImage, selectedVariationIndex: index }
            : layer
        ));
      }

      debugLog('Variation applied', { index }, 'success');
      showToast('Variation applied', 'success', 3000);
    } catch (error) {
      debugLog('Failed to apply variation', error.message, 'error');
      console.error('Failed to apply variation:', error);
    }
  };

  const pushToUndoStack = useCallback(() => {
    setUndoStack(prev => {
      const newStack = [...prev, {
        canvasImage,
        layers: layers.map(l => ({...l})),
        selectedLayerId,
        currentLassoPath: currentLassoPath ? {...currentLassoPath, points: [...currentLassoPath.points]} : null
      }];
      return newStack.slice(-50);
    });
    setRedoStack([]);
  }, [canvasImage, layers, selectedLayerId, currentLassoPath]);

  const handleShowLayerVariations = (layerId) => {
    const layer = layers.find(l => l.id === layerId);
    if (layer && layer.variations && layer.variations.length > 0) {
      setActiveLayerForVariations(layerId);
      setCurrentLassoPath(layer.lassoPath);
      setGeneratedVariations(layer.variations);
      setShowVariationsMenu(true);
      setShowLayersMenu(false);
      debugLog('Showing layer variations', { layerId, count: layer.variations.length });
    }
  };

  const handleDeleteSelected = () => {
    if (selectedLayerId) {
      deleteLayer(selectedLayerId);
      setSelectedButton(null);
    }
  };

  useEffect(() => {
    if (isGenerateMenuOpen) {
      const interval = setInterval(() => {
        setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isGenerateMenuOpen]);

  useEffect(() => {
    if (currentLassoPath?.points.length > 2) {
      let startTime = null;
      const totalDashLength = MARCHING_ANTS_DASH_PATTERN.reduce((a, b) => a + b, 0);
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const offset = (elapsed * MARCHING_ANTS_SPEED * 0.05) % totalDashLength;
        setMarchingAntsOffset(offset);
        marchingAntsAnimRef.current = requestAnimationFrame(animate);
      };
      marchingAntsAnimRef.current = requestAnimationFrame(animate);
      return () => {
        if (marchingAntsAnimRef.current) cancelAnimationFrame(marchingAntsAnimRef.current);
      };
    } else {
      if (marchingAntsAnimRef.current) cancelAnimationFrame(marchingAntsAnimRef.current);
      setMarchingAntsOffset(0);
    }
  }, [currentLassoPath]);

  const getLassoBounds = (points) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    points.forEach(p => {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    });
    return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
  };

  const drawAstroid = (ctx, cx, cy, size, color, isStroke = false) => {
    ctx.save();
    ctx.beginPath();
    const steps = 80;
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * Math.PI * 2;
      const cosT = Math.cos(t);
      const sinT = Math.sin(t);
      const px = cx + size * Math.pow(cosT, 3);
      const py = cy + size * Math.pow(sinT, 3);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    if (isStroke) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.8;
      ctx.stroke();
    } else {
      ctx.fillStyle = color;
      ctx.fill();
    }
    ctx.restore();
  };

  class BouncingStar {
    constructor(canvas, x, y, size, vx, vy) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.x = x;
      this.y = y;
      this.size = size;
      this.vx = vx;
      this.vy = vy;
    }
    draw() { drawAstroid(this.ctx, this.x, this.y, this.size, 'white', false); }
    update() {
      this.x += this.vx;
      this.y += this.vy;
      const margin = this.size + 2;
      if (this.x - margin < 0) { this.x = margin; this.vx = -this.vx; }
      if (this.x + margin > this.canvas.width) { this.x = this.canvas.width - margin; this.vx = -this.vx; }
      if (this.y - margin < 0) { this.y = margin; this.vy = -this.vy; }
      if (this.y + margin > this.canvas.height) { this.y = this.canvas.height - margin; this.vy = -this.vy; }
    }
  }

  const calculatePerimeter = (w, h, r) => {
    const straightTop = Math.max(0, w - 2 * r);
    const straightRight = Math.max(0, h - 2 * r);
    const straightBottom = Math.max(0, w - 2 * r);
    const straightLeft = Math.max(0, h - 2 * r);
    const cornerLen = (Math.PI * r) / 2;
    return straightTop + straightRight + straightBottom + straightLeft + (4 * cornerLen);
  };

  const getPointAtDistance = (dist, w, h, r, perimeterVal) => {
    const straightTop = Math.max(0, w - 2 * r);
    const straightRight = Math.max(0, h - 2 * r);
    const straightBottom = Math.max(0, w - 2 * r);
    const straightLeft = Math.max(0, h - 2 * r);
    const cornerLen = (Math.PI * r) / 2;

    const segments = [
      { length: straightTop, type: 'top' },
      { length: cornerLen, type: 'tr-corner' },
      { length: straightRight, type: 'right' },
      { length: cornerLen, type: 'br-corner' },
      { length: straightBottom, type: 'bottom' },
      { length: cornerLen, type: 'bl-corner' },
      { length: straightLeft, type: 'left' },
      { length: cornerLen, type: 'tl-corner' }
    ];

    let d = ((dist % perimeterVal) + perimeterVal) % perimeterVal;

    for (let seg of segments) {
      if (seg.length === 0) continue;
      if (d <= seg.length) {
        const t = d / seg.length;
        switch (seg.type) {
          case 'top': return { x: r + d, y: 0 };
          case 'tr-corner': {
            const angle = -Math.PI / 2 + t * (Math.PI / 2);
            return { x: w - r + r * Math.cos(angle), y: r + r * Math.sin(angle) };
          }
          case 'right': return { x: w, y: r + d };
          case 'br-corner': {
            const angle = t * (Math.PI / 2);
            return { x: w - r + r * Math.cos(angle), y: h - r + r * Math.sin(angle) };
          }
          case 'bottom': return { x: w - r - d, y: h };
          case 'bl-corner': {
            const angle = Math.PI / 2 + t * (Math.PI / 2);
            return { x: r + r * Math.cos(angle), y: h - r + r * Math.sin(angle) };
          }
          case 'left': return { x: 0, y: h - r - d };
          case 'tl-corner': {
            const angle = Math.PI + t * (Math.PI / 2);
            return { x: r + r * Math.cos(angle), y: r + r * Math.sin(angle) };
          }
        }
      }
      d -= seg.length;
    }
    return { x: 0, y: 0 };
  };

  const updateButtonAnimation = (timestamp) => {
    const btn = buttonContainerRef.current?.querySelector('button');
    if (!btn || !perimeter) return;

    if (!animationStartTimeRef.current) {
      animationStartTimeRef.current = timestamp;
    }

    const w = btn.offsetWidth;
    const h = btn.offsetHeight;
    const r = 20;

    const currentPerimeter = calculatePerimeter(w, h, r);
    const elapsed = timestamp - animationStartTimeRef.current;
    const borderProgress = ((elapsed * borderSpeedMultiplier) % totalDuration) / totalDuration;
    const asteroidProgress = ((elapsed * asteroidSpeedMultiplier) % totalDuration) / totalDuration;

    if (borderRectRef.current) {
      const borderOffset = -borderProgress * currentPerimeter;
      borderRectRef.current.style.strokeDasharray = `${currentPerimeter - gapSize}px ${gapSize}px`;
      borderRectRef.current.style.strokeDashoffset = `${borderOffset}px`;
    }

    const gapCenter = (asteroidProgress * currentPerimeter) + (gapSize / 2);
    let starDistance = gapCenter + starOffset;

    const point = getPointAtDistance(starDistance, w, h, r, currentPerimeter);

    if (trailCanvasRef.current) {
      const trailCtx = trailCanvasRef.current.getContext('2d');
      trailCtx.clearRect(0, 0, trailCanvasRef.current.width, trailCanvasRef.current.height);
      drawAstroid(trailCtx, point.x + 40, point.y + 40, 12, '#ffffff', true);
    }
  };

  const animateButton = (timestamp) => {
    updateButtonAnimation(timestamp);
    animationFrameIdRef.current = requestAnimationFrame(animateButton);
  };

  const animateInsideStars = () => {
    if (!starCanvasRef.current || !starCanvasRef.current.width) {
      insideStarsAnimationRef.current = requestAnimationFrame(animateInsideStars);
      return;
    }
    const ctx = starCanvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, starCanvasRef.current.width, starCanvasRef.current.height);
    if (bouncingStarsRef.current) {
      for (let star of bouncingStarsRef.current) {
        star.update();
        star.draw();
      }
    }
    insideStarsAnimationRef.current = requestAnimationFrame(animateInsideStars);
  };

  const initButtonAnimation = () => {
    const btn = buttonContainerRef.current?.querySelector('button');
    if (!btn) {
      setTimeout(() => initButtonAnimation(), 50);
      return;
    }

    const w = btn.clientWidth;
    const h = btn.clientHeight;

    if (w <= 0 || h <= 0) {
      return;
    }

    const r = 20;

    perimeter = calculatePerimeter(w, h, r);

    if (borderRectRef.current) {
      borderRectRef.current.setAttribute('width', w - 3);
      borderRectRef.current.setAttribute('height', h - 3);
      borderRectRef.current.style.strokeDasharray = `${perimeter - gapSize}px ${gapSize}px`;
      borderRectRef.current.style.strokeDashoffset = `0px`;
    }

    if (starCanvasRef.current) {
      starCanvasRef.current.width = w;
      starCanvasRef.current.height = h;
    }
    if (trailCanvasRef.current) {
      trailCanvasRef.current.width = w + 80;
      trailCanvasRef.current.height = h + 80;
    }

    const newStars = [];
    for (let i = 0; i < 5; i++) {
      const size = 6 + Math.random() * 5;
      const x = size + Math.random() * (w - 2 * size);
      const y = size + Math.random() * (h - 2 * size);
      const vx = (Math.random() * 1.8 + 0.6) * (Math.random() > 0.5 ? 1 : -1);
      const vy = (Math.random() * 1.8 + 0.6) * (Math.random() > 0.5 ? 1 : -1);
      newStars.push(new BouncingStar(starCanvasRef.current, x, y, size, vx, vy));
    }
    bouncingStarsRef.current = newStars;

    if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
    animationFrameIdRef.current = requestAnimationFrame(animateButton);

    if (insideStarsAnimationRef.current) cancelAnimationFrame(insideStarsAnimationRef.current);
    insideStarsAnimationRef.current = requestAnimationFrame(animateInsideStars);
  };

  useEffect(() => {
    if (isGenerateMenuOpen) {
      const timer = setTimeout(() => {
        initButtonAnimation();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isGenerateMenuOpen]);

  const projectName = projectData?.name || "Untitled Project";
  const getImageSizeDisplay = () => imageDimensions.width && imageDimensions.height ? `${imageDimensions.width} x ${imageDimensions.height}` : "1920 x 1080";
  const imageSize = getImageSizeDisplay();
  const getFileName = () => canvasImage ? (typeof canvasImage === 'object' && canvasImage.name ? canvasImage.name : `${projectData?.name?.toLowerCase().replace(/\s+/g, '_') || 'untitled'}.png`) : "untitled.png";
  const fileName = getFileName();

  const toggleMenu = () => {
    if (!isMenuOpen) {
      setIsMenuOpen(true);
      setIsSideMenuOpen(false);
      setIsBottomMenuOpen(false);
      setShowLayersMenu(false);
      setSliderPosition(0);
      if (sliderButtonRef.current) sliderButtonRef.current.style.transform = 'translateY(0px)';
    }
  };

  const toggleSideMenu = () => {
    if (isMenuOpen) return;
    setIsSideMenuOpen(!isSideMenuOpen);
    if (!isSideMenuOpen) {
      setIsBottomMenuOpen(false);
    }
  };

  const toggleBottomMenu = () => {
    if (isMenuOpen) return;
    setIsBottomMenuOpen(!isBottomMenuOpen);
    if (!isBottomMenuOpen) {
      setIsSideMenuOpen(false);
    }
  };

  const handleToolsClick = () => {
    if (isMenuOpen) {
      closeWithoutRestoreRef.current = true;
      setIsMenuOpen(false);
      setSliderPosition(0);
      if (sliderButtonRef.current) sliderButtonRef.current.style.transform = 'translateY(0px)';
    }
    setIsSideMenuOpen(!isSideMenuOpen);
    setIsBottomMenuOpen(false);
    setShowLayersMenu(false);
  };

  const handleButtonClick = (buttonName) => {
    if (sacRef.current?.isListening && selectedButton !== buttonName) {
      sacRef.current.stopListening();
    }

    if (buttonName !== 'RECOLOR' && rightSidebarTool === 'wallColor') {
      setRightSidebarTool(null);
      setIsRightSidebarOpen(false);
      setShowWallColorTool(false);
      setActiveTool(null);
    }

    if (selectedButton === buttonName) {
      setSelectedButton(null);
      if (buttonName === 'P' || buttonName === 'L1' || buttonName === 'PT' || buttonName === 'RECOLOR') {
        setActiveTool(null);
      }
      if (buttonName === 'L1') {
        setLassoPoints([]);
        setIsDrawingLasso(false);
        setCurrentLassoPath(null);
        setIsGenerateMenuOpen(false);
      } else if (buttonName === 'T') {
        handleDeleteSelected();
      } else if (buttonName === 'RECOLOR') {
        setShowWallColorTool(false);
        setRightSidebarTool(null);
        setIsRightSidebarOpen(false);
        setActiveTool(null);
      } else if (buttonName === 'PT') {
        setActiveTool(null);
      } else if (buttonName === 'P') {
        setActiveTool(null);
      }
      return;
    }

    setSelectedButton(buttonName);

    if (buttonName === 'P') {
      setActiveTool('pointer');
      setLassoPoints([]);
      setIsDrawingLasso(false);
      setCurrentLassoPath(null);
      setIsGenerateMenuOpen(false);
      setShowWallColorTool(false);
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setPointerPosition({ x: rect.width / 2, y: rect.height / 2 });
      }
    } else if (buttonName === 'L1') {
      setActiveTool('penLasso');
      setLassoPoints([]);
      setIsDrawingLasso(false);
      setCurrentLassoPath(null);
      setIsGenerateMenuOpen(false);
      setShowWallColorTool(false);
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setPointerPosition({ x: rect.width / 2, y: rect.height / 2 });
      }
    } else if (buttonName === 'PT') {
      setActiveTool('penTool');
    } else if (buttonName === 'T') {
    } else if (buttonName === 'RECOLOR') {
      setActiveTool('recolor');
      setShowWallColorTool(true);
      setRightSidebarTool('wallColor');
      setIsRightSidebarOpen(true);
    }
  };

  const handleBottomButtonClick = (buttonName) => {
    if (sacRef.current?.isListening && selectedBottomButton !== buttonName) {
      sacRef.current.stopListening();
    }

    if (selectedBottomButton === buttonName) {
      setSelectedBottomButton(null);
      if (buttonName === 'DC') {
      } else if (buttonName === 'STYLE') {
        setShowQuickStylePanel(false);
      }
    } else {
      setSelectedBottomButton(buttonName);

      if (buttonName === 'DC') {
        toggleDynamicCanvas();
      } else if (buttonName === 'STYLE') {
        setShowQuickStylePanel(true);
      }
    }
  };

  const handleSettingsClick = () => {
    setShowSettings(true);
  };

  const handleOpenDynamicCanvas = () => {
    setIsDynamicCanvasActive(true);
  };

  const handleApplyDynamicCanvas = () => {
    setIsDynamicCanvasActive(true);
    setShowDynamicCanvas(false);
    requestGyroPermission();
    if (isMenuOpen) {
      setIsMenuOpen(false);
      setSliderPosition(0);
      if (sliderButtonRef.current) sliderButtonRef.current.style.transform = '';
    }
  };

  useEffect(() => {
    return () => {
      if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
      if (dotsIntervalRef.current) clearInterval(dotsIntervalRef.current);
      if (marchingAntsAnimRef.current) cancelAnimationFrame(marchingAntsAnimRef.current);
      if (pointerAnimRef.current) cancelAnimationFrame(pointerAnimRef.current);
      if (resetSliderTimeoutRef.current) clearTimeout(resetSliderTimeoutRef.current);
      if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
      if (insideStarsAnimationRef.current) cancelAnimationFrame(insideStarsAnimationRef.current);
      if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current);
      if (autoSaveInterval) clearInterval(autoSaveInterval);
    };
  }, [autoSaveInterval]);

  useEffect(() => {
    if (sliderPosition >= 160) {
      setIsMenuOpen(false);
      setSliderPosition(0);
      if (sliderButtonRef.current) sliderButtonRef.current.style.transform = 'translateY(0px)';
    }
  }, [sliderPosition]);

  // Handle top nav menu opening/closing
  useEffect(() => {
    if (isMenuOpen) {
      previousMenuStateRef.current = {
        sideMenuOpen: isSideMenuOpen,
        bottomMenuOpen: isBottomMenuOpen,
        layersMenuOpen: showLayersMenu,
        wallColorOpen: showWallColorTool,
        pictureFrameOpen: showPictureFrameTool,
        compassOpen: showCompassTools,
        quickStyleOpen: showQuickStylePanel,
      };
      setIsSideMenuOpen(false);
      setIsBottomMenuOpen(false);
      setShowLayersMenu(false);
      if (showWallColorTool) {
        setShowWallColorTool(false);
        setRightSidebarTool(null);
        setIsRightSidebarOpen(false);
        setActiveTool(null);
      }
      if (showPictureFrameTool) {
        if (pictureFrameToolRef.current) {
          try {
            pictureFrameToolRef.current.saveActiveEdit();
          } catch (err) {
            console.error('Error saving active frame edit:', err);
          }
        }
        setShowPictureFrameTool(false);
      }
      if (showCompassTools) {
        setShowCompassTools(false);
      }
      if (showQuickStylePanel) {
        setShowQuickStylePanel(false);
      }
    } else {
      if (!closeWithoutRestoreRef.current) {
        setIsSideMenuOpen(previousMenuStateRef.current.sideMenuOpen);
        setIsBottomMenuOpen(previousMenuStateRef.current.bottomMenuOpen);
        setShowLayersMenu(previousMenuStateRef.current.layersMenuOpen);
        if (previousMenuStateRef.current.wallColorOpen) {
          setShowWallColorTool(true);
          setRightSidebarTool('wallColor');
          setIsRightSidebarOpen(true);
          setActiveTool('recolor');
        }
        if (previousMenuStateRef.current.pictureFrameOpen) {
          setShowPictureFrameTool(true);
        }
        if (previousMenuStateRef.current.compassOpen) {
          setShowCompassTools(true);
        }
        if (previousMenuStateRef.current.quickStyleOpen) {
          setShowQuickStylePanel(true);
        }
      }
      closeWithoutRestoreRef.current = false;
    }
  }, [isMenuOpen]);

  // Measure text width and update the simulated cursor position
  useEffect(() => {
    const isMobileView = window.innerWidth <= 767;
    const input = isMobileView ? inputRef.current : desktopInputRef.current;
    const measureEl = isMobileView ? mobileMeasureRef.current : desktopMeasureRef.current;
    const cursorEl = isMobileView ? mobileCursorRef.current : desktopCursorRef.current;
    if (!measureEl || !cursorEl || !input) {
      if (cursorEl) cursorEl.style.display = 'none';
      return;
    }

    const shouldShow = isListening && !isInputFocused && generatePrompt && generatePrompt.length > 0;
    if (!shouldShow) {
      cursorEl.style.display = 'none';
      return;
    }

    const computed = getComputedStyle(input);
    const textStyleProps = [
      'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
      'letterSpacing', 'wordSpacing', 'textTransform',
      'lineHeight'
    ];
    for (let prop of textStyleProps) {
      try {
        measureEl.style[prop] = computed[prop];
      } catch (e) {
        // Skip
      }
    }

    measureEl.style.position = 'absolute';
    measureEl.style.visibility = 'hidden';
    measureEl.style.whiteSpace = 'pre';
    measureEl.style.display = 'inline-block';
    measureEl.style.pointerEvents = 'none';
    measureEl.style.width = 'auto';
    measureEl.style.height = 'auto';
    measureEl.style.margin = '0';
    measureEl.style.padding = '0';
    measureEl.style.border = 'none';
    measureEl.style.boxSizing = 'content-box';

    measureEl.textContent = generatePrompt;

    void measureEl.offsetHeight;

    const width = measureEl.offsetWidth;

    const CURSOR_OFFSET = 0;

    const inputPaddingLeft = parseFloat(computed.paddingLeft) || 0;
    cursorEl.style.left = (inputPaddingLeft + width + CURSOR_OFFSET) + 'px';
    cursorEl.style.display = 'block';
  }, [generatePrompt, isListening, isInputFocused]);

  // Listen to focus/blur events on both inputs
  useEffect(() => {
    const handleFocus = () => setIsInputFocused(true);
    const handleBlur = () => setIsInputFocused(false);
    const desktopInput = desktopInputRef.current;
    const mobileInput = inputRef.current;
    if (desktopInput) {
      desktopInput.addEventListener('focus', handleFocus);
      desktopInput.addEventListener('blur', handleBlur);
    }
    if (mobileInput) {
      mobileInput.addEventListener('focus', handleFocus);
      mobileInput.addEventListener('blur', handleBlur);
    }
    return () => {
      if (desktopInput) {
        desktopInput.removeEventListener('focus', handleFocus);
        desktopInput.removeEventListener('blur', handleBlur);
      }
      if (mobileInput) {
        mobileInput.removeEventListener('focus', handleFocus);
        mobileInput.removeEventListener('blur', handleBlur);
      }
    };
  }, []);

  const handleDragStart = (e) => {
    if (!isMenuOpen) return;
    e.preventDefault();
    setIsDragging(true);
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartYRef.current = clientY;
    dragStartPositionRef.current = sliderPosition;
  };

  const handleDragMove = (e) => {
    if (!isDragging || !isMenuOpen) return;
    e.preventDefault();
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = dragStartYRef.current - currentY;
    let newPosition = dragStartPositionRef.current + deltaY;
    newPosition = Math.max(0, Math.min(380, newPosition));
    setSliderPosition(newPosition);
    if (sliderButtonRef.current) sliderButtonRef.current.style.transform = `translateY(${-newPosition}px)`;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    if (isMenuOpen) {
      setSliderPosition(0);
      if (sliderButtonRef.current) sliderButtonRef.current.style.transform = 'translateY(0px)';
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging]);

  // Prompt panel drag handlers
  useEffect(() => {
    const handlePromptDragMove = (e) => {
      if (!isDraggingPromptPanel) return;
      e.preventDefault();
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const deltaY = promptPanelDragStartY.current - clientY;
      let newHeight = promptPanelStartHeight.current + deltaY;
      newHeight = Math.max(24, Math.min(300, newHeight));
      setPromptPanelHeight(newHeight);
      setIsPromptPanelExpanded(newHeight > 60);
    };

    const handlePromptDragEnd = () => {
      if (isDraggingPromptPanel) {
        setIsDraggingPromptPanel(false);
        if (promptPanelHeight > 60) {
          setPromptPanelHeight(null);
          setIsPromptPanelExpanded(true);
        } else {
          setPromptPanelHeight(24);
          setIsPromptPanelExpanded(false);
        }
      }
    };

    if (isDraggingPromptPanel) {
      window.addEventListener('mousemove', handlePromptDragMove);
      window.addEventListener('mouseup', handlePromptDragEnd);
      window.addEventListener('touchmove', handlePromptDragMove, { passive: false });
      window.addEventListener('touchend', handlePromptDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handlePromptDragMove);
      window.removeEventListener('mouseup', handlePromptDragEnd);
      window.removeEventListener('touchmove', handlePromptDragMove);
      window.removeEventListener('touchend', handlePromptDragEnd);
    };
  }, [isDraggingPromptPanel, promptPanelHeight]);

  const smoothPointerRef = useRef({ x: null, y: null, targetX: null, targetY: null });
  useEffect(() => {
    if (activeTool && pointerPosition.x !== null && pointerPosition.y !== null && !isMenuOpen) {
      smoothPointerRef.current.targetX = pointerPosition.x;
      smoothPointerRef.current.targetY = pointerPosition.y;
      if (smoothPointerRef.current.x === null) {
        smoothPointerRef.current.x = pointerPosition.x;
        smoothPointerRef.current.y = pointerPosition.y;
      }
      const animatePointer = () => {
        if (smoothPointerRef.current.targetX !== null) {
          smoothPointerRef.current.x += (smoothPointerRef.current.targetX - smoothPointerRef.current.x) * 0.3;
          smoothPointerRef.current.y += (smoothPointerRef.current.targetY - smoothPointerRef.current.y) * 0.3;
          const pointerDot = document.querySelector('.pointer-dot');
          if (pointerDot) {
            pointerDot.style.left = `${smoothPointerRef.current.x}px`;
            pointerDot.style.top = `${smoothPointerRef.current.y}px`;
          }
          pointerAnimRef.current = requestAnimationFrame(animatePointer);
        }
      };
      animatePointer();
      return () => {
        if (pointerAnimRef.current) cancelAnimationFrame(pointerAnimRef.current);
      };
    }
  }, [activeTool, pointerPosition, isMenuOpen]);

  const handleCanvasMove = (e) => {
    if (showExportOptions) return;
    if (isMenuOpen) return;
    const rect = e.currentTarget.getBoundingClientRect();
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    let x = clientX - rect.left;
    let y = clientY - rect.top;
    x = Math.max(0, Math.min(rect.width, x));
    y = Math.max(0, Math.min(rect.height, y));

    if (activeTool === 'recolor') return;

    if (activeTool === 'pointer') {
      setPointerPosition({ x, y });
    } else if (activeTool === 'penLasso') {
      setPointerPosition({ x, y });
      if (isDrawingLasso) setLassoPoints(prev => [...prev, { x, y }]);
    }
  };

  const handleCanvasMouseLeave = () => {
    if (showExportOptions) return;
    if (isMenuOpen) return;
    if (activeTool === 'penLasso' && isDrawingLasso) {
      if (lassoPoints.length > 2) {
        const innerRect = desktopCanvasInnerRef.current?.getBoundingClientRect();
        const containerW = innerRect?.width;
        const containerH = innerRect?.height;
        const imagePoints = convertContainerPointsToImageContain(
          lassoPoints,
          containerW,
          containerH,
          imageDimensions.width,
          imageDimensions.height
        );
        setCurrentLassoPath({
          points: imagePoints,
          isImageSpace: true,
          containerWidth: containerW,
          containerHeight: containerH,
        });
        setActiveLayerForVariations(null);
        if (voiceEnabled && isMobile && sacRef.current) {
          sacRef.current.startListening();
        }
      }
      setIsDrawingLasso(false);
      setLassoPoints([]);
    }
  };

  const handleCanvasMouseDown = (e) => {
    if (showExportOptions) return;
    if (isMenuOpen) return;

    if (sacRef.current?.isListening) {
      sacRef.current.stopListening();
    }

    if (activeTool === 'recolor') return;

    if (activeTool === 'penLasso') {
      if (!imageDimensions.width || !imageDimensions.height) {
        return;
      }
      pushToUndoStack();
      setCurrentLassoPath(null);
      const rect = e.currentTarget.getBoundingClientRect();
      let clientX, clientY;
      if (e.touches) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      setLassoPoints([{ x, y }]);
      setIsDrawingLasso(true);
    } else if (activeTool === 'pointer') {
      handleLayerClick(e);
    }
  };

  const handleCanvasMouseUp = () => {
    if (showExportOptions) return;
    if (isMenuOpen) return;

    if (activeTool === 'recolor') return;

    if (activeTool === 'penLasso' && isDrawingLasso) {
      if (lassoPoints.length > 2) {
        const isDesktop = window.innerWidth > 767;
        const containerEl = isDesktop ? desktopCanvasInnerRef.current : canvasRef.current;
        const rect = containerEl?.getBoundingClientRect();
        const containerW = rect?.width;
        const containerH = rect?.height;
        const useContain = isDesktop;
        const imagePoints = useContain
          ? convertContainerPointsToImageContain(
              lassoPoints,
              containerW,
              containerH,
              imageDimensions.width,
              imageDimensions.height
            )
          : convertContainerPointsToImage(
              lassoPoints,
              containerW,
              containerH,
              imageDimensions.width,
              imageDimensions.height
            );
        setCurrentLassoPath({
          points: imagePoints,
          isImageSpace: true,
          containerWidth: containerW,
          containerHeight: containerH,
          useContain: useContain,
        });
        setActiveLayerForVariations(null);
        if (voiceEnabled && isMobile && sacRef.current) {
          sacRef.current.startListening();
        }
      }
      setIsDrawingLasso(false);
      setLassoPoints([]);
    }
  };

  const handleCanvasAreaMouseDown = (e) => {
    if (!isMenuOpen && activeTool === 'recolor' && showWallColorTool && desktopCanvasInnerRef.current && desktopCanvasAreaRef.current) {
      const outerRect = desktopCanvasAreaRef.current.getBoundingClientRect();
      const innerRect = desktopCanvasInnerRef.current.getBoundingClientRect();
      const offsetX = innerRect.left - outerRect.left;
      const offsetY = innerRect.top - outerRect.top;
      const clientX = (e.touches ? e.touches[0].clientX : e.clientX) - offsetX;
      const clientY = (e.touches ? e.touches[0].clientY : e.clientY) - offsetY;
      const syntheticE = { ...e, clientX, clientY, touches: e.touches ? [{ clientX, clientY }] : undefined };
      if (recolorToolRef.current?.startDrawing) {
        recolorToolRef.current.startDrawing(syntheticE, innerRect);
      }
    }
  };

  const handleCanvasAreaMouseMove = (e) => {
    if (!isMenuOpen && activeTool === 'recolor' && showWallColorTool && desktopCanvasInnerRef.current && desktopCanvasAreaRef.current) {
      const outerRect = desktopCanvasAreaRef.current.getBoundingClientRect();
      const innerRect = desktopCanvasInnerRef.current.getBoundingClientRect();
      const offsetX = innerRect.left - outerRect.left;
      const offsetY = innerRect.top - outerRect.top;
      const clientX = (e.touches ? e.touches[0].clientX : e.clientX) - offsetX;
      const clientY = (e.touches ? e.touches[0].clientY : e.clientY) - offsetY;
      const syntheticE = { ...e, clientX, clientY, touches: e.touches ? [{ clientX, clientY }] : undefined };
      if (recolorToolRef.current?.draw) {
        recolorToolRef.current.draw(syntheticE, innerRect);
      }
    }
  };

  const handleCanvasAreaMouseUp = (e) => {
    if (!isMenuOpen && activeTool === 'recolor' && showWallColorTool && desktopCanvasInnerRef.current && desktopCanvasAreaRef.current) {
      const outerRect = desktopCanvasAreaRef.current.getBoundingClientRect();
      const innerRect = desktopCanvasInnerRef.current.getBoundingClientRect();
      const offsetX = innerRect.left - outerRect.left;
      const offsetY = innerRect.top - outerRect.top;
      const clientX = (e.touches ? e.touches[0].clientX : e.clientX) - offsetX;
      const clientY = (e.touches ? e.touches[0].clientY : e.clientY) - offsetY;
      const syntheticE = { ...e, clientX, clientY, touches: e.touches ? [{ clientX, clientY }] : undefined };
      if (recolorToolRef.current?.stopDrawing) {
        recolorToolRef.current.stopDrawing(syntheticE, innerRect);
      }
    }
  };
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 767;

  // ============================================
  // COMPOSITE TRIGGER — non-destructive display
  // ----------------------------------------------------------------------------
  // The composite is a pure function of (canvasImage, frameImages). It is
  // built here on every relevant change and written ONLY to displayImageUrl.
  // canvasImage is NEVER touched by this effect.
  //
  // Loading gate: isProjectLoading is only flipped to false from here, and
  // only when BOTH:
  //   1. The canonical working base has landed (baseCanonicalRef.current)
  //   2. The frames pipeline has run to completion (framesAttemptedRef.current)
  // This is the "load everything at once" guarantee — the visible canvas
  // never shows a bare base while waiting for frames to appear.
  // ============================================
  const compositeGenerationRef = useRef(0);
  // Per-fingerprint cache of composites built this session. Keyed by the
  // compositeKey so that toggling between rooms A and B does NOT force a
  // rebuild every single time — A's composite and B's composite both live
  // in this Map, so each switch is a synchronous restore instead of an
  // async rebuild. A single-slot ref (the previous design) could only hold
  // ONE room's composite at a time, which meant every A↔B toggle forced a
  // full rebuild on one side and could leave displayImageUrl stuck on the
  // raw base if the rebuild was interrupted by another switch.
  const compositeCacheRef = useRef(new Map());
  useEffect(() => {
    if (!canvasImage) return;
    // Gate: do not build a composite until the project load pipeline has
    // fully settled. See the projectReady state declaration for details.
    if (!projectReady) return;

    const placedFrames = (frameImages || []).filter(
      f => f && f.corners && Array.isArray(f.corners) && f.corners.length === 4 && f.isPlaced !== false
    );

    if (placedFrames.length === 0) {
      console.log('[rooms:composite] no placed frames → showing raw base', {
        activeRoomId: activeRoomIdRef.current,
        isDataUrl: String(canvasImage).startsWith('data:'),
        isStoragePath: String(canvasImage).startsWith('storage-path:'),
      });
      setDisplayImageUrl(canvasImage);
      setIsCompositePending(false);
      setIsProjectLoading(false);
      // NOTE: do NOT clear compositeCacheRef here. Other rooms' composites
      // remain valid and must survive a switch to a no-frame room and back.
      return;
    }

    // Cheap fingerprint of every input that actually affects the composite.
    // If the fingerprint matches the last successful build, skip the work.
    // This eliminates rebuilds that fire when only unrelated project state
    // changes (layers array identity, selectedLayerId, etc.) in a way that
    // happens to re-render this effect.
    const compositeKey = JSON.stringify({
      base: typeof canvasImage === 'string' ? canvasImage.slice(-160) : null,
      frames: placedFrames.map(f => ({
        id: f.id,
        corners: f.corners,
        rotation: f.rotation || { x: 0, y: 0 },
        borderWidth: f.borderWidth || 0,
        borderType: f.borderType || 'solid',
        borderColor: f.borderColor || '#000000',
        gradientStartColor: f.gradientStartColor || '#000000',
        gradientEndColor: f.gradientEndColor || '#ffffff',
        naturalWidth: f.naturalWidth,
        naturalHeight: f.naturalHeight,
      })),
    });

    const cachedForThisKey = compositeCacheRef.current.get(compositeKey);
    if (cachedForThisKey) {
      console.log('[rooms:composite] CACHE HIT (sync restore)', {
        activeRoomId: activeRoomIdRef.current,
        isDataUrl: String(canvasImage).startsWith('data:'),
        isStoragePath: String(canvasImage).startsWith('storage-path:'),
        frameCount: placedFrames.length,
      });
      setDisplayImageUrl(cachedForThisKey);
      setIsProjectLoading(false);
      return;
    }
    console.log('[rooms:composite] cache MISS → scheduling async build', {
      activeRoomId: activeRoomIdRef.current,
      isDataUrl: String(canvasImage).startsWith('data:'),
      isStoragePath: String(canvasImage).startsWith('storage-path:'),
      frameCount: placedFrames.length,
      cacheSize: compositeCacheRef.current.size,
    });

    const myGeneration = ++compositeGenerationRef.current;
    setIsCompositePending(true);
    setLoadStatusText('Compositing frames…');

    const timer = setTimeout(async () => {
      try {
        compositeLog('start', {
          generation: myGeneration,
          frameCount: frameImages.length,
          placedCount: placedFrames.length,
          frames: placedFrames.map(f => ({
            id: f.id,
            isPlaced: f.isPlaced,
            corners: f.corners?.length,
            hasOriginalImage: !!f.originalImage,
          })),
        });

        // ── IndexedDB cache check ────────────────────────────────────
        // If Dashboard (or a previous visit this session) already built
        // this exact composite, reuse it — no JS pixel loop.
        const cacheKey = computeCompositeCacheKey(canvasImage, placedFrames);
        const cached = await getCachedComposite(cacheKey);

        compositeLog('cache lookup', {
          generation: myGeneration,
          hit: !!cached,
        });

        if (myGeneration !== compositeGenerationRef.current) return;

        if (cached) {
          console.log('[rooms:composite] IndexedDB HIT', {
            activeRoomId: activeRoomIdRef.current,
            gen: myGeneration,
          });
          compositeCacheRef.current.set(compositeKey, cached);
          setDisplayImageUrl(cached);
          setIsCompositePending(false);
          setIsProjectLoading(false);
          return;
        }
        console.log('[rooms:composite] IndexedDB MISS → building pixels', {
          activeRoomId: activeRoomIdRef.current,
          gen: myGeneration,
        });

        const tBuild = performance.now();
        const composite = await buildFrameComposite(canvasImage, frameImages);
        loadDebugMark('composite built', {
          elapsedMs: (performance.now() - tBuild).toFixed(0),
          generation: myGeneration,
        });

        compositeLog('built', {
          generation: myGeneration,
          current: compositeGenerationRef.current,
          stale: myGeneration !== compositeGenerationRef.current,
          hasResult: !!composite,
          resultLength: composite ? composite.length : 0,
        });

        // Drop stale composites
        if (myGeneration !== compositeGenerationRef.current) return;
        if (composite) {
          console.log('[rooms:composite] BUILD OK', {
            activeRoomId: activeRoomIdRef.current,
            gen: myGeneration,
            currentGen: compositeGenerationRef.current,
            frameCount: placedFrames.length,
          });
          compositeCacheRef.current.set(compositeKey, composite);
          setDisplayImageUrl(composite);
          // Fire-and-forget — future reopens of this project will hit the
          // cache and skip the pixel loop entirely.
          setCachedComposite(cacheKey, composite).catch(() => { /* noop */ });
        }
        setIsCompositePending(false);
        setIsProjectLoading(false);
      } catch (err) {
        console.error('[rooms:composite] BUILD FAILED', {
          activeRoomId: activeRoomIdRef.current,
          gen: myGeneration,
          currentGen: compositeGenerationRef.current,
          error: err?.message || String(err),
          stack: err?.stack,
        });
        if (myGeneration === compositeGenerationRef.current) {
          setDisplayImageUrl(canvasImage);
          setIsCompositePending(false);
          setIsProjectLoading(false);
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [canvasImage, frameImages, projectReady]);

  // ============================================
  // SEED THE "ORIGINAL" ROOM (fallback path)
  // ----------------------------------------------------------------------------
  // Every project must have at least one room. When the load pipeline
  // settles and the project has no persisted rooms (brand-new project,
  // or a project that predates the Rooms feature), we seed a single room
  // named "Original" whose canvas is the current base.
  //
  // Only fires when rooms.length === 0, so it never fights the load
  // pipeline when rooms ARE restored from Firestore or the prefetch.
  // ============================================
  useEffect(() => {
    if (isProjectLoading) return;
    if (rooms.length > 0) return;
    if (!canvasImage) return;

    const seedRoom = {
      id: `room_${Date.now()}_original`,
      name: 'Original',
      canvasImage: canvasImage,
      originalImage: originalImage || canvasImage,
      thumbnail: canvasImage,
      naturalWidth: imageDimensions.width || 0,
      naturalHeight: imageDimensions.height || 0,
      layers: layers,
      layerCount: Array.isArray(layers) ? layers.length : 0,
      selectedLayerId: selectedLayerId,
      frameImages: frameImagesRef.current,
      createdAt: Date.now(),
    };
    setRooms([seedRoom]);
    setActiveRoomId(seedRoom.id);
    activeRoomIdRef.current = seedRoom.id;
    roomsRef.current = [seedRoom];
  }, [isProjectLoading, canvasImage, rooms.length, originalImage, imageDimensions, layers, selectedLayerId]);

  // Lasso drawing effect
  useEffect(() => {
    let canvas = lassoCanvasDesktopRef.current;
    if (canvas && (!canvas.parentElement || canvas.parentElement.getBoundingClientRect().width === 0)) {
      canvas = null;
    }
    if (!canvas) {
      canvas = lassoCanvasRef.current;
    }
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    const rect = canvas.parentElement?.getBoundingClientRect();
    if (!rect) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);

    const mapToContainer = (imgPoints) => {
      const containerW = rect.width;
      const containerH = rect.height;
      const imgW = imageDimensions.width;
      const imgH = imageDimensions.height;
      if (!imgW || !imgH) return [];
      const containerAspect = containerW / containerH;
      const imageAspect = imgW / imgH;
      let renderWidth, renderHeight, offsetX, offsetY, scale;

      const useCover = isMobile;

      if (useCover) {
        if (containerAspect > imageAspect) {
          renderHeight = containerH;
          scale = renderHeight / imgH;
          renderWidth = imgW * scale;
          offsetX = (containerW - renderWidth) / 2;
          offsetY = 0;
        } else {
          renderWidth = containerW;
          scale = renderWidth / imgW;
          renderHeight = imgH * scale;
          offsetX = 0;
          offsetY = (containerH - renderHeight) / 2;
        }
      } else {
        if (containerAspect > imageAspect) {
          renderHeight = containerH;
          scale = renderHeight / imgH;
          renderWidth = imgW * scale;
          offsetX = (containerW - renderWidth) / 2;
          offsetY = 0;
        } else {
          renderWidth = containerW;
          scale = renderWidth / imgW;
          renderHeight = imgH * scale;
          offsetX = 0;
          offsetY = (containerH - renderHeight) / 2;
        }
      }

      return imgPoints.map(p => ({
        x: p.x * scale + offsetX,
        y: p.y * scale + offsetY,
      }));
    };

    if (currentLassoPath?.points.length > 2) {
      const containerPoints = currentLassoPath.isImageSpace
        ? mapToContainer(currentLassoPath.points)
        : currentLassoPath.points;

      if (isGenerating) {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(containerPoints[0].x, containerPoints[0].y);
        for (let i = 1; i < containerPoints.length; i++) ctx.lineTo(containerPoints[i].x, containerPoints[i].y);
        ctx.closePath();
        ctx.clip();
        ctx.filter = 'blur(8px)';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.restore();

        const bounds = getLassoBounds(containerPoints);
        const barWidth = Math.min(120, bounds.width * 0.5);
        const barHeight = 4;
        const barX = bounds.minX + (bounds.width - barWidth) / 2;
        const barY = bounds.minY + bounds.height / 2;

        ctx.fillStyle = '#333';
        ctx.fillRect(barX, barY, barWidth, barHeight);

        if (generationProgress > 0) {
          const progressWidth = (generationProgress / 100) * barWidth;
          ctx.fillStyle = '#4594f9';
          ctx.fillRect(barX, barY, progressWidth, barHeight);
        }

        ctx.fillStyle = 'white';
        ctx.font = '12px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(`Generating${generationDots}`, barX + barWidth / 2, barY - 8);
      }

      ctx.beginPath();
      ctx.moveTo(containerPoints[0].x, containerPoints[0].y);
      for (let i = 1; i < containerPoints.length; i++) ctx.lineTo(containerPoints[i].x, containerPoints[i].y);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
      ctx.fill();
      ctx.save();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = LASSO_LINE_WIDTH;
      ctx.setLineDash(MARCHING_ANTS_DASH_PATTERN);
      ctx.lineDashOffset = -marchingAntsOffset;
      ctx.stroke();
      ctx.restore();
    }
    if (isDrawingLasso && lassoPoints.length > 1) {
      ctx.beginPath();
      ctx.moveTo(lassoPoints[0].x, lassoPoints[0].y);
      for (let i = 1; i < lassoPoints.length; i++) ctx.lineTo(lassoPoints[i].x, lassoPoints[i].y);
      ctx.save();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = LASSO_LINE_WIDTH;
      ctx.setLineDash(MARCHING_ANTS_DASH_PATTERN);
      ctx.lineDashOffset = -marchingAntsOffset;
      ctx.stroke();
      ctx.restore();
    }
    ctx.setLineDash([]);
  }, [currentLassoPath, lassoPoints, isDrawingLasso, marchingAntsOffset, isGenerating, generationProgress, generationDots, displayImageUrl, isDesktopImageLoaded, imageDimensions]);
  // Reset desktop image loaded flag
  useEffect(() => {
    if (displayImageUrl) {
      setIsDesktopImageLoaded(false);
    }
  }, [displayImageUrl]);

  // Hover tooltip state
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleToolHover = (text, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipText(text);
    setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.bottom + 5 });
    setTooltipVisible(true);
  };

  const handleToolLeave = () => {
    setTooltipVisible(false);
  };

  // Theme tokens exposed to popups
  const T = THEME_TOKENS[uiTheme] || THEME_TOKENS.light;

  // Common inline popup styles (theme-aware)
  const popupOverlayStyle = useMemo(() => ({
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }), []);
  const popupContentStyle = useMemo(() => ({
    backgroundColor: T.bg, color: T.text,
    borderRadius: '16px', padding: '25px',
    maxWidth: '400px', width: '90%',
    border: `1px solid ${T.border}`,
  }), [T]);
  const popupInputStyle = useMemo(() => ({
    width: '100%', padding: '12px 16px', marginBottom: '15px',
    backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: '8px',
    fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif',
    boxSizing: 'border-box', color: T.text,
  }), [T]);
  const popupCancelStyle = useMemo(() => ({
    padding: '10px 20px', backgroundColor: 'transparent',
    border: `1px solid ${T.border}`, color: T.text,
    borderRadius: '30px', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
  }), [T]);
  const popupConfirmStyle = useMemo(() => ({
    padding: '10px 20px', backgroundColor: T.accent, border: 'none',
    borderRadius: '30px', color: 'white', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
  }), [T]);

  return (
    <div className={`editor-container ${uiTheme === 'light' ? 'theme-light' : 'theme-dark'}`}>
      {tooltipVisible && (
        <div className="desktop-tooltip" style={{ left: tooltipPosition.x, top: tooltipPosition.y }}>
          {tooltipText}
        </div>
      )}

      <SettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        uiTheme={uiTheme}
        onToggleTheme={toggleUITheme}
        onOpenDepthDiorama={handleOpenDynamicCanvas}
        autoSaveEnabled={autoSaveEnabled}
        onToggleAutoSave={toggleAutoSave}
        gyroEnabled={gyroEnabled}
        onToggleGyro={async () => {
          if (!gyroEnabled) {
            const granted = await requestGyroPermission();
            if (granted) {
              setGyroEnabled(true);
            }
          } else {
            setGyroEnabled(false);
          }
        }}
        voiceEnabled={voiceEnabled}
        onToggleVoice={toggleVoice}
        tutorialShown={tutorialShown}
        onToggleTutorial={toggleTutorialSetting}
        onOpenTutorial={openTutorial}
      />

      <QuickStylePanel
        isOpen={showQuickStylePanel}
        onClose={() => setShowQuickStylePanel(false)}
        canvasImage={canvasImage}
        onStyleGenerated={handleStyleGenerated}
        authUser={authUser}
      />

      <EditorErrorBoundary label="Picture Frame Tool">
      <PictureFrameTool
        ref={pictureFrameToolRef}
        isOpen={showPictureFrameTool}
        onClose={async () => {
          if (pictureFrameToolRef.current) {
            try {
              await pictureFrameToolRef.current.saveActiveEdit();
            } catch (err) {
              console.error('Error saving active frame edit on close:', err);
            }
          }
          setShowPictureFrameTool(false);
        }}
        canvasImage={canvasImage}
        originalBackgroundImage={originalImage}
        onFrameApplied={handleFrameApplied}
        onFrameRemoved={handleFrameRemoved}
        onCompositeUpdate={(composite) => {
          if (frameImagesRef.current && frameImagesRef.current.length > 0) {
            setHasUnsavedChanges(true);
          }
        }}
        authUser={authUser}
        showToast={showToast}
        desktopCanvasRef={desktopCanvasInnerRef}
        mobileCanvasRef={canvasRef}
        savedFrames={frameImages}
        onFramesUpdate={(frames) => {
          setFrameImages(frames);
          frameImagesRef.current = frames;
        }}
      />
      </EditorErrorBoundary>

      <DynamicCanvas
        imageUrl={canvasImage}
        isActive={isDynamicCanvasActive}
        onActivate={() => {}}
        onDeactivate={() => {
          console.log('DynamicCanvas closing...');
          setIsDynamicCanvasActive(false);
          setGyroEnabled(false);
        }}
        gyroEnabled={gyroEnabled}
        onRequestGyro={requestGyroPermission}
        onGyroToggle={(enabled) => setGyroEnabled(enabled)}
        isModal={true}
      />

      {isMobile && (
        <SmartRecolorTool
          ref={recolorToolRef}
          isOpen={showWallColorTool}
          positionMode="overlay"
          onClose={() => {
            setShowWallColorTool(false);
            setActiveTool(null);
          }}
          canvasImage={canvasImage}
          onApplyWithMask={(previewImage, maskPoints) => {
            const newLayer = createNewLayer(
              maskPoints ? { points: maskPoints } : null,
              previewImage,
              'Wall Color',
              []
            );
            setLayers(prev => prev.map(l => l.id === newLayer.id ? { ...l, name: 'Wall Color' } : l));
            setShowWallColorTool(false);
            setActiveTool(null);
            debugLog('Wall Color layer created', { layerId: newLayer.id });
          }}
          authUser={authUser}
          updateRemainingCredits={(credits) => setImagesRemaining(credits)}
          mainCanvasContainerRef={canvasRef}
        />
      )}

      {showRendersPanel && (
        <RendersPanel
          authUser={authUser}
          onClose={() => setShowRendersPanel(false)}
          initialImage={renderPanelCanvasImage}
          isPWA={isPWA}
          updateRemainingCounts={() => {
            const refreshUserData = async () => {
              if (authUser) {
                const userRef = doc(db, 'users', authUser.uid);
                const updatedUser = await getDoc(userRef);
                if (updatedUser.exists()) {
                  const remaining = getRemainingCredits(updatedUser.data());
                  setImagesRemaining(remaining.images);
                }
              }
            };
            refreshUserData();
          }}
        />
      )}

      <InpaintingVariationsMenu
        isOpen={showVariationsMenu}
        variations={generatedVariations}
        onSelectVariation={handleSelectVariation}
        onClose={handleCloseVariationsMenu}
        layerId={activeLayerForVariations}
        layers={layers}
      />

      <DebugConsole
        logs={debugLogs}
        isExpanded={isDebugExpanded}
        onToggle={() => setIsDebugExpanded(!isDebugExpanded)}
        onClear={clearDebugLogs}
        onCopy={copyDebugLogs}
      />

      <div className={`loading-overlay ${isLoading ? 'show' : ''}`}>
        <div className="loading-text">Saving Project {loadingDots}</div>
        <div className="loading-bar-container">
          <div className="loading-bar" style={{ width: `${loadingProgress}%` }}></div>
        </div>
        <div className="loading-percentage">{Math.round(loadingProgress)}%</div>
      </div>

      <div className="layout-placeholder">this is desktop layout, tablet layout</div>

      {showCompassTools && (
        <CompassToolsModal
          onClose={() => setShowCompassTools(false)}
          uiTheme={uiTheme}
        />
      )}

      {showExportOptions && (
        <div className="export-options-overlay" onClick={() => {
          setShowExportOptions(false);
          if (isMenuOpen) {
            setIsMenuOpen(false);
            setSliderPosition(0);
            if (sliderButtonRef.current) sliderButtonRef.current.style.transform = 'translateY(0px)';
          }
        }}>
          <div className="export-options-panel" onClick={(e) => e.stopPropagation()}>
            <div className="export-options-header">
              <h2>Export</h2>
              <button className="export-options-close" onClick={() => setShowExportOptions(false)}>✕</button>
            </div>
            <div className="export-options-content">
              <div className="export-options-section">
                <h3>Export Settings</h3>
                <div className="export-options-item">
                  <span>Format</span>
                  <div className="export-format-buttons">
                    <button
                      className={`export-format-btn ${exportFormat === 'png' ? 'active' : ''}`}
                      onClick={() => setExportFormat('png')}
                    >
                      PNG
                    </button>
                    <button
                      className={`export-format-btn ${exportFormat === 'jpeg' ? 'active' : ''}`}
                      onClick={() => setExportFormat('jpeg')}
                    >
                      JPEG
                    </button>
                    <button
                      className={`export-format-btn ${exportFormat === 'webp' ? 'active' : ''}`}
                      onClick={() => setExportFormat('webp')}
                    >
                      WEBP
                    </button>
                  </div>
                </div>
                <div className="export-options-item">
                  <span>Quality</span>
                  <div className="export-quality-control">
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={exportQuality}
                      onChange={(e) => setExportQuality(parseInt(e.target.value))}
                      className="export-quality-slider"
                    />
                    <span className="export-quality-value">{exportQuality}%</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button
                  className="export-action-btn"
                  style={{ flex: 1 }}
                  onClick={async () => {
                    const exportSource = displayImageUrl || canvasImage;
                    if (exportSource) {
                      try {
                        const img = new Image();
                        img.crossOrigin = 'anonymous';
                        img.onload = async () => {
                          const canvas = document.createElement('canvas');
                          canvas.width = img.width;
                          canvas.height = img.height;
                          const ctx = canvas.getContext('2d', { colorSpace: 'display-p3' });
                          ctx.drawImage(img, 0, 0);
                          const mimeType = exportFormat === 'png' ? 'image/png' : exportFormat === 'webp' ? 'image/webp' : 'image/jpeg';
                          const quality = exportFormat === 'png' ? 1 : exportQuality / 100;
                          const dataUrl = canvas.toDataURL(mimeType, quality);

                          const isMobileDevice = /Android|iPhone|iPad|iPod|webOS/i.test(navigator.userAgent);

                          if (isMobileDevice && navigator.share) {
                            try {
                              const response = await fetch(dataUrl);
                              const blob = await response.blob();
                              const file = new File([blob], `design.${exportFormat}`, { type: mimeType });

                              if (navigator.canShare && navigator.canShare({ files: [file] })) {
                                await navigator.share({
                                  files: [file],
                                  title: 'Design Export',
                                });
                              } else {
                                const link = document.createElement('a');
                                link.download = `design.${exportFormat}`;
                                link.href = dataUrl;
                                link.click();
                              }
                            } catch (shareError) {
                              if (shareError.name !== 'AbortError') {
                                console.warn('Share failed, falling back to download:', shareError);
                                const link = document.createElement('a');
                                link.download = `design.${exportFormat}`;
                                link.href = dataUrl;
                                link.click();
                              }
                            }
                          } else {
                            const link = document.createElement('a');
                            link.download = `design.${exportFormat}`;
                            link.href = dataUrl;
                            link.click();
                          }
                        };
                        img.src = exportSource;
                      } catch (error) {
                        console.error('Export failed:', error);
                        showToast('Export failed. Please try again.', 'error', 3000);
                      }
                    } else {
                      showToast('No image to export', 'warning', 3000);
                    }
                  }}
                >
                  Export Image
                </button>
                <button
                  className="export-action-btn video-btn"
                  style={{ flex: 1 }}
                  onClick={() => {
                    setShowExportOptions(false);
                    openRendersPanel();
                  }}
                >
                  Export as Video
                </button>
              </div>
            </div>
          </div>
          <style>{`
            .export-options-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: rgba(0, 0, 0, 0.5);
              z-index: 2000;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .export-options-panel {
              background-color: #2c2c2c;
              border-radius: 20px;
              width: 90%;
              max-width: 420px;
              max-height: 80vh;
              overflow: hidden;
              display: flex;
              flex-direction: column;
              border: 1px solid #444;
            }
            .theme-light .export-options-panel {
              background-color: #ffffff;
              border-color: #E0E0E0;
            }
            .export-options-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 20px;
              border-bottom: 1px solid #444;
            }
            .theme-light .export-options-header {
              border-bottom-color: #e0e0e0;
            }
            .export-options-header h2 {
              color: white;
              margin: 0;
              font-size: 18px;
              font-family: Avenir, sans-serif;
            }
            .theme-light .export-options-header h2 {
              color: #333;
            }
            .export-options-close {
              background: none;
              border: none;
              color: white;
              font-size: 20px;
              cursor: pointer;
            }
            .theme-light .export-options-close {
              color: #666;
            }
            .export-options-content {
              flex: 1;
              overflow-y: auto;
              padding: 20px;
            }
            .export-options-section h3 {
              color: #4594F9;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin-bottom: 12px;
              font-family: Avenir, sans-serif;
            }
            .export-options-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 0;
              border-bottom: 1px solid #444;
            }
            .theme-light .export-options-item {
              border-bottom-color: #f0f0f0;
            }
            .export-options-item span {
              color: white;
              font-family: Avenir, sans-serif;
              font-size: 14px;
            }
            .theme-light .export-options-item span {
              color: #333;
            }
            .export-format-buttons {
              display: flex;
              gap: 6px;
            }
            .export-format-btn {
              padding: 6px 14px;
              border-radius: 16px;
              border: 1px solid #555;
              background: transparent;
              color: #888;
              cursor: pointer;
              font-family: Avenir, sans-serif;
              font-size: 12px;
              transition: all 0.2s ease;
            }
            .theme-light .export-format-btn {
              border-color: #ddd;
              color: #888;
            }
            .export-format-btn.active {
              border-color: #4594F9;
              background: rgba(136, 183, 255, 0.2);
              color: #4594F9;
            }
            .theme-light .export-format-btn.active {
              background: rgba(136, 183, 255, 0.1);
            }
            .export-format-btn:hover:not(.active) {
              border-color: #4594F9;
              color: #4594F9;
            }
            .export-quality-control {
              display: flex;
              align-items: center;
              gap: 12px;
              flex: 1;
              max-width: 180px;
            }
            .export-quality-slider {
              flex: 1;
              -webkit-appearance: none;
              appearance: none;
              height: 4px;
              border-radius: 2px;
              background: #555;
              outline: none;
            }
            .theme-light .export-quality-slider {
              background: #ddd;
            }
            .export-quality-slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 16px;
              height: 16px;
              border-radius: 50%;
              background: #4594F9;
              cursor: pointer;
            }
            .export-quality-value {
              color: white;
              font-family: Avenir, sans-serif;
              font-size: 13px;
              min-width: 40px;
              text-align: center;
            }
            .theme-light .export-quality-value {
              color: #333;
            }
            .export-action-btn {
              width: 100%;
              padding: 12px;
              border-radius: 30px;
              border: none;
              background: #4594F9;
              color: white;
              font-weight: 600;
              cursor: pointer;
              font-family: Avenir, sans-serif;
              font-size: 14px;
              margin-top: 16px;
              transition: background 0.2s ease;
            }
            .export-action-btn:hover {
              background: #6a9fdf;
            }
            .export-action-btn.video-btn {
              background: #4594f9;
            }
            .export-action-btn.video-btn:hover {
              background: #3a7ed4;
            }
          `}</style>
        </div>
      )}

      {/* ============================================
         DESKTOP / TABLET EDITOR LAYOUT
         ============================================ */}
      <div className="desktop-tablet-editor-layout">
        {/* Top Bar */}
        <div className="desktop-editor-topbar">
          <div className="desktop-editor-topbar-left">
            <button className="desktop-editor-back-btn" onClick={handleDashboardClick}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Exit
            </button>
            <button
              className="desktop-editor-back-btn"
              onClick={() => {
                if (isMenuOpen) {
                  setIsMenuOpen(false);
                  setSliderPosition(0);
                  if (sliderButtonRef.current) sliderButtonRef.current.style.transform = 'translateY(0px)';
                }
                setShowExportOptions(true);
              }}
              onMouseEnter={(e) => handleToolHover('Export', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <CiExport size={18} />
              Export
            </button>
            <div className="desktop-editor-project-info">
              <div className="desktop-editor-project-name">{projectName}</div>
              <div className="desktop-editor-project-meta">
                <span>{imageSize}</span>
                <span>{fileName}</span>
              </div>
            </div>
          </div>

          <div className="desktop-editor-topbar-center">
            <div className="desktop-editor-undo-redo-group">
              <button
                className="desktop-editor-undo-btn"
                onClick={() => {
                  if (undoStack.length > 0) {
                    const previousState = undoStack[undoStack.length - 1];
                    setRedoStack(prev => [...prev, {
                      canvasImage,
                      layers: [...layers],
                      selectedLayerId,
                      currentLassoPath
                    }]);
                    setUndoStack(prev => prev.slice(0, -1));
                    setCanvasImage(previousState.canvasImage);
                    setDisplayImage(previousState.canvasImage);
                    setLayers(previousState.layers);
                    setSelectedLayerId(previousState.selectedLayerId);
                    setCurrentLassoPath(previousState.currentLassoPath);
                    debugLog('Undo performed', { stackSize: undoStack.length - 1 });
                  }
                }}
                title="Undo"
                disabled={undoStack.length === 0}
              >
                <LuUndo size={16} />
              </button>
              <button
                className="desktop-editor-redo-btn"
                onClick={() => {
                  if (redoStack.length > 0) {
                    const nextState = redoStack[redoStack.length - 1];
                    setUndoStack(prev => [...prev, {
                      canvasImage,
                      layers: [...layers],
                      selectedLayerId,
                      currentLassoPath
                    }]);
                    setRedoStack(prev => prev.slice(0, -1));
                    setCanvasImage(nextState.canvasImage);
                    setDisplayImage(nextState.canvasImage);
                    setLayers(nextState.layers);
                    setSelectedLayerId(nextState.selectedLayerId);
                    setCurrentLassoPath(nextState.currentLassoPath);
                    debugLog('Redo performed', { stackSize: redoStack.length - 1 });
                  }
                }}
                title="Redo"
                disabled={redoStack.length === 0}
              >
                <LuRedo size={16} />
              </button>
            </div>
            <div className="desktop-editor-generate-input-wrapper" style={{ position: 'relative' }}>
              <SpeakAndCreate
                ref={sacRef}
                enabled={voiceEnabled}
                onTranscript={(transcript) => {
                  setGeneratePrompt(prev => prev + (prev ? ' ' : '') + transcript);
                }}
                isGenerating={isGenerating}
                onListeningChange={setIsListening}
              />
              <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                <input
                  ref={desktopInputRef}
                  type="text"
                  inputMode="text"
                  className="desktop-editor-generate-input"
                  placeholder={placeholders[placeholderIndex]}
                  value={generatePrompt}
                  onChange={(e) => setGeneratePrompt(e.target.value)}
                  disabled={isGenerating}
                  style={{ flex: 1, paddingRight: '20px' }}
                />
                <span
                  ref={desktopMeasureRef}
                  style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    whiteSpace: 'pre',
                    font: 'inherit',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    letterSpacing: 'inherit',
                    padding: '0',
                    margin: '0',
                    border: 'none',
                    height: 'auto',
                  }}
                />
                <span
                  ref={desktopCursorRef}
                  className="voice-cursor-sim"
                  style={{
                    position: 'absolute',
                    left: '0px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#4594f9',
                    fontSize: 'inherit',
                    fontWeight: '300',
                    animation: 'blink 1s step-end infinite',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                >
                  |
                </span>
              </div>
              <button
                className="desktop-editor-generate-btn"
                onClick={handleGeneratePress}
                disabled={isGenerating || !generatePrompt.trim()}
              >
                {isGenerating ? '...' : '→'}
              </button>
            </div>
          </div>

          <div className="desktop-editor-topbar-right">
            <button
              className={`desktop-editor-tool-btn ${activeTool === 'pointer' ? 'active' : ''}`}
              onClick={() => handleButtonClick('P')}
              onMouseEnter={(e) => handleToolHover('Cursor (Select Layers)', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2FUI_ICONS%2Fcursor.png?alt=media&token=9a8595a0-7b86-45d4-b5bd-262b50a75c4f" alt="Cursor" />
            </button>
            <button
              className={`desktop-editor-tool-btn ${activeTool === 'penLasso' ? 'active' : ''}`}
              onClick={() => handleButtonClick('L1')}
              onMouseEnter={(e) => handleToolHover('Pen Lasso (Draw Selection)', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2FUI_ICONS%2Fpenlasso.png?alt=media&token=fe5e180f-74fd-4009-bc3d-2b60bbd4773b" alt="Pen Lasso" />
            </button>
            <button
              className={`desktop-editor-tool-btn ${activeTool === 'penTool' ? 'active' : ''}`}
              onClick={() => {
                setActiveTool(prev => prev === 'penTool' ? null : 'penTool');
                showToast('Pen Tool selected (straight‑line drawing)', 'info', 2000);
              }}
              onMouseEnter={(e) => handleToolHover('Pen Tool (Straight Line)', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <LuPenTool size={18} />
            </button>

            <div className="desktop-editor-tool-separator"></div>

            <button
              className={`desktop-editor-tool-btn ${showRoomsPanel ? 'active' : ''}`}
              onClick={() => {
                setIsLeftSidebarOpen(true);
                setLayersPanelTab('rooms');
                setShowRoomsPanel(true);
              }}
              onMouseEnter={(e) => handleToolHover('Rooms (Organize Angles)', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <TbCubePlus size={18} />
            </button>
            <button
              className={`desktop-editor-tool-btn ${activeTool === 'recolor' ? 'active' : ''}`}
              onClick={() => showComingSoonSection('WALL_COLOR_TOOL')}
              onMouseEnter={(e) => handleToolHover('Wall Color Tool', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <TbWallpaper size={18} />
            </button>
            <button
              className={`desktop-editor-tool-btn ${showCompassTools ? 'active' : ''}`}
              onClick={() => setShowCompassTools(true)}
              onMouseEnter={(e) => handleToolHover('Architectural Tools', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <LuDraftingCompass size={18} />
            </button>
            <button
              className={`desktop-editor-tool-btn ${showPictureFrameTool ? 'active' : ''}`}
              onClick={() => setShowPictureFrameTool(true)}
              onMouseEnter={(e) => handleToolHover('Frames Tool', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <RiPaintingLine size={18} />
            </button>
            <button
              className="desktop-editor-tool-btn"
              onClick={() => setShowQuickStylePanel(true)}
              onMouseEnter={(e) => handleToolHover('Quick Style', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <FiImage size={18} />
            </button>
            <button
              className={`desktop-editor-tool-btn ${isDynamicCanvasActive ? 'active' : ''}`}
              onClick={() => showComingSoonSection('DYNAMIC_CANVAS')}
              onMouseEnter={(e) => handleToolHover('Dynamic Canvas', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <TbAtom2 size={18} />
            </button>

            <button
              className="desktop-editor-tool-btn"
              onClick={() => setShowSettings(true)}
              onMouseEnter={(e) => handleToolHover('Settings', e)}
              onMouseLeave={handleToolLeave}
              title=""
            >
              <FiSettings size={18} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="desktop-editor-main">
          <div className={`desktop-editor-sidebar ${isLeftSidebarOpen ? 'open' : 'closed'}`}>
            <div className="desktop-editor-sidebar-content">
              <div className="desktop-editor-sidebar-section">
                <div className="desktop-editor-sidebar-title">Layers</div>
                <EditorErrorBoundary label="Desktop Layers Panel">
                <LayersPanel
                  layers={layers}
                  selectedLayerId={selectedLayerId}
                  onSelectLayer={selectLayer}
                  onRenameLayer={(layerId, newName) => {
                    setLayers(prev => prev.map(l =>
                      l.id === layerId ? { ...l, name: newName } : l
                    ));
                    showToast(`Layer renamed to "${newName}"`, 'success', 3000);
                  }}
                  onDeleteLayer={deleteLayer}
                  onShowVariations={handleShowLayerVariations}
                  onToggleVisibility={toggleLayerVisibility}
                  onToggleLock={toggleLayerLock}
                  onReorderLayers={reorderLayers}
                  activeTab={layersPanelTab}
                  onTabChange={(tab) => {
                    setLayersPanelTab(tab);
                    if (tab !== 'rooms') setShowRoomsPanel(false);
                  }}
                  rooms={rooms}
                  activeRoomId={activeRoomId}
                  onSelectRoom={handleSelectRoom}
                  onAddRoom={handleAddRoom}
                  onRenameRoom={handleRenameRoom}
                  onDeleteRoom={handleDeleteRoom}
                  onSetPrimaryRoom={handleSetPrimaryRoom}
                  projectName={projectName}
                  authUser={authUser}
                  projectId={currentProjectId}
                />
                </EditorErrorBoundary>
              </div>
            </div>
            <div className="desktop-editor-sidebar-toggle" onClick={() => setIsLeftSidebarOpen(!isLeftSidebarOpen)}>
              <span className="toggle-arrow">{isLeftSidebarOpen ? '◀' : '▶'}</span>
            </div>
          </div>

          <div
            ref={desktopCanvasAreaRef}
            className="desktop-editor-canvas-area"
            style={{
              paddingLeft: isLeftSidebarOpen ? 'calc(20px + 252px)' : '20px',
              paddingRight: isRightSidebarOpen ? 'calc(20px + 260px)' : '20px',
              transition: 'padding-left 0.3s ease, padding-right 0.3s ease'
            }}

          >
            <div
              className="desktop-editor-canvas-inner"
              ref={desktopCanvasInnerRef}
              onMouseMove={handleCanvasMove}
              onMouseDown={handleCanvasMouseDown}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseLeave}
              onTouchMove={(e) => { e.preventDefault(); handleCanvasMove(e); }}
              onTouchStart={(e) => { e.preventDefault(); handleCanvasMouseDown(e); }}
              onTouchEnd={(e) => { e.preventDefault(); handleCanvasMouseUp(); }}
              onTouchCancel={handleCanvasMouseLeave}
            >
              {isProjectLoading ? (
                <div className="project-loading-panel">
                  <div className="project-loading-progress-bar">
                    <div className="project-loading-progress-fill" />
                  </div>
                  <div className="project-loading-text">{loadStatusText}</div>
                </div>
              ) : displayImageUrl ? (
                <img
                  key={displayImageUrl}
                  src={displayImageUrl}
                  alt="Project canvas"
                  draggable={false}
                  style={{
                    pointerEvents: activeTool === 'penLasso' ? 'none' : 'auto'
                  }}
                  onLoad={() => {
                    console.log('[rooms:img] LOADED', {
                      activeRoomId: activeRoomIdRef.current,
                      isData: String(displayImageUrl).startsWith('data:'),
                      isBlob: String(displayImageUrl).startsWith('blob:'),
                      isRawBase: displayImageUrl === canvasImageRef.current,
                      isComposite: displayImageUrl !== canvasImageRef.current,
                    });
                    setIsImageLoading(false);
                    setIsDesktopImageLoaded(true);
                  }}
                  onError={() => {
                    console.error('[rooms:img] FAILED TO LOAD', {
                      activeRoomId: activeRoomIdRef.current,
                      isData: String(displayImageUrl).startsWith('data:'),
                      isBlob: String(displayImageUrl).startsWith('blob:'),
                      isRawBase: displayImageUrl === canvasImageRef.current,
                    });
                    setIsImageLoading(false);
                    setIsDesktopImageLoaded(true);
                  }}
                />
              ) : (
                <div style={{
                  width: '600px',
                  height: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#2a2a2a',
                  color: '#666',
                  fontFamily: 'Avenir, sans-serif',
                  fontSize: '0.9rem',
                }}>
                  No image loaded
                </div>
              )}
              <canvas
                ref={lassoCanvasDesktopRef}
                className="desktop-editor-lasso-canvas"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 10
                }}
              />
              {activeTool && pointerPosition.x !== null && pointerPosition.y !== null && activeTool !== 'recolor' && (
                <div
                  className="desktop-editor-pointer-dot"
                  style={{ left: pointerPosition.x, top: pointerPosition.y }}
                />
              )}
            </div>
          </div>
          {!isMobile && (
            <div className="desktop-editor-right-sidebar">
              {isRightSidebarOpen && rightSidebarTool === 'wallColor' && showWallColorTool && (
                <div className="desktop-editor-right-sidebar-content">
                  <SmartRecolorTool
                    ref={recolorToolRef}
                    isOpen={true}
                    positionMode="sidebar"
                    onClose={() => {
                      setShowWallColorTool(false);
                      setActiveTool(null);
                      setRightSidebarTool(null);
                      setIsRightSidebarOpen(false);
                    }}
                    canvasImage={canvasImage}
                    onApplyWithMask={(previewImage, maskPoints) => {
                      const newLayer = createNewLayer(
                        maskPoints ? { points: maskPoints } : null,
                        previewImage,
                        'Wall Color',
                        []
                      );
                      setLayers(prev => prev.map(l => l.id === newLayer.id ? { ...l, name: 'Wall Color' } : l));
                      setShowWallColorTool(false);
                      setActiveTool(null);
                      setRightSidebarTool(null);
                      setIsRightSidebarOpen(false);
                      debugLog('Wall Color layer created', { layerId: newLayer.id });
                    }}
                    authUser={authUser}
                    updateRemainingCredits={(credits) => setImagesRemaining(credits)}
                    mainCanvasContainerRef={desktopCanvasInnerRef}
                  />
                </div>
              )}
              <div
                className={`desktop-editor-right-sidebar-toggle ${rightSidebarTool === null ? 'greyed' : ''}`}
                onClick={() => {
                  if (rightSidebarTool !== null) {
                    if (isRightSidebarOpen) {
                      setIsRightSidebarOpen(false);
                      setRightSidebarTool(null);
                      setShowWallColorTool(false);
                      setActiveTool(null);
                    } else {
                      setIsRightSidebarOpen(true);
                    }
                  }
                }}
              >
                <span className="toggle-arrow">{isRightSidebarOpen ? '▶' : '◀'}</span>
              </div>
            </div>
          )}
        </div>
        <div className="desktop-editor-statusbar">
          <div className="desktop-editor-statusbar-left">
            <span>{imageSize}</span>
            <span>Layers: {layers.length}</span>
          </div>
          <div className="desktop-editor-statusbar-center">
            {isGenerating ? 'Generating...' : activeTool ? `Tool: ${activeTool === 'pointer' ? 'Cursor' : activeTool === 'penLasso' ? 'Pen Lasso' : activeTool === 'recolor' ? 'Wall Color' : activeTool}` : 'Ready'}
          </div>
          <div className="desktop-editor-statusbar-right">
            <span>{fileName}</span>
            <span>{isDynamicCanvasActive ? '3D On' : '2D'}</span>
          </div>
        </div>
      </div>

      {/* ============================================
         MOBILE LAYOUT
         ============================================ */}
      <div className={`mobile-layout ${uiTheme === 'light' ? 'theme-light' : 'theme-dark'}`}>
        <div className="fixed-header" ref={fixedHeaderRef}>
          <div className="top-nav-bar">
            <div className="dynamic-island-spacer"></div>
          </div>

          <div className="three-column-bar">
            <div className="column project-name">{projectName}</div>
            <div className="column image-size">{imageSize}</div>
            <div className="column file-name">{fileName}</div>
          </div>

          <div className={`header-dropdown-sheet ${isMenuOpen ? 'open' : 'closed'} ${isGenerateMenuOpen ? 'hide' : ''}`}>
            <div className="extended-menu-inner">
              <div className="menu-separator top-separator"></div>
              <div className="menu-buttons-container">
                <button className="menu-button dashboard-button" onClick={handleDashboardClick}>
                  <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2FUI_ICONS%2Fhome.png?alt=media&token=4e911d99-2fe9-4d38-a893-f708cb06b29e" alt="Home" className="menu-icon" />
                </button>
                <button className={`menu-button tools-button ${isSideMenuOpen ? 'active' : ''}`} onClick={handleToolsClick}>
                  <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2FUI_ICONS%2Ftools.png?alt=media&token=c0b17565-491b-4611-8037-2b804834a10b" alt="Tools" className="menu-icon" />
                </button>
                <button className="menu-button" onClick={() => {
                  if (isMenuOpen) {
                    // Tell the top-menu close effect NOT to restore the
                    // previous sub-menu state. Without this, the effect
                    // immediately overwrites setShowLayersMenu(true) with
                    // the stale "layers menu was closed" snapshot and the
                    // popup unmounts ~1 frame after it opens.
                    closeWithoutRestoreRef.current = true;
                    setIsMenuOpen(false);
                    setSliderPosition(0);
                    if (sliderButtonRef.current) sliderButtonRef.current.style.transform = 'translateY(0px)';
                  }
                  setLayersPanelTab('rooms');
                  setShowLayersMenu(true);
                  setMobileLayersPanelVisible(true);
                }}>
                  <TbCubePlus size={22} color="white" />
                </button>
                <button className="menu-button" onClick={() => {
                  if (isMenuOpen) {
                    setIsMenuOpen(false);
                    setSliderPosition(0);
                    if (sliderButtonRef.current) sliderButtonRef.current.style.transform = 'translateY(0px)';
                  }
                  setShowExportOptions(true);
                }}>
                  <CiExport size={24} color="white" />
                </button>
                <button className="menu-button" onClick={() => setShowSettings(true)}>
                  <FiSettings size={20} color="white" />
                </button>
              </div>
              <div className="menu-separator bottom-separator"></div>
            </div>

            <button className="dropdown-toggle-button" onClick={toggleMenu}>
              <span className="dropdown-arrow">{isMenuOpen ? '▲' : '▼'}</span>
            </button>
          </div>
        </div>

        <div
          className={`generate-menu-container ${isGenerateMenuOpen ? 'open' : 'closed'}`}
          style={{
            position: 'absolute',
            top: `${headerBottom - 15}px`,
            paddingTop: '15px',
            left: 0,
            right: 0,
            transform: isGenerateMenuOpen ? 'translateY(0)' : 'translateY(-100%)',
            transition: 'transform 0.35s cubic-bezier(0.34, 1.3, 0.64, 1)',
            zIndex: 19,
            overflow: 'hidden',
            pointerEvents: isGenerateMenuOpen ? 'auto' : 'none',
          }}
        >
          <div className="generate-menu">
            <div className="generate-menu-inner">
              <div className="generate-input-wrapper" style={{ position: 'relative' }}>
                <SpeakAndCreate
                  ref={sacRef}
                  enabled={voiceEnabled}
                  onTranscript={(transcript) => {
                    setGeneratePrompt(prev => prev + (prev ? ' ' : '') + transcript);
                  }}
                  isGenerating={isGenerating}
                  onListeningChange={setIsListening}
                />
                <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="text"
                    className="generate-input"
                    value={generatePrompt}
                    onChange={(e) => setGeneratePrompt(e.target.value)}
                    placeholder={placeholders[placeholderIndex]}
                    disabled={isGenerating}
                    style={{ flex: 1, paddingRight: '20px' }}
                  />
                  <span
                    ref={mobileMeasureRef}
                    style={{
                      position: 'absolute',
                      visibility: 'hidden',
                      whiteSpace: 'pre',
                      font: 'inherit',
                      fontSize: 'inherit',
                      fontFamily: 'inherit',
                      letterSpacing: 'inherit',
                      padding: '0',
                      margin: '0',
                      border: 'none',
                      height: 'auto',
                    }}
                  />
                  <span
                    ref={mobileCursorRef}
                    className="voice-cursor-sim"
                    style={{
                      position: 'absolute',
                      left: '0px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#4594f9',
                      fontSize: 'inherit',
                      fontWeight: '300',
                      animation: 'blink 1s step-end infinite',
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  >
                    |
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`prompt-panel ${isPromptPanelExpanded ? 'expanded' : ''}`}
          >
            <div
              className="prompt-panel-handle"
              onClick={() => setIsPromptPanelExpanded(prev => !prev)}
              onMouseDown={(e) => {
                const startX = e.clientX;
                const startY = e.clientY;
                const handleMouseMove = (moveEvent) => {
                  const deltaX = Math.abs(moveEvent.clientX - startX);
                  const deltaY = Math.abs(moveEvent.clientY - startY);
                  if (deltaX > 5 || deltaY > 5) {
                    setIsDraggingPromptPanel(true);
                    promptPanelDragStartY.current = moveEvent.clientY;
                    promptPanelStartHeight.current = promptPanelHeight;
                    window.removeEventListener('mousemove', handleMouseMove);
                    window.removeEventListener('mouseup', handleMouseUp);
                  }
                };
                const handleMouseUp = () => {
                  window.removeEventListener('mousemove', handleMouseMove);
                  window.removeEventListener('mouseup', handleMouseUp);
                };
                window.addEventListener('mousemove', handleMouseMove);
                window.addEventListener('mouseup', handleMouseUp);
              }}
              onTouchStart={(e) => {
                const touch = e.touches[0];
                const startX = touch.clientX;
                const startY = touch.clientY;
                const handleTouchMove = (moveEvent) => {
                  const touch = moveEvent.touches[0];
                  const deltaX = Math.abs(touch.clientX - startX);
                  const deltaY = Math.abs(touch.clientY - startY);
                  if (deltaX > 5 || deltaY > 5) {
                    setIsDraggingPromptPanel(true);
                    promptPanelDragStartY.current = touch.clientY;
                    promptPanelStartHeight.current = promptPanelHeight;
                    window.removeEventListener('touchmove', handleTouchMove);
                    window.removeEventListener('touchend', handleTouchEnd);
                  }
                };
                const handleTouchEnd = () => {
                  window.removeEventListener('touchmove', handleTouchMove);
                  window.removeEventListener('touchend', handleTouchEnd);
                };
                window.addEventListener('touchmove', handleTouchMove);
                window.addEventListener('touchend', handleTouchEnd);
              }}
            >
              <MdDragHandle size={28} color="#9d9d9dff" style={{ opacity: 1, marginTop: '-10px' }} />
            </div>
            <div className="prompt-panel-content">
              {promptHistory.length === 0 ? (
                <div className="prompt-panel-empty"><span className="prompt-panel-empty-blinking" style={{ fontFamily: 'monospace', fontSize: '8px' }}>Your Input Will Appear Below</span></div>
              ) : (
                <div className="prompt-panel-list">
                  {promptHistory.slice().reverse().map((item, index) => (
                    <div key={item.id || index} className="prompt-panel-item">
                      <span className="prompt-panel-time">{item.timestamp}</span>
                      <span className="prompt-panel-text">{item.text}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="prompt-panel-current">
                <span className="prompt-panel-current-label">Input:</span>
                <span className="prompt-panel-current-text">{generatePrompt || <span className="prompt-panel-placeholder">{placeholders[placeholderIndex]}</span>}</span>
              </div>
            </div>
          </div>

          <div className="generate-dropdown-toggle-container">
            <button className="generate-dropdown-toggle-button" onClick={handleCloseGenerateMenu}>
              <span className="dropdown-arrow">▼</span>
            </button>
            <span className="cancel-label">Back</span>
          </div>
        </div>

        <div
          className={`canvas-section ${isGenerateMenuOpen ? 'generate-menu-visible' : ''}`}
          style={{
            paddingTop: isMenuOpen && !isGenerateMenuOpen ? '80px' : '0px',
            transition: 'padding-top 0.2s ease-out'
          }}
        >
          {isMenuOpen && (
            <div className="floating-button-labels" style={{ top: '92px' }}>
              <span className="floating-label dashboard-label" style={{ animation: 'labelFadeIn 0.25s ease-out both', animationDelay: '0s' }}>Home</span>
              <span className={`floating-label tools-label ${isSideMenuOpen ? 'active' : ''}`} style={{ animation: 'labelFadeIn 0.25s ease-out both', animationDelay: '0.06s' }}>Tools</span>
              <span className="floating-label rooms-label" style={{ animation: 'labelFadeIn 0.25s ease-out both', animationDelay: '0.12s' }}>Rooms</span>
              <span className="floating-label export-label" style={{ animation: 'labelFadeIn 0.25s ease-out both', animationDelay: '0.18s' }}>Export</span>
              <span className="floating-label settings-label" style={{ animation: 'labelFadeIn 0.25s ease-out both', animationDelay: '0.24s' }}>Settings</span>
            </div>
          )}

          <div
            className={`canvas-artboard ${isMenuOpen ? 'menu-open' : ''} ${isGenerateMenuOpen ? 'generate-menu-open' : ''}`}
            ref={canvasRef}
            onMouseMove={handleCanvasMove}
            onTouchMove={(e) => { if (!isMenuOpen) { e.preventDefault(); handleCanvasMove(e); } }}
            onMouseLeave={handleCanvasMouseLeave}
            onTouchCancel={handleCanvasMouseLeave}
            onMouseDown={handleCanvasMouseDown}
            onTouchStart={(e) => { if (!isMenuOpen) { e.preventDefault(); handleCanvasMouseDown(e); } }}
            onMouseUp={handleCanvasMouseUp}
            onTouchEnd={(e) => { if (!isMenuOpen) { e.preventDefault(); handleCanvasMouseUp(); } }}
          >

            <div className={`canvas-blur-overlay ${isMenuOpen ? 'show' : ''}`}></div>

            <div className="canvas-placeholder">
              {isProjectLoading ? (
                <div className="project-loading-panel">
                  <div className="project-loading-progress-bar">
                    <div className="project-loading-progress-fill" />
                  </div>
                  <div className="project-loading-text">{loadStatusText}</div>
                </div>
              ) : displayImageUrl ? (
                <img
                  key={displayImageUrl}
                  src={displayImageUrl}
                  alt="Project canvas"
                  className="canvas-image"
                  onLoad={() => setIsImageLoading(false)}
                  onError={(e) => {
                    console.error('Mobile canvas image failed to load:', e);
                    setIsImageLoading(false);
                  }}
                  draggable={false}
                />
              ) : null}
            </div>

            <canvas ref={lassoCanvasRef} className="lasso-canvas" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 4 }} />

            {activeTool && !isMenuOpen && activeTool !== 'recolor' && (
              <div className="pointer-dot" style={{ display: pointerPosition.x !== null && pointerPosition.y !== null ? 'block' : 'none' }} />
            )}

            {isDynamicCanvasActive && !showDynamicCanvas && (
              <div className="gyroscope-compass">
                <div className="compass-direction top-direction">Y</div>
                <div className="compass-direction left-direction">X</div>
                <div className="gyroscope-icon-container">
                  <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2Fgyro_ico.png?alt=media&token=1be5b496-0b4f-42d4-b762-1e99ccd36cda" alt="Gyroscope" className="gyroscope-icon" />
                </div>
                <div className="compass-direction right-direction">X</div>
                <div className="compass-direction bottom-direction">Y</div>
              </div>
            )}
          </div>

          {isMenuOpen && (
            <div
              className="swipe-up-bar-container"
              style={{
                top: '130px',
                transition: 'top 0.2s ease-out',
                animation: 'swipeBarSlideIn 0.35s ease-out both'
              }}
            >
              <div className="swipe-up-bar" ref={sliderContainerRef}>
                <div className="vertical-text">
                  <div className="rotated-text">
                    <span className="arrows">‹‹</span>
                    <span>Swipe up to hide</span>
                  </div>
                </div>
                <div className="slider-button" ref={sliderButtonRef} onMouseDown={handleDragStart} onTouchStart={handleDragStart}></div>
              </div>
            </div>
          )}

          {isGenerateMenuOpen && !showVariationsMenu && (
            <div className="generate-button-section">
              <div className={`shake-text ${isShaking ? 'shake' : ''}`}>
                {isGenerating ? `Generating${generationDots}` : 'Tap to Generate'}
              </div>
              <div className="generate-button-wrapper" ref={buttonContainerRef}>
                <button
                  className="generate-action-button"
                  onClick={handleGeneratePress}
                  disabled={isGenerating}
                >
                  <canvas ref={starCanvasRef} className="star-canvas"></canvas>
                </button>
                <div className="border-snake">
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible' }}>
                    <rect ref={borderRectRef} x="1.5" y="1.5" rx="18.5" ry="18.5" />
                  </svg>
                </div>
                <canvas ref={trailCanvasRef} className="trail-canvas"></canvas>
              </div>
            </div>
          )}

          <div className={`right-side-menu-container ${isMenuOpen ? 'disabled' : ''}`}>
            <div className={`side-menu-toggle-button ${isSideMenuOpen ? 'open' : ''} ${isMenuOpen ? 'disabled' : ''}`} onClick={toggleSideMenu} style={{ pointerEvents: isMenuOpen ? 'none' : 'auto', opacity: isMenuOpen ? 0.5 : 1 }}>
              <span className="side-menu-arrow">{isSideMenuOpen ? '▶' : '◀'}</span>
            </div>
            {isSideMenuOpen && selectedButton && !isMenuOpen && (
              <div className="selected-label">
                {selectedButton === 'P' && 'Cursor'}
                {selectedButton === 'L1' && 'Pen Lasso'}
                {selectedButton === 'PT' && 'Pen Tool'}
                {selectedButton === 'L2' && 'Layers'}
                {selectedButton === 'T' && 'Trash'}
                {selectedButton === 'RECOLOR' && 'Wall Color'}
                {selectedButton === 'FRAME' && 'Frames Tool'}
              </div>
            )}
            <div className={`side-menu ${isSideMenuOpen ? 'open' : 'closed'} ${isMenuOpen ? 'disabled' : ''}`}>
              <div className="side-menu-inner">
                <div className="side-menu-separator top-side-separator"></div>
                <div className="side-menu-buttons-container">
                  <button className={`side-menu-button ${selectedButton === 'P' ? 'selected' : ''} ${activeTool === 'pointer' ? 'active-tool' : ''}`} onClick={() => handleButtonClick('P')} disabled={isMenuOpen}>
                    <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2FUI_ICONS%2Fcursor.png?alt=media&token=9a8595a0-7b86-45d4-b5bd-262b50a75c4f" alt="Cursor" className="side-menu-icon" />
                  </button>
                  <button className={`side-menu-button ${selectedButton === 'L1' ? 'selected' : ''} ${activeTool === 'penLasso' ? 'active-tool' : ''}`} onClick={() => handleButtonClick('L1')} disabled={isMenuOpen}>
                    <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2FUI_ICONS%2Fpenlasso.png?alt=media&token=fe5e180f-74fd-4009-bc3d-2b60bbd4773b" alt="Pen Lasso" className="side-menu-icon" />
                  </button>
                  <button className={`side-menu-button ${selectedButton === 'PT' ? 'selected' : ''} ${activeTool === 'penTool' ? 'active-tool' : ''}`} onClick={() => handleButtonClick('PT')} disabled={isMenuOpen}>
                    <LuPenTool size={20} />
                  </button>
                  <button className={`side-menu-button ${selectedButton === 'L2' ? 'selected' : ''} ${showLayersMenu ? 'active-tool' : ''}`} onClick={() => {
                    if (showLayersMenu) {
                      setMobileLayersPanelVisible(false);
                      setShowLayersMenu(false);
                      setSelectedButton(null);
                    } else {
                      setShowPictureFrameTool(false);
                      setSelectedButton('L2');
                      setMobileLayersPanelVisible(true);
                      setShowLayersMenu(true);
                    }
                  }} disabled={isMenuOpen}>
                    <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2FUI_ICONS%2Flayers.png?alt=media&token=b291f049-a305-4f9a-89c1-0cfcedf1978f" alt="Layers" className="side-menu-icon" />
                  </button>
                  <button className={`side-menu-button ${selectedButton === 'T' ? 'selected' : ''}`} onClick={() => handleButtonClick('T')} disabled={isMenuOpen}>
                    <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2FUI_ICONS%2Ftrash.png?alt=media&token=cc71b951-6ec2-40db-b219-107a65e2b582" alt="Trash" className="side-menu-icon" />
                  </button>
                  <div className="side-menu-separator" style={{ marginTop: '4px', marginBottom: '4px' }} />
                  <button className={`side-menu-button ${selectedButton === 'RECOLOR' ? 'selected' : ''} ${activeTool === 'recolor' ? 'active-tool' : ''}`} onClick={() => showComingSoonSection('WALL_COLOR_TOOL')} disabled={isMenuOpen}>
                    <TbWallpaper size={20} />
                  </button>
                  <button className={`side-menu-button ${selectedButton === 'FRAME' ? 'selected' : ''} ${showPictureFrameTool ? 'active-tool' : ''}`} onClick={() => {
                    if (showPictureFrameTool) {
                      setShowPictureFrameTool(false);
                      setSelectedButton(null);
                    } else {
                      setShowLayersMenu(false);
                      setMobileLayersPanelVisible(true);
                      setSelectedButton('FRAME');
                      setShowPictureFrameTool(true);
                    }
                  }} disabled={isMenuOpen}>
                    <RiPaintingLine size={20} />
                  </button>
                  <button className={`side-menu-button ${selectedButton === 'COMPASS' ? 'selected' : ''} ${showCompassTools ? 'active-tool' : ''}`} onClick={() => setShowCompassTools(true)} disabled={isMenuOpen}>
                    <LuDraftingCompass size={20} />
                  </button>
                </div>
                <div className="side-menu-separator bottom-side-separator"></div>
              </div>
            </div>
          </div>

          {!showVariationsMenu && !showPictureFrameTool && (
            <div className={`bottom-menu-sheet ${isBottomMenuOpen ? 'open' : ''} ${isMenuOpen ? 'disabled' : ''}`}>
              <div className="bottom-menu-handle" onClick={toggleBottomMenu}>
                <span className="bottom-menu-arrow">{isBottomMenuOpen ? '▼' : '▲'}</span>
              </div>

              <div className="bottom-menu-inner">
                <div className="bottom-menu-spacer top-spacer"></div>
                <div className="bottom-menu-buttons-container">
                  <button
                    className={`bottom-menu-button ${selectedBottomButton === 'DC' ? 'selected' : ''} ${isDynamicCanvasActive ? 'active-tool' : ''}`}
                    onClick={() => showComingSoonSection('DYNAMIC_CANVAS')}
                    disabled={isMenuOpen}
                  >
                    <TbAtom2 size={20} />
                  </button>
                  <button
                    className={`bottom-menu-button ${selectedBottomButton === 'STYLE' ? 'selected' : ''}`}
                    onClick={() => handleBottomButtonClick('STYLE')}
                    disabled={isMenuOpen}
                  >
                    <FiImage size={20} />
                  </button>
                </div>
                <div className="bottom-menu-spacer bottom-spacer"></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Layers Popup */}
      {showLayersMenu && isMobile && (
        <div className={`mobile-layers-popup ${mobileLayersPanelVisible ? 'open' : 'closed'} ${uiTheme === 'light' ? 'theme-light' : 'theme-dark'}`}>
          <div className="mobile-layers-handle" onClick={() => {
            setMobileLayersPanelVisible(!mobileLayersPanelVisible);
            if (showPictureFrameTool) {
              setShowPictureFrameTool(false);
              setSelectedButton(null);
            }
          }}>
            <img src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2FUI_ICONS%2Flayers.png?alt=media&token=b291f049-a305-4f9a-89c1-0cfcedf1978f" alt="Layers" className="mobile-layers-icon" />
            <div className={`mobile-layers-label ${mobileLayersPanelVisible ? 'hidden' : ''}`}>Layers Menu</div>
          </div>
          {mobileLayersPanelVisible && (
          <div className="mobile-layers-content">
            <div className="mobile-layers-header">
              <h3>Layers</h3>
            </div>
            <EditorErrorBoundary label="Mobile Layers Panel">
            <LayersPanel
              layers={layers}
              selectedLayerId={selectedLayerId}
              onSelectLayer={(layerId) => {
                selectLayer(layerId);
                setShowLayersMenu(false);
              }}
              onRenameLayer={(layerId, newName) => {
                setLayers(prev => prev.map(l =>
                  l.id === layerId ? { ...l, name: newName } : l
                ));
                showToast(`Layer renamed to "${newName}"`, 'success', 3000);
              }}
              onDeleteLayer={deleteLayer}
              onShowVariations={(layerId) => {
                handleShowLayerVariations(layerId);
                setShowLayersMenu(false);
              }}
              onToggleVisibility={toggleLayerVisibility}
              onToggleLock={toggleLayerLock}
              onReorderLayers={reorderLayers}
              activeTab={layersPanelTab}
              onTabChange={setLayersPanelTab}
              rooms={rooms}
              activeRoomId={activeRoomId}
              onSelectRoom={handleSelectRoom}
              onAddRoom={handleAddRoom}
              onRenameRoom={handleRenameRoom}
              onDeleteRoom={handleDeleteRoom}
              onSetPrimaryRoom={handleSetPrimaryRoom}
              projectName={projectName}
              authUser={authUser}
              projectId={currentProjectId}
            />
            </EditorErrorBoundary>
          </div>
          )}
          <style>{`
            .mobile-layers-popup {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              z-index: 100;
              background: transparent;
              max-height: 80vh;
              display: flex;
              flex-direction: column;
              align-items: center;
              transition: all 0.3s ease;
              overflow: visible;
            }

            .theme-light .mobile-layers-popup {
              background: transparent;
            }
            .mobile-layers-handle {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 2px;
              padding: 0;
              cursor: pointer;
              background: #595959;
              border-bottom: none;
              user-select: none;
              -webkit-tap-highlight-color: transparent;
              border-radius: 12px 12px 0 0;
              width: 150px;
              height: 28px;
              margin: 0 auto;
              transition: background 0.2s ease, border-radius 0.2s ease;
              position: relative;
              margin-bottom: 0;
              flex-shrink: 0;
            }
            .mobile-layers-popup.open .mobile-layers-handle {
              background: #4594f9;
            }
            .mobile-layers-arrow {
              color: white;
              font-size: 14px;
              line-height: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 18px;
            }
            .mobile-layers-icon {
              width: 15px;
              height: 15px;
              object-fit: contain;
              filter: brightness(0) invert(1);
            }
            .mobile-layers-popup.open .mobile-layers-icon {
              filter: none;
            }
            .mobile-layers-handle.active {
              background: #4594f9;
            }
            .theme-light .mobile-layers-handle {
              background: #aaa;
            }
            .mobile-layers-label {
              position: absolute;
              left: 155px;
              top: 0;
              transform: translateY(-130%);
              background-color: rgba(0, 0, 0, 0.7);
              color: white;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: normal;
              white-space: nowrap;
              z-index: 31;
              pointer-events: none;
              margin-right: 8px;
              line-height: 1.2;
              font-family: Avenir, sans-serif;
              transition: opacity 0.3s ease, transform 0.3s ease;
              opacity: 1;
            }
            .mobile-layers-label::before {
              content: '';
              position: absolute;
              top: calc(100% + 8px);
              left: 50%;
              width: 40px;
              height: 16px;
              border-right: 3px solid #4594f9;
              border-bottom: 3px solid #4594f9;
              border-top: none;
              border-left: none;
              transform: translateX(-100%);
              border-radius: 0;
              box-sizing: border-box;
            }
            .theme-light .mobile-layers-label {
              background-color: rgba(0, 0, 0, 0.7);
              color: white;
            }
            .mobile-layers-content {
              width: 100%;
              flex-shrink: 0;
              overflow-y: auto;
              max-height: calc(80vh - 28px);
              background: #2c2c2c;
              border-top: 1px solid #000000;
              border-radius: 0 0 12px 12px;
              display: flex;
              flex-direction: column;
            }
            .theme-light .mobile-layers-content {
              background: #ffffff;
              border-top-color: #e0e0e0;
            }
            .mobile-layers-header {
              display: flex;
              align-items: center;
              padding: 16px 20px;
              border-bottom: 1px solid #444;
              flex-shrink: 0;
            }
            .theme-light .mobile-layers-header {
              border-bottom-color: #e0e0e0;
            }
            .mobile-layers-header h3 {
              color: white;
              margin: 0;
              font-family: Avenir, sans-serif;
              font-size: 18px;
            }
            .theme-light .mobile-layers-header h3 {
              color: #333;
            }
            .mobile-layers-label {
              opacity: 1;
              transform: translateY(-130%);
              pointer-events: auto;
            }
            .mobile-layers-label.hidden {
              opacity: 0;
              transform: translateY(-50%) translateY(-10px);
              pointer-events: none;
            }

          `}</style>
        </div>
      )}

      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        .editor-container { width: 100%; margin: 0; padding: 0; }
        .editor-container.theme-light { background-color: #FAFAFA; }
        .editor-container.theme-dark { background-color: #2c2c2c; }
        .layout-placeholder { display: none; }

        /* ============================================
           PROJECT LOADING PANEL
           Replaces the old overlay spinner. Shows a solid
           gray background with a progress bar. This never
           sits on top of a partially-loaded canvas — the
           canvas image itself is not mounted until
           isProjectLoading is false.
           ============================================ */
        .project-loading-panel {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 18px;
          width: 100%;
          height: 100%;
          z-index: 2;
        }
        .theme-light .project-loading-panel { background-color: #E8E8E8; }
        .theme-dark .project-loading-panel { background-color: #1a1a1a; }
        .project-loading-progress-bar {
          width: 160px;
          height: 6px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 999px;
          overflow: hidden;
          position: relative;
        }
        .theme-dark .project-loading-progress-bar {
          background: rgba(255, 255, 255, 0.12);
        }
        .project-loading-progress-fill {
          height: 100%;
          width: 40%;
          background: #4594f9;
          border-radius: 999px;
          animation: projectLoadingSlide 1.2s ease-in-out infinite;
        }
        .project-loading-text {
          font-family: Avenir, sans-serif;
          font-size: 12px;
          letter-spacing: 0.3px;
          opacity: 0.75;
        }
        .theme-light .project-loading-text { color: #555; }
        .theme-dark .project-loading-text { color: #ccc; }
        @keyframes projectLoadingSlide {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(250%); }
        }

        .desktop-tooltip {
          position: fixed;
          background: rgba(0,0,0,0.8);
          color: white;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-family: Avenir, sans-serif;
          white-space: nowrap;
          transform: translateX(-50%);
          z-index: 1000;
          pointer-events: none;
          animation: fadeIn 0.15s ease;
        }
        .desktop-tooltip::before {
          content: '';
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid rgba(0,0,0,0.8);
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-5px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        .mobile-layout { display: flex; flex-direction: column; width: 100%; margin: 0; padding: 0; height: 100vh; height: 100dvh; max-height: 100vh; max-height: 100dvh; overflow: hidden; position: relative; }
        .mobile-layout.theme-light { background-color: #FAFAFA; }
        .mobile-layout.theme-dark { background-color: #4f4f4fff; }
        .fixed-header { position: relative; z-index: 20; width: 100%; margin: 0; padding: 0; flex-shrink: 0; }
        .theme-light .fixed-header { background-color: #FFFFFF; border-bottom: 1px solid #E8E8E8; }
        .theme-dark .fixed-header { background-color: #595959; }
        .top-nav-bar {
          width: 100%;
          position: relative;
          z-index: 21;
        }
        .theme-light .top-nav-bar { background-color: #FFFFFF; }
        .theme-dark .top-nav-bar { background-color: #595959; }
        .dynamic-island-spacer { height: 15px; width: 100%; }
        .three-column-bar { display: flex; height: 27px; border-top: 1px solid black; border-bottom: 1px solid black; width: 100%; margin: 0; padding: 0; position: relative; z-index: 22; }
        .theme-light .three-column-bar { background-color: #F5F5F5; border-top: 1px solid #E0E0E0; border-bottom: 1px solid #E0E0E0; }
        .theme-dark .three-column-bar { background-color: #595959; border-top: 1px solid black; border-bottom: 1px solid black; }
        .three-column-bar .column { flex: 1; display: flex; align-items: center; justify-content: center; padding: 0 8px; font-size: 12px; text-align: center; word-break: break-word; }
        .theme-light .three-column-bar .column { color: #333; }
        .theme-dark .three-column-bar .column { color: white; }
        .three-column-bar .column:first-child { border-right: 1px solid black; }
        .theme-light .three-column-bar .column:first-child { border-right: 1px solid #E0E0E0; }
        .three-column-bar .column:nth-child(2) { border-right: 1px solid black; }
        .theme-light .three-column-bar .column:nth-child(2) { border-right: 1px solid #E0E0E0; }
        .header-dropdown-sheet {
          position: absolute;
          top: 100%;
          left: 0;
          z-index: 19;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          transform: translateY(0);
          transition: transform 0.35s cubic-bezier(0.34, 1.3, 0.64, 1);
        }
        .header-dropdown-sheet.open {
          transform: translateY(0);
        }
        .header-dropdown-sheet.closed {
          transform: translateY(calc(-100% + 25px));
        }
        .header-dropdown-sheet.hide {
          opacity: 0;
          pointer-events: none;
          transition: transform 0.35s ease-out, opacity 0.2s ease-out;
        }

        .extended-menu-inner {
        border-bottom:solid 1px;
          padding: 10px 0;
          width: 100%;
          position: relative;
          z-index: 2;
        }
        .theme-light .extended-menu-inner { background-color: #F5F5F5; border-bottom: 1px solid #E8E8E8; }
        .theme-dark .extended-menu-inner { background-color: #4f4f4fff; }

        .menu-separator { height: 1px; background-color: #666; margin: 0 20px; }
        .theme-light .menu-separator { background-color: #E0E0E0; }
        .top-separator { margin-bottom: 10px; }
        .bottom-separator { margin-top: 10px; }
        .menu-buttons-container { display: flex; justify-content: center; gap: 8px; padding: 0 12px; flex-wrap: nowrap; width: 100%; }
        .menu-button { border: 1px solid #4392f9; border-radius: 50px; cursor: pointer; display: flex; align-items: center; justify-content: center; height: 44px; padding: 0 8px; transition: all 0.2s ease; flex: 1; min-width: 0; background: #595959; color: white; }
        .theme-light .menu-button { background-color: #F0F0F0; }
        .theme-dark .menu-button { background-color: #5e5e5eff; }
        .theme-light .menu-button:hover, .theme-light .menu-button.active { border-color: #4594F9; background-color: #EEF3FF; }
        .theme-dark .menu-button:hover, .theme-dark .menu-button:active, .theme-dark .menu-button.active { border-color: #4594f9; outline: none; }
        .menu-icon { width: 24px; height: 24px; object-fit: contain; }
        .dashboard-button { flex: 0.7; }
        .tools-button, .export-button, .settings-button { flex: 1; }

        .dropdown-toggle-button {
          width: 120px;
          height: 25px;
          background-color: #4594f9;
          border: none;
          border-radius: 0 0 15px 15px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s ease, transform 0.2s ease-out 0.20s;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }
        .header-dropdown-sheet.open .dropdown-toggle-button {
          transform: translateY(-25px);
          opacity: 0;
          pointer-events: none;
          transition: transform 0.2s ease-out, opacity 0s;
        }
        .dropdown-toggle-button:hover { background-color: #3a7ed4; }
        .dropdown-arrow { font-size: 16px; color: white; line-height: 1; }
        .generate-menu-container {
          display: flex;
          flex-direction: column;
          background-color: #4f4f4fff;
          flex-shrink: 0;
          overscroll-behavior: none;
          touch-action: pan-y pinch-zoom;
        }
        .theme-light .generate-menu-container {
          background-color: #F5F5F5;
        }
        .generate-menu-container.open {
          pointer-events: auto;
        }
        .theme-light .generate-menu-container { background-color: #F5F5F5; }
        .theme-dark .generate-menu-container { background-color: #4f4f4fff; }
        .generate-menu-container.closed { max-height: 0; overflow: hidden; padding: 0 !important; margin: 0 !important; }
        .generate-menu-container.closed {
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
        }
        .generate-menu-container.closed {
          pointer-events: none;
        }
        .theme-light .generate-menu-container.closed { border-bottom: solid 0px #E0E0E0; }
        .theme-dark .generate-menu-container.closed { border-bottom: solid 0px black; }
        .generate-menu-container.open { overflow: visible; }
        .theme-light .generate-menu-container.open { max-height: none; height: auto; flex: 0 0 auto; background-color: transparent; padding-bottom: 0; margin-bottom: 0; }
        .theme-dark .generate-menu-container.open { max-height: none; height: auto; flex: 0 0 auto; background-color: transparent; padding-bottom: 0; margin-bottom: 0; }
        .generate-menu {
          width: 100%;
          position: relative;
        }
        .generate-menu::before {
          content: '';
          display: block;
          position: absolute;
          top: -60px;
          left: 0;
          right: 0;
          height: 60px;
        }
        .theme-light .generate-menu { background-color: #F5F5F5; }
        .theme-light .generate-menu::before { background-color: #F5F5F5; }
        .theme-dark .generate-menu { background-color: #4f4f4fff; }
        .theme-dark .generate-menu::before { background-color: #4f4f4fff; }
        .generate-menu-inner { padding: 0px 0; width: 100%; }
        .generate-input-wrapper { padding: 0 16px; display: flex; justify-content: center; align-items: center; height: 50px; }
        .generate-input { width: 100%; height: 30px; border: 1px solid #888; border-radius: 8px; font-family: monospace; font-size: 16px; padding: 0 12px; outline: none; transition: border-color 0.2s ease; }
        .theme-light .generate-input { background-color: #FFFFFF; border: 1px solid #E0E0E0; color: #333; }
        .theme-dark .generate-input { background-color: #414141; color: white; }
        .theme-light .generate-input::placeholder { color: #AAA; }
        .theme-dark .generate-input::placeholder { color: rgba(255, 255, 255, 1); animation: blink 1.5s infinite; }
        .generate-input:focus { border-color: #4594f9; }
        .generate-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .theme-dark .generate-input::placeholder { font-family: monospace; font-size: 11px; }
        .generate-menu-container > .generate-dropdown-toggle-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          z-index: 26;
          margin: 0;
          padding: 0px 0 6px 0;
          background: transparent;
          border: none;
          height: auto;
          line-height: normal;
        }

        .generate-menu-container.closed .generate-dropdown-toggle-container { display: none; }
        .generate-dropdown-toggle-button { width: 120px; height: 25px; background-color: #4594f9; border: none; border-radius: 0 0 15px 15px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background-color 0.2s ease; position: relative; top: 0; margin: 0; padding: 0; flex-shrink: 0; min-height: 0; line-height: 0; }
        .generate-dropdown-toggle-button:hover { background-color: #3a7ed4; }
        .cancel-label { color: white; font-size: 10px; font-family: monospace; margin: 0; padding: 0; text-align: center; opacity: 0.8; line-height: 1.2; min-height: 0; transform: translateY(5px); }
        .theme-light .cancel-label { color: #666; }
        .generate-button-section { position: absolute; bottom: 80px; left: 0; right: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 20; pointer-events: auto; }
        .shake-text { font-family: monospace; font-weight:500; font-size: 12px; margin-bottom: 8px; text-align: center; letter-spacing: -0.5px; display: inline-block; animation: gentleRock 4s ease-in-out infinite; }
        .theme-light .shake-text { color: #333; }
        .theme-dark .shake-text { color: white; }
        .shake-text.shake { animation: shakeText 1.5s ease-in-out infinite; }
        @keyframes shakeText { 0% { transform: translateX(0px); } 25% { transform: translateX(-8px); } 75% { transform: translateX(8px); } 100% { transform: translateX(0px); } }
        @keyframes gentleRock { 0% { transform: translateX(0px); } 25% { transform: translateX(-6px); } 75% { transform: translateX(6px); } 100% { transform: translateX(0px); } }
        .generate-button-wrapper { position: relative; display: inline-block; }
        .generate-action-button { background: #8a8a8ab0; backdrop-filter: blur(3px); border: none; width: 200px; height: 50px; font-size: 18px; font-weight: 500; color: white; cursor: pointer; border-radius: 20px; display: block; position: relative; overflow: hidden; }
        .generate-action-button:hover { background: #7a7a7a; }
        .generate-action-button:disabled { opacity: 0.5; cursor: not-allowed; }
        .star-canvas { position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: block; pointer-events: none; }
        .border-snake { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 2; }
        .border-snake svg { width: 100%; height: 100%; display: block; overflow: visible; }
        .border-snake rect { fill: none; stroke: #318bff; stroke-width: 2.5px; }
        .trail-canvas { position: absolute; top: -40px; left: -40px; width: calc(100% + 80px); height: calc(100% + 80px); pointer-events: none; z-index: 3; }
        .canvas-section { position: relative; width: 100%; z-index: 1; flex: 1; overflow: visible; min-height: 0; display: flex; flex-direction: column; margin-bottom: 0; }

        .canvas-artboard { width: 100%; flex: 1; position: relative; overflow: hidden; transition: none; touch-action: none; min-height: 0; }
        .theme-light .canvas-artboard { background-color: #E8E8E8; }
        .theme-dark .canvas-artboard { background-color: #1a1a1a; }

        .canvas-blur-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0);
          backdrop-filter: blur(0px);
          z-index: 5;
          pointer-events: none;
          transition: backdrop-filter 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1), background-color 0.4s ease;
        }
        .canvas-blur-overlay.show {
          backdrop-filter: blur(8px);
          background-color: rgba(0, 0, 0, 0.15);
          pointer-events: auto;
        }
        .canvas-placeholder { color: white; text-align: center; padding: 0; position: relative; z-index: 1; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .theme-light .canvas-placeholder { background-color: #E8E8E8; color: #333; }
        .theme-dark .canvas-placeholder { background-color: #1a1a1a; color: white; }

        .no-image-placeholder { width: 100%; height: 100%; }
        .canvas-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          top: 0;
          left: 0;
          filter: none;
          user-select: none;
          -webkit-user-select: none;
          -webkit-touch-callout: none;
          pointer-events: auto;
          -webkit-user-drag: none;
          touch-action: none;
        }
        .lasso-canvas { image-rendering: crisp-edges; }
        .loading-placeholder {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #888;
          font-family: monospace;
          font-size: 12px;
          z-index: 3;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }
        .theme-light .loading-placeholder { color: #666; }
        .theme-dark .loading-placeholder { color: #aaa; }
        .canvas-loading-progress-bar {
          width: 120px;
          height: 6px;
          background: rgba(0, 0, 0, 0.45);
          border-radius: 999px;
          overflow: hidden;
          position: relative;
        }
        .theme-light .canvas-loading-progress-bar {
          background: rgba(0, 0, 0, 0.15);
        }
        .canvas-loading-progress-fill {
          height: 100%;
          width: 40%;
          background: #4594f9;
          border-radius: 999px;
          animation: canvasLoadingSlide 1.2s ease-in-out infinite;
        }
        @keyframes canvasLoadingSlide {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(250%); }
        }
        .composite-pending-indicator {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 6px 14px;
          border-radius: 20px;
          font-family: monospace;
          font-size: 11px;
          z-index: 6;
          pointer-events: none;
          animation: blink 1.5s ease-in-out infinite;
        }
        .pointer-dot {
          position: absolute;
          width: 16px;
          height: 16px;
          background-color: white;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(0, 0, 0, 0.2);
          z-index: 20;
          pointer-events: none;
          transition: none;
          will-change: left, top;
        }
        .gyroscope-compass {
          position: absolute;
          bottom: 90px;
          left: 35px;
          width: 80px;
          height: 80px;
          z-index: 4;
          pointer-events: none;
        }
        .gyroscope-icon-container {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 48px;
          height: 48px;
          pointer-events: none;
          z-index: 2;
        }
        .gyroscope-icon { width: 100%; height: 100%; object-fit: contain; }
        .compass-direction {
          position: absolute;
          color: rgba(255, 255, 255, 0.5);
          font-size: 12px;
          font-weight: normal;
          pointer-events: none;
          white-space: nowrap;
          font-family: monospace;
          letter-spacing: 0.5px;
          z-index: 3;
          background: transparent;
          padding: 0;
          border-radius: 0;
          text-shadow: none;
        }
        .top-direction { top: -10px; left: 50%; transform: translateX(-50%); }
        .bottom-direction { bottom: -10px; left: 50%; transform: translateX(-50%); }
        .left-direction { left: -10px; top: 50%; transform: translateY(-50%); }
        .right-direction { right: -10px; top: 50%; transform: translateY(-50%); }
        .floating-button-labels {
          display: flex;
          justify-content: center;
          gap: 2px;
          padding: 0 9px;
          flex-wrap: nowrap;
          position: absolute;
          top: 7px;
          left: 0;
          right: 0;
          z-index: 25;
          pointer-events: none;
          transition: top 0.2s ease-out;
        }
        .floating-label {
          font-size: 12px;
          text-align: center;
          white-space: nowrap;
          flex: 1;
          transition: color 0.2s ease;
        }
        .theme-light .floating-label { color: #333; }
        .theme-dark .floating-label { color: white; }
        .floating-label.active { color: #ffffffff; }
        .dashboard-label { flex: 0.7; }
        .tools-label, .rooms-label, .export-label, .settings-label { flex: 1; }
        .swipe-up-bar-container {
          position: absolute;
          left: 0;
          top: 50px;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 15;
        }
        .swipe-up-bar {
          position: absolute;
          left: 20px;
          top: 0;
          width: 40px;
          height: 250px;
          border: solid 1px #4392f9;
          border-radius: 30px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          padding: 15px 0;
          pointer-events: auto;
          overflow: hidden !important;
        }
        .theme-light .swipe-up-bar { background-color: rgba(240, 240, 240, 0.9); }
        .theme-dark .swipe-up-bar { background-color: rgba(60, 60, 60, 0.82); }
        .slider-button {
          width: 28px;
          height: 28px;
          background-color: #4392f9;
          border-radius: 25px;
          cursor: grab;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: none;
          margin-bottom: 10px;
          will-change: transform;
          position: relative;
        }
        .slider-button::before {
          content: '';
          position: absolute;
          top: -20px;
          left: -20px;
          right: -20px;
          bottom: -20px;
          background: transparent;
        }
        .slider-button:active { cursor: grabbing; }
        .vertical-text { flex: 1; display: flex; align-items: center; justify-content: center; }
        .rotated-text {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: avenir-lt-pro, sans-serif;
          font-size: 13px;
          font-weight: normal;
          white-space: nowrap;
          writing-mode: vertical-rl;
          text-orientation: mixed;
          transform: rotate(0deg);
          animation: blink 3s ease-in-out infinite, floatVertical 6s ease-in-out infinite;
        }
        .theme-light .rotated-text { color: #333; }
        .theme-dark .rotated-text { color: white; }
        .rotated-text span { display: inline-block; line-height: 1.2; }
        .arrows { font-size: 16px; display: inline-block; }
        .right-side-menu-container {
          position: absolute;
          right: 0;
          top: 45%;
          transform: translateY(-50%);
          z-index: 30;
          display: flex;
          align-items: center;
        }
        .right-side-menu-container.disabled { z-index: 25; }
        .side-menu-toggle-button {
          width: 24px;
          height: 140px;
          background-color: #4594f9;
          border: none;
          border-radius: 15px 0 0 15px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          right: 0;
          transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
        }
        .side-menu-toggle-button:hover { background-color: #3a7ed4; }
        .side-menu-toggle-button.disabled { cursor: not-allowed; }
        .side-menu-arrow { font-size: 16px; color: white; font-weight: bold; }
        .selected-label {
          position: absolute;
          top: -35px;
          right: 0%;
          transform: translateX(0);
          background-color: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: normal;
          white-space: nowrap;
          z-index: 31;
          pointer-events: none;
          margin-right: 8px;
          line-height: 1.2;
        }
        .side-menu { overflow: hidden; transition: width 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1); position: relative; width: 0; height: 540px; border-radius: 25px 0 0 25px; }
        .theme-light .side-menu { background-color: #FFFFFF; border: 1px solid #E8E8E8; border-right: none; }
        .theme-dark .side-menu { background-color: #333333; }
        .side-menu.open { width: 65px; }
        .side-menu.closed { width: 0; }
        .side-menu.disabled { opacity: 0.5; }
        .side-menu-inner {
          padding: 16px 0 12px 0;
          width: 65px;
          position: relative;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .side-menu-separator { height: 1px; margin: 0 15px; flex-shrink: 0; }
        .theme-light .side-menu-separator { background-color: #E0E0E0; }
        .theme-dark .side-menu-separator { background-color: #666; }
        .top-side-separator { margin-bottom: 0px; flex-shrink: 0; }
        .bottom-side-separator { margin-top: 0px; flex-shrink: 0; }
        .side-menu-buttons-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 10px;
          padding: 0 12px;
          flex: 1;
        }
        .side-menu-button {
          background-color: transparent;
          border: 2px solid transparent;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 42px;
          width: 100%;
          transition: all 0.2s ease;
          margin: 0 auto;
          padding: 0;
          color: inherit;
          flex-shrink: 0;
        }
        .theme-dark .side-menu-button {
          color: white;
        }
        .theme-light .side-menu-button {
          color: #333;
        }
        .side-menu-button:hover:not(.selected):not(.active-tool), .side-menu-button:active:not(.selected):not(.active-tool) { border-color: #4594f9; outline: none; }
        .side-menu-button.selected { border-color: #4594f9; background: transparent; }
        .side-menu-button.active-tool { border-color: #4594f9; background: transparent; }
        .side-menu-button.selected:hover,
        .side-menu-button.active-tool:hover {
          border-color: #4594f9;
          background: transparent;
          opacity: 1;
        }
        .side-menu-button.selected:active,
        .side-menu-button.active-tool:active {
          border-color: #4594f9;
          background: transparent;
          opacity: 1;
        }
        .side-menu-button:disabled { cursor: not-allowed; opacity: 0.5; }
        .side-menu-icon { width: 24px; height: 24px; object-fit: contain; }

        .theme-light .side-menu-icon {
          filter: brightness(0) invert(1);
        }

        .theme-light .side-menu-button.selected .side-menu-icon {
          filter: brightness(0) invert(1);
        }

        .theme-light .side-menu-button:hover .side-menu-icon {
          filter: brightness(0) invert(1);
        }

        .bottom-menu-sheet {
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translate(-50%, calc(100% - 28px));
          width: 220px;
          background: transparent;
          border-radius: 0;
          transition: transform 0.3s ease-out;
          z-index: 30;
          display: flex;
          flex-direction: column;
          align-items: center;
          pointer-events: auto;
        }

        .bottom-menu-sheet.open {
          transform: translate(-50%, 0);
        }
        .bottom-menu-sheet.disabled {
          opacity: 0.5;
          pointer-events: none;
        }

        .bottom-menu-handle {
          width: 150px;
          height: 28px;
          background: #595959;
          border-radius: 15px 15px 0 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
          border: none;
        }
        .bottom-menu-handle:hover {
          background: #666;
        }
        .bottom-menu-arrow {
          font-size: 16px;
          font-weight: bold;
          color: white;
        }

        .bottom-menu-inner {
          width: 100%;
          padding: 12px 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #333;
          border-radius: 12px 12px 0 0;
          border: none;
        }
        .bottom-menu-spacer {
          width: 1px;
          height: 30px;
          background: #666;
          margin: 0 15px;
        }
        .theme-light .bottom-menu-spacer { background: #E0E0E0; }

        .bottom-menu-buttons-container {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 16px;
          flex: 1;
          justify-content: center;
        }

        .bottom-menu-button {
          background: transparent;
          border: 2px solid transparent;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 48px;
          width: 70px;
          transition: all 0.2s ease;
          margin: 0;
          padding: 0;
          color: #ccc;
        }
        .theme-light .bottom-menu-button { color: #666; }
        .bottom-menu-button:hover:not(.selected):not(.active-tool),
        .bottom-menu-button:active:not(.selected):not(.active-tool) {
          border-color: #4594f9;
          outline: none;
          background: rgba(69,148,249,0.1);
        }
        .bottom-menu-button.selected {
          border-color: #4594f9;
          background: rgba(69,148,249,0.15);
        }
        .bottom-menu-button.active-tool {
          border-color: #4594f9;
          background: rgba(69,148,249,0.1);
        }
        .bottom-menu-button.selected:hover,
        .bottom-menu-button.active-tool:hover {
          border-color: #4594f9;
          background: rgba(69,148,249,0.15);
        }
        .bottom-menu-button.selected:active,
        .bottom-menu-button.active-tool:active {
          border-color: #4594f9;
          background: rgba(69,148,249,0.15);
        }
        .bottom-menu-button:disabled {
          cursor: not-allowed;
          opacity: 0.4;
        }

        .theme-light .bottom-menu-sheet {
          background: transparent;
        }
        .theme-light .bottom-menu-handle {
          background: #aaa;
          color: #333;
          border: none;
        }
        .theme-light .bottom-menu-handle:hover {
          background: #999;
        }
        .theme-light .bottom-menu-arrow {
          color: #333;
        }
        .theme-light .bottom-menu-inner {
          background: #ddd;
        }
        .theme-light .bottom-menu-button svg {
          color: #333;
        }

        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          backdrop-filter: blur(20px) opacity(100);
          opacity: 0;
          transition: opacity 0.3s ease;
          padding-top: env(safe-area-inset-top);
          padding-bottom: env(safe-area-inset-bottom);
          pointer-events: none;
        }
        .loading-overlay.show { opacity: 1; pointer-events: auto; }
        .loading-text { color: white; font-family: 'Avenir', sans-serif; font-size: 1.2rem; margin-bottom: 20px; text-align: center; }
        .loading-bar-container { width: 80%; max-width: 300px; height: 6px; background-color: #333; border-radius: 3px; overflow: hidden; }
        .loading-bar { height: 100%; background-color: #488effff; border-radius: 3px; transition: width 0.3s ease; width: 0%; }
        .loading-percentage { color: white; font-family: 'Avenir', sans-serif; font-size: 1rem; margin-top: 10px; text-align: center; }
        @keyframes blink { 0%, 100% { opacity: 0.7; } 50% { opacity: 0.2; } }
        @keyframes floatHorizontal { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(-10px); } }
        @keyframes floatVertical { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes labelFadeIn {
          0% { opacity: 0; transform: translateY(-4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes swipeBarSlideIn {
          0% { opacity: 0; transform: translateX(-30px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @media (max-width: 767px) { .layout-placeholder { display: none; } .mobile-layout { display: flex; } .desktop-tablet-editor-layout { display: none !important; } }
        @media (min-width: 768px) { .layout-placeholder { display: none; } .mobile-layout { display: none; } .desktop-tablet-editor-layout { display: flex; } }

        html, body { width: 100%; margin: 0; padding: 0; overflow-x: hidden; overflow-y: hidden; position: fixed; }
        .theme-light html, .theme-light body { background-color: #FAFAFA; }
        .theme-dark html, .theme-dark body { background-color: #595959; }
        body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; position: fixed; top: 0; left: 0; right: 0; bottom: 0; overflow: hidden; display: flex; place-items: center; min-width: 320px; min-height: 100vh; }
        button:hover { background: #3c3c3cff; }
        .theme-light button:hover { background: #e0e0e0; }
        @media (max-width: 767px) { body { display: block !important; place-items: normal !important; min-width: auto !important; min-height: auto !important; } }

        @media (min-width: 768px) {
          .desktop-tablet-editor-layout {
            display: flex;
            flex-direction: column;
            width: 100vw;
            height: 100vh;
            overflow: hidden;
            position: relative;
          }
          .theme-light .desktop-tablet-editor-layout { background: #FAFAFA; }
          .theme-dark .desktop-tablet-editor-layout { background: #1a1a1a; }

          .desktop-editor-topbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 24px;
            border-bottom: 1px solid;
            z-index: 20;
            flex-shrink: 0;
            min-height: 56px;
            gap: 16px;
          }
          .theme-light .desktop-editor-topbar { background: #FFFFFF; border-bottom-color: #E8E8E8; }
          .theme-dark .desktop-editor-topbar { background: #3a3a3a; border-bottom-color: #555; }

          .desktop-editor-topbar-left {
            display: flex;
            align-items: center;
            gap: 16px;
            flex-shrink: 0;
          }

          .desktop-editor-back-btn {
            display: flex;
            align-items: center;
            gap: 6px;
            border: none;
            border-radius: 20px;
            padding: 8px 16px;
            font-family: 'Avenir', sans-serif;
            font-size: 0.8rem;
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;
          }
          .theme-light .desktop-editor-back-btn { background: #F0F0F0; color: #333; }
          .theme-dark .desktop-editor-back-btn { background: #555; color: #fff; }
          .theme-light .desktop-editor-back-btn:hover { background: #E0E0E0; }
          .theme-dark .desktop-editor-back-btn:hover { background: #666; }

          .desktop-editor-back-btn svg {
            width: 14px;
            height: 14px;
          }

          .desktop-editor-project-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-width: 0;
          }

          .desktop-editor-project-name {
            font-family: 'Avenir', sans-serif;
            font-weight: 600;
            font-size: clamp(0.85rem, 1.2vw, 1.1rem);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            letter-spacing: -0.3px;
          }
          .theme-light .desktop-editor-project-name { color: #222; }
          .theme-dark .desktop-editor-project-name { color: #fff; }

          .desktop-editor-project-meta {
            display: flex;
            gap: 16px;
            font-family: 'Avenir', sans-serif;
            font-size: clamp(0.65rem, 0.9vw, 0.75rem);
            white-space: nowrap;
          }
          .theme-light .desktop-editor-project-meta { color: #999; }
          .theme-dark .desktop-editor-project-meta { color: #aaa; }

          .desktop-editor-topbar-center {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 1;
            justify-content: center;
            max-width: 560px;
            min-width: 280px;
          }

          .desktop-editor-undo-redo-group {
            display: flex;
            align-items: center;
            gap: 6px;
            background: #3a3a3a;
            border-radius: 12px;
            padding: 4px 8px;
          }
          .theme-light .desktop-editor-undo-redo-group {
            background: #f0f0f0;
          }

          .desktop-editor-undo-btn, .desktop-editor-redo-btn {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
          }
          .theme-dark .desktop-editor-undo-btn,
          .theme-dark .desktop-editor-redo-btn {
            background: #555;
            color: #ccc;
          }
          .theme-light .desktop-editor-undo-btn,
          .theme-light .desktop-editor-redo-btn {
            background: #e8e8e8;
            color: #666;
          }
          .desktop-editor-undo-btn:hover:not(:disabled),
          .desktop-editor-redo-btn:hover:not(:disabled) {
            background: #4594f9;
            color: white;
          }
          .desktop-editor-undo-btn:disabled,
          .desktop-editor-redo-btn:disabled {
            opacity: 0.4;
            cursor: not-allowed;
          }

          .desktop-editor-generate-input-wrapper {
            display: flex;
            align-items: center;
            border: 1px solid;
            border-radius: 8px;
            overflow: hidden;
            width: 100%;
            max-width: 450px;
            transition: border-color 0.2s ease;
          }
          .theme-light .desktop-editor-generate-input-wrapper { background: #F5F5F5; border-color: #E0E0E0; }
          .theme-dark .desktop-editor-generate-input-wrapper { background: #444; border-color: #555; }
          .desktop-editor-generate-input-wrapper:focus-within {
            border-color: #4594F9;
          }
          .theme-light .desktop-editor-generate-input-wrapper:focus-within { background: #FFFFFF; }

          .desktop-editor-generate-input {
            flex: 1;
            background: transparent;
            border: none;
            outline: none;
            padding: 8px 14px;
            font-family: 'Roboto Mono', monospace;
            font-size: clamp(0.75rem, 1vw, 0.9rem);
          }
          .theme-light .desktop-editor-generate-input { color: #333; }
          .theme-dark .desktop-editor-generate-input { color: #eee; }

          .desktop-editor-generate-input::placeholder {
            font-size: clamp(0.65rem, 0.9vw, 0.75rem);
          }
          .theme-light .desktop-editor-generate-input::placeholder { color: #AAA; }
          .theme-dark .desktop-editor-generate-input::placeholder { color: #888; }

          .desktop-editor-generate-input:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .desktop-editor-generate-btn {
            border: none;
            padding: 8px 16px;
            cursor: pointer;
            color: white;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            border-radius: 8px;
            font-weight: 500;
          }
          .theme-light .desktop-editor-generate-btn {
            background: #4594F9;
            color: white;
            border: 1px solid #4594F9;
          }
          .theme-dark .desktop-editor-generate-btn {
            background: #4594f9;
            color: white;
            border: 1px solid #4594f9;
          }
          .theme-light .desktop-editor-generate-btn:hover {
            background: #5998FF;
            border-color: #5998FF;
          }
          .theme-dark .desktop-editor-generate-btn:hover {
            background: #3a7ed4;
            border-color: #3a7ed4;
          }
          .desktop-editor-generate-btn:disabled {
            background: #999;
            border-color: #999;
            cursor: not-allowed;
            opacity: 0.6;
          }

          .desktop-editor-topbar-right {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-shrink: 0;
          }

          .desktop-editor-tool-btn {
            border: 1px solid transparent;
            border-radius: 10px;
            width: clamp(36px, 4vw, 44px);
            height: clamp(36px, 4vw, 44px);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            padding: 0;
            position: relative;
          }
          .theme-light .desktop-editor-tool-btn { background: #F5F5F5; color: #666; }
          .theme-dark .desktop-editor-tool-btn { background: #555; color: #ccc; }
          .theme-light .desktop-editor-tool-btn:hover:not(.active) { background: #EEF3FF; border-color: #4594F9; color: #4594F9; }
          .theme-dark .desktop-editor-tool-btn:hover:not(.active) { background: #666; border-color: #4594f9; color: #fff; }
          .desktop-editor-tool-btn.active { border-color: #4594F9; }
          .theme-light .desktop-editor-tool-btn.active { background: #4594F9; color: white; }
          .theme-dark .desktop-editor-tool-btn.active { background: #4594F9; color: white; }
          .desktop-editor-tool-btn.active:hover {
            border-color: #4594F9;
            background: #4594F9;
            color: white;
          }
          .theme-light .desktop-editor-tool-btn.active:hover {
            background: #4594F9;
            color: white;
          }
          .theme-dark .desktop-editor-tool-btn.active:hover {
            background: #4594F9;
            color: white;
          }
          .desktop-editor-tool-btn:active:not(.active) {
            transform: scale(0.95);
          }
          .desktop-editor-tool-btn.active:active {
            transform: scale(0.95);
          }
          .desktop-editor-tool-btn:active {
            transform: scale(0.95);
          }
          .theme-light .desktop-editor-tool-btn.active { background: #4594F9; color: white; }
          .theme-dark .desktop-editor-tool-btn.active { background: #4594f9; color: white; }

          .desktop-editor-tool-btn svg, .desktop-editor-tool-btn img {
            width: clamp(18px, 2vw, 22px);
            height: clamp(18px, 2vw, 22px);
            object-fit: contain;
          }

          .theme-light .desktop-editor-tool-btn img {
            filter: brightness(0) invert(1);
          }

          .theme-light .desktop-editor-tool-btn.active img {
            filter: brightness(0) invert(1);
          }

          .theme-light .desktop-editor-tool-btn:hover img {
            filter: brightness(0) invert(1);
          }

          .desktop-editor-tool-separator {
            width: 1px;
            height: 24px;
          }
          .theme-light .desktop-editor-tool-separator { background: #E0E0E0; }
          .theme-dark .desktop-editor-tool-separator { background: #555; }

          .desktop-editor-main {
            display: flex;
            flex: 1;
            min-height: 0;
            position: relative;
          }

          .desktop-editor-canvas-area {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
            min-width: 0;
            padding: 20px;
            margin-left: 0;
            width: 100%;
            transition: padding-left 0.3s ease;
          }
          .theme-light .desktop-editor-main { background: #FAFAFA; }
          .theme-dark .desktop-editor-main { background: #1a1a1a; }

          .desktop-editor-sidebar.closed {
            background: transparent !important;
          }

          .desktop-editor-sidebar {
            display: flex;
            position: absolute;
            left: 0;
            top: 0;
            flex-direction: row;
            align-items: center;
            flex-shrink: 0;
            z-index: 20;
            overflow: visible;
            height: 100%;
          }

          .desktop-editor-sidebar-content {
            width: 252px;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            flex-shrink: 0;
            height: 100%;
            background: inherit;
            transition: transform 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
            transform: translateX(0);
          }

          .desktop-editor-sidebar.closed .desktop-editor-sidebar-content {
            transform: translateX(-252px);
          }

          .desktop-editor-sidebar.open .desktop-editor-sidebar-content {
            transform: translateX(0);
          }

          .theme-light .desktop-editor-sidebar .desktop-editor-sidebar-content { background: #FFFFFF; border-right: 1px solid #E8E8E8; }
          .theme-dark .desktop-editor-sidebar .desktop-editor-sidebar-content { background: #333; border-right: 1px solid #555; }

          .desktop-editor-sidebar-toggle {
            width: 28px;
            height: 180px;
            background-color: #4594f9;
            border-radius: 0 12px 12px 0;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 25;
            transition: all 0.2s ease;
            flex-shrink: 0;
            margin-left: 0;
            box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
          }
          .desktop-editor-sidebar-toggle:hover {
            background-color: #3a7ed4;
          }
          .desktop-editor-right-sidebar {
            display: flex;
            position: absolute;
            right: 0;
            top: 0;
            flex-direction: row-reverse;
            align-items: center;
            flex-shrink: 0;
            z-index: 20;
            overflow: visible;
            height: 100%;
            pointer-events: none;
          }

          .desktop-editor-right-sidebar-content {
            width: 260px;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            flex-shrink: 0;
            height: 100%;
            background: inherit;
            transition: transform 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
            transform: translateX(0);
            pointer-events: auto;
          }

          .theme-light .desktop-editor-right-sidebar-content {
            background: #FFFFFF;
            border-left: 1px solid #E8E8E8;
          }

          .theme-dark .desktop-editor-right-sidebar-content {
            background: #333;
            border-left: 1px solid #555;
          }

          .desktop-editor-right-sidebar-toggle {
            width: 28px;
            height: 180px;
            background-color: #4594f9;
            border-radius: 12px 0 0 12px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 25;
            transition: all 0.2s ease;
            flex-shrink: 0;
            pointer-events: auto;
            box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
          }

          .desktop-editor-right-sidebar-toggle.greyed {
            background-color: #888;
            cursor: default;
          }

          .desktop-editor-right-sidebar-toggle:hover:not(.greyed) {
            background-color: #3a7ed4;
          }

          .desktop-editor-right-sidebar-toggle.greyed:hover {
            background-color: #888;
          }

          .toggle-arrow {
            color: white;
            font-size: 16px;
            font-weight: bold;
          }

          .desktop-editor-sidebar.open .desktop-editor-sidebar-toggle {
            border-radius: 0 12px 12px 0;
          }
          .desktop-editor-sidebar-toggle:hover {
            background-color: #3a7ed4;
          }
          .toggle-arrow {
            color: white;
            font-size: 16px;
            font-weight: bold;
          }

          .desktop-editor-sidebar.closed .desktop-editor-sidebar-content {
            display: none;
          }
          .desktop-editor-sidebar.closed {
            background: transparent !important;
          }

          .desktop-editor-sidebar-section {
            padding: 0;
          }
          .theme-light .desktop-editor-sidebar-section { border-bottom: 1px solid #F0F0F0; }
          .theme-dark .desktop-editor-sidebar-section { border-bottom: 1px solid #444; }
          .theme-dark .desktop-editor-sidebar-section {
            color: #797979ff;
          }
          .desktop-editor-sidebar-section:last-child { border-bottom: none; }

          .desktop-editor-sidebar-title {
            font-family: 'Avenir', sans-serif;
            font-weight: 600;
            font-size: 0.75rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin: 0;
            padding: 16px 16px 12px 16px;
          }
          .theme-light .desktop-editor-sidebar-title { color: #999; }
          .theme-dark .desktop-editor-sidebar-title { color: #aaa; }

          .desktop-editor-canvas-area {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
            min-width: 0;
            padding: 20px;
            margin-left: 0;
            width: 100%;
          }
          .theme-light .desktop-editor-canvas-area { background: #E8E8E8; }
          .theme-dark .desktop-editor-canvas-area { background: #1a1a1a; }

          .desktop-editor-canvas-inner {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 12px;
            overflow: visible;
            box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
            display: flex;
            align-items: center;
            justify-content: center;
            user-select: none;
            -webkit-user-select: none;
            -webkit-touch-callout: none;
          }
          .theme-light .desktop-editor-canvas-inner { background: #FFFFFF; }
          .theme-dark .desktop-editor-canvas-inner { background: #2a2a2a; }

          .desktop-editor-canvas-inner img {
            max-width: 100%;
            max-height: calc(100vh - 160px);
            width: auto;
            height: auto;
            object-fit: contain;
            display: block;
            user-select: none;
            -webkit-user-select: none;
            -webkit-user-drag: none;
          }

          .desktop-editor-lasso-canvas {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 10;
            image-rendering: crisp-edges;
          }

          .desktop-editor-pointer-dot {
            position: absolute;
            width: 16px;
            height: 16px;
            background-color: white;
            border-radius: 50%;
            transform: translate(-50%, -50%);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(0, 0, 0, 0.2);
            z-index: 20;
            pointer-events: none;
            transition: none;
            will-change: left, top;
          }

          .desktop-editor-properties-panel {
            width: 260px;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            flex-shrink: 0;
            z-index: 10;
          }
          .theme-light .desktop-editor-properties-panel { background: #FFFFFF; border-left: 1px solid #E8E8E8; }
          .theme-dark .desktop-editor-properties-panel { background: #333; border-left: 1px solid #555; }

          .desktop-editor-properties-section { padding: 20px; }
          .theme-light .desktop-editor-properties-section { border-bottom: 1px solid #F0F0F0; }
          .theme-dark .desktop-editor-properties-section { border-bottom: 1px solid #444; }
          .desktop-editor-properties-section:last-child { border-bottom: none; }

          .desktop-editor-properties-title {
            font-family: 'Avenir', sans-serif;
            font-weight: 600;
            font-size: 0.75rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 14px;
          }
          .theme-light .desktop-editor-properties-title { color: #999; }
          .theme-dark .desktop-editor-properties-title { color: #aaa; }

          .desktop-editor-slider-group { margin-bottom: 14px; }
          .desktop-editor-slider-group label {
            display: flex;
            justify-content: space-between;
            font-family: 'Avenir', sans-serif;
            font-size: 0.7rem;
            margin-bottom: 4px;
          }
          .theme-light .desktop-editor-slider-group label { color: #888; }
          .theme-dark .desktop-editor-slider-group label { color: #aaa; }
          .desktop-editor-slider-group input[type="range"] {
            width: 100%;
            height: 4px;
            border-radius: 2px;
            -webkit-appearance: none;
            appearance: none;
            outline: none;
          }
          .theme-light .desktop-editor-slider-group input[type="range"] { background: #E0E0E0; }
          .theme-dark .desktop-editor-slider-group input[type="range"] { background: #555; }
          .desktop-editor-slider-group input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 14px;
            height: 14px;
            background: #4594F9;
            border-radius: 50%;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          }

          .desktop-editor-statusbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 6px 20px;
            z-index: 20;
            flex-shrink: 0;
            min-height: 28px;
          }
          .theme-light .desktop-editor-statusbar { background: #FFFFFF; border-top: 1px solid #E8E8E8; }
          .theme-dark .desktop-editor-statusbar { background: #3a3a3a; border-top: 1px solid #555; }

          .desktop-editor-statusbar-left,
          .desktop-editor-statusbar-right {
            display: flex;
            align-items: center;
            gap: 16px;
            font-family: 'Roboto Mono', monospace;
            font-size: 0.65rem;
          }
          .theme-light .desktop-editor-statusbar-left span,
          .theme-light .desktop-editor-statusbar-right span { color: #BBB; }
          .theme-dark .desktop-editor-statusbar-left span,
          .theme-dark .desktop-editor-statusbar-right span { color: #aaa; }

          .desktop-editor-statusbar-center {
            font-family: 'Avenir', sans-serif;
            font-size: 0.7rem;
          }
          .theme-light .desktop-editor-statusbar-center { color: #999; }
          .theme-dark .desktop-editor-statusbar-center { color: #aaa; }

          .desktop-editor-statusbar-right span:last-child { font-weight: 500; }
          .theme-light .desktop-editor-statusbar-right span:last-child { color: #4594F9; }
          .theme-dark .desktop-editor-statusbar-right span:last-child { color: #4594f9; }
        }

        @media (min-width: 768px) and (max-width: 1024px) {
          .desktop-editor-sidebar { width: 240px; }
          .desktop-editor-properties-panel { width: 240px; }
          .desktop-editor-topbar-center { max-width: 350px; }
        }

        .voice-mic-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 6px;
  flex-shrink: 0;
  color: #888;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  background-color: transparent !important;
}
.voice-mic-btn.listening {
  background: rgba(69, 148, 249, 0.2) !important;
  background-color: rgba(69, 148, 249, 0.2) !important;
  color: #ffffffff !important;
  border-color: #4594f9;
  animation: micPulse 1.5s ease-in-out infinite;
}

.theme-light .voice-mic-btn { color: #666; }
.theme-dark .voice-mic-btn { color: #ccc; }

@media (hover: hover) {
  .voice-mic-btn:hover:not(:disabled):not(.listening) {
    background: rgba(69,148,249,0.15) !important;
    color: #4594f9;
  }
}

.voice-mic-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.voice-mic-btn:focus:not(.listening),
.voice-mic-btn:active:not(.listening) {
  background: transparent !important;
  color: #888;
}
.theme-light .voice-mic-btn:focus:not(.listening),
.theme-light .voice-mic-btn:active:not(.listening) {
  color: #666;
}
.theme-dark .voice-mic-btn:focus:not(.listening),
.theme-dark .voice-mic-btn:active:not(.listening) {
  color: #ccc;
}
        @keyframes micPulse {
          0% { box-shadow: 0 0 0 0 rgba(69,148,249,0.5); }
          70% { box-shadow: 0 0 0 12px rgba(69,148,249,0); }
          100% { box-shadow: 0 0 0 0 rgba(69,148,249,0); }
        }

        .generate-input-wrapper .voice-mic-btn {
          width: 32px;
          height: 32px;
          margin-right: 4px;
        }

      @keyframes blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0; }
      }

.prompt-panel {
  position: relative;
  width: 100%;
  background-color: #414141;
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
  z-index: 26;
  max-height: 24px;
  flex: 0 0 auto;
  border: none;
  border-radius: 0;
  border-bottom: 1px solid #000000;
  display: flex;
  flex-direction: column;
}
.prompt-panel.expanded {
  max-height: 300px;
  flex: 0 0 auto;
}
.prompt-panel-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: grab;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  height: 24px;
  min-height: 24px;
  position: sticky;
  top: 0;
  background-color: #4f4f4fff;
  z-index: 2;
  border: none;
  border-bottom: 1px solid #000000;
  flex-shrink: 0;
}
.theme-light .prompt-panel-handle {
  background-color: #4f4f4fff;
  border-bottom-color: #000000;
}
.prompt-panel-handle:active {
  cursor: grabbing;
}
.prompt-panel-content {
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow-y: auto;
  overscroll-behavior: none;
  flex: 0 1 auto;
  min-height: 0;
  max-height: 200px;
  background-color: #505050;
}
.theme-light .prompt-panel-content {
  background-color: #505050;
}
.prompt-panel-empty {
  color: #ccc;
  font-size: 11px;
  font-family: Avenir, sans-serif;
  text-align: center;
  padding: 4px 16px;
  margin: auto;
}
.prompt-panel-list {
  flex: 0 1 auto;
  overflow-y: auto;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
.prompt-panel-item {
  display: flex;
  gap: 12px;
  padding: 4px 16px;
  border-bottom: 1px solid #444;
  font-size: 12px;
}
.theme-light .prompt-panel-item {
  border-bottom-color: #444;
}
.prompt-panel-item:last-child {
  border-bottom: none;
}
.prompt-panel-time {
  color: #888;
  font-family: monospace;
  font-size: 10px;
  flex-shrink: 0;
  min-width: 50px;
}
.prompt-panel-text {
  color: #ddd;
  font-family: monospace;
  word-break: break-word;
}
.prompt-panel-current {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #000000;
  font-size: 14px;
  background-color: #414141;
  flex-shrink: 0;
  align-items: flex-start;
  min-height: 40px;
  width: 100%;

}
.theme-light .prompt-panel-current {
  border-top-color: #000000;
  background-color: #414141;
}
.prompt-panel-current-label {
  color: #aaa;
  font-family: Avenir, sans-serif;
  font-weight: 600;
  flex-shrink: 0;
}
.prompt-panel-current-text {
  color: #fff;
  font-family: monospace;
  word-break: break-word;
  font-size: 14px;
}
.prompt-panel-blinking-placeholder {
  animation: smoothBlink 2s ease-in-out infinite;
  color: rgba(255, 255, 255, 0.5);
}
@keyframes smoothBlink {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.15; }
}
.prompt-panel-placeholder {
  color: rgba(255, 255, 255, 1);
  font-family: monospace;
  animation: placeholderPulse 2s ease-in-out infinite;
}
@keyframes placeholderPulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.2; }
}
.prompt-panel-empty-blinking {
  animation: emptyBlink 2s ease-in-out infinite;
  color: rgba(255, 255, 255, 0.9);
  font-family: monospace;
  font-size: 8px;
}
@keyframes emptyBlink {
  0%, 100% { opacity: 0.9; }
  50% { opacity: 0.4; }
}

      `}</style>
    </div>
  );
};

export default Editor;